/**
 * ВПР — модалка только для бухгалтерии и логистики.
 * Не связана с отчётами HR (Отчет удаленка, Отчет локальных пользователей ПК,
 * Мониторинг времени, Отработка отгулов). Отдельный функционал, отдельное состояние.
 */
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import Modal from 'react-modal';
import { FiUpload, FiDownload, FiChevronUp, FiChevronDown, FiCpu, FiGitMerge, FiAlertCircle, FiSend, FiArrowLeft, FiSettings, FiCheckSquare, FiFilter, FiX } from 'react-icons/fi';
import {
  applyManualActionToEntry,
  serializeManualActionStep,
  syncManualActionPipelineForRun,
  getManualActionStepLabel,
  MANUAL_PIPELINE_ACTIONS,
  cloneManualSheetsByName,
  getMergeSourceSheets,
  listMergeSheetOptions,
  summarizeRowsByDateColumn,
  sampleRowsForMergedPreview,
  MERGE_COL_SOURCE_TAB,
  MERGE_COL_DATE_FROM_TAB,
  detectPhoneColumnIndices,
  detectManualResultFilterColumns,
  manualResultRowPassesTextFilters,
  manualColIndicesSelectionSet,
  manualSheetKeysSelectionSet,
} from '../../utils/vprManualSheetActions';
import ModalCloseButton from '../Common/ModalCloseButton';
import VprManualDeepseekPanel from './VprManualDeepseekPanel';
import { buildDeepseekResultSheets, getDeepseekComparisonSummary } from '../../utils/vprManualDeepseek';
import { detectManualNdCol, normalizeVprCrossMatchMode } from '../../utils/vprCrossMatchNd';
import * as XLSX from 'xlsx';
import { getDisplayValue } from '../../utils/vprFormula';
import api from '../../services/api';
import { useApp } from '../../context/AppContext';
import { loadFrontendSettings, VPR_DEFAULT_BACKGROUND, normalizeVprBackground } from '../../utils/frontendSettings';

Modal.setAppElement('#root');

const EXCEL_EXT = /\.(xlsx|xls)$/i;
const CSV_EXT = /\.csv$/i;
function isExcelFile(file) {
  return file?.name && EXCEL_EXT.test(file.name);
}
function isMergeSourceFile(file) {
  return file?.name && (EXCEL_EXT.test(file.name) || CSV_EXT.test(file.name));
}
function isMergeTemplateFile(file) {
  return file?.name && EXCEL_EXT.test(file.name);
}

// Парсинг CSV (поддержка запятая/точка-с-запятой, кавычки)
function parseCSV(text) {
  const lines = [];
  let current = '';
  let inQuotes = false;
  const row = [];
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (c === '"') {
      inQuotes = !inQuotes;
    } else if (inQuotes) {
      current += c;
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++;
      row.push(current.trim());
      lines.push(row.slice());
      row.length = 0;
      current = '';
    } else if (c === ',' || c === ';' || c === '\t') {
      row.push(current.trim());
      current = '';
    } else {
      current += c;
    }
  }
  if (current || row.length > 0) {
    row.push(current.trim());
    lines.push(row);
  }
  return lines;
}

// Определение типа значения для сортировки (число, дата, время)
function detectType(str) {
  if (str == null || str === '') return 'string';
  const s = String(str).trim();
  if (/^-?\d+([.,]\d+)?$/.test(s.replace(/\s/g, ''))) return 'number';
  if (/^\d{1,2}[./]\d{1,2}[./]\d{2,4}/.test(s) || /^\d{4}-\d{2}-\d{2}/.test(s)) return 'date';
  if (/^\d{1,2}:\d{2}(:\d{2})?/.test(s)) return 'time';
  return 'string';
}

function compareValues(a, b, type) {
  if (a == null || a === '') a = '';
  if (b == null || b === '') b = '';
  const sa = String(a).trim();
  const sb = String(b).trim();
  if (type === 'number') {
    const na = parseFloat(sa.replace(',', '.'));
    const nb = parseFloat(sb.replace(',', '.'));
    if (Number.isNaN(na) && Number.isNaN(nb)) return 0;
    if (Number.isNaN(na)) return 1;
    if (Number.isNaN(nb)) return -1;
    return na - nb;
  }
  if (type === 'date' || type === 'time') {
    const da = new Date(sa);
    const db = new Date(sb);
    if (!isNaN(da.getTime()) && !isNaN(db.getTime())) return da - db;
  }
  return sa.localeCompare(sb, 'ru');
}

const DEFAULT_SHEET_NAME = 'Данные';
const VPR_MANUAL_PRESETS_KEY = 'vpr_manual_presets_v1';
const VPR_MANUAL_SPREAD_STATE_KEY = 'vpr_manual_spread_state_v1';

/** Слот с книгой-мастером после «Разнести» — не участвует в «Запустить» и сравнении. */
function isManualWorkbookMasterSlot(entry) {
  return !!entry?.workbookMasterOnly;
}

/** Слоты с данными вкладок для парсинга (без книги-мастера). */
function isManualSlotWithParseData(entry) {
  if (!entry || isManualWorkbookMasterSlot(entry)) return false;
  return (entry.rows || []).length > 0 || (entry.headers || []).length > 0;
}

function getManualLoadedSlotIndices(slotList, manualFiles) {
  return (slotList || []).filter((i) => isManualSlotWithParseData(manualFiles[i]));
}

function createManualWorkbookMasterEntry(entry, masterSlotIdx) {
  if (!entry) return null;
  return {
    file: entry.file,
    fileName: entry.fileName,
    sheetNames: entry.sheetNames,
    sheetsByName: entry.sheetsByName,
    _sourceSheetsByName: entry._sourceSheetsByName || cloneManualSheetsByName(entry.sheetsByName),
    _sourceSheetNames: entry._sourceSheetNames || [...(entry.sheetNames || [])],
    workbookSourceSlot: masterSlotIdx,
    workbookMasterOnly: true,
    excelSheetName: entry.excelSheetName || entry.sheetNames?.[0] || '',
    headers: [],
    rows: [],
  };
}

function saveManualSpreadState(distributedRanges, slotCount) {
  try {
    sessionStorage.setItem(
      VPR_MANUAL_SPREAD_STATE_KEY,
      JSON.stringify({
        distributedRanges,
        slotCount,
        savedAt: new Date().toISOString(),
      }),
    );
  } catch (_) {}
}

/** Цвета цепочек связывания столбцов между файлами (порядок = новая связка). */
const MANUAL_LINK_COLORS = ['#e53e3e', '#2563eb', '#16a34a', '#ca8a04', '#9333ea', '#ea580c', '#0891b2', '#db2777'];

/** Значение `linkAction` в цепочке → API: compare_eq/compare_ne + опционально phoneFormat (как в «Преобразование телефонов»). */
const MANUAL_LINK_ACTION_OPTIONS = [
  { value: 'compare_eq', label: 'Сравнить (равно)' },
  { value: 'compare_ne', label: 'Сравнить (не равно)' },
  { value: 'sum', label: 'Сумма (числа: столбец 1 + столбец 2 по шагам связки)' },
  { value: 'subtract', label: 'Разность (столбец 1 − столбец 2)' },
  { value: 'multiply', label: 'Умножение (столбец 1 × столбец 2)' },
  { value: 'divide', label: 'Деление (столбец 1 ÷ столбец 2; делитель 0 → пусто)' },
  { value: 'phone_eq_ua', label: 'Телефоны: равно (Украина: 0XX XXX XX XX)' },
  { value: 'phone_eq_intl', label: 'Телефоны: равно (международный +38 …)' },
  { value: 'phone_eq_brackets', label: 'Телефоны: равно (+380 (XX) XXX-XX-XX)' },
  { value: 'phone_eq_digits', label: 'Телефоны: равно (только цифры)' },
  { value: 'phone_ne_ua', label: 'Телефоны: не равно (Украина: 0XX XXX XX XX)' },
  { value: 'phone_ne_intl', label: 'Телефоны: не равно (международный +38 …)' },
  { value: 'phone_ne_brackets', label: 'Телефоны: не равно (+380 (XX) XXX-XX-XX)' },
  { value: 'phone_ne_digits', label: 'Телефоны: не равно (только цифры)' },
  { value: 'extract_phone_eq_ua', label: 'Извлечь телефон из «ФИО + номер», равно (Украина: 0XX …)' },
  { value: 'extract_phone_eq_intl', label: 'Извлечь телефон из «ФИО + номер», равно (международный +38 …)' },
  { value: 'extract_phone_eq_brackets', label: 'Извлечь телефон из «ФИО + номер», равно (+380 (XX) …)' },
  { value: 'extract_phone_eq_digits', label: 'Извлечь телефон из «ФИО + номер», равно (только цифры)' },
  { value: 'extract_phone_ne_ua', label: 'Извлечь телефон из «ФИО + номер», не равно (Украина: 0XX …)' },
  { value: 'extract_phone_ne_intl', label: 'Извлечь телефон из «ФИО + номер», не равно (международный +38 …)' },
  { value: 'extract_phone_ne_brackets', label: 'Извлечь телефон из «ФИО + номер», не равно (+380 (XX) …)' },
  { value: 'extract_phone_ne_digits', label: 'Извлечь телефон из «ФИО + номер», не равно (только цифры)' },
];

function parseManualLinkActionToRuleFields(linkAction) {
  const la = linkAction || 'compare_eq';
  if (la === 'compare_ne') return { action: 'compare_ne', phoneFormat: null, extractFioPhone: false };
  if (la === 'compare_eq') return { action: 'compare_eq', phoneFormat: null, extractFioPhone: false };
  if (la === 'sum' || la === 'subtract' || la === 'multiply' || la === 'divide') {
    return { action: la, phoneFormat: null, extractFioPhone: false };
  }
  const ex = /^extract_phone_(eq|ne)_(ua|intl|brackets|digits)$/.exec(la);
  if (ex) {
    return {
      action: ex[1] === 'ne' ? 'compare_ne' : 'compare_eq',
      phoneFormat: ex[2],
      extractFioPhone: true,
    };
  }
  const m = /^phone_(eq|ne)_(ua|intl|brackets|digits)$/.exec(la);
  if (m) {
    return {
      action: m[1] === 'ne' ? 'compare_ne' : 'compare_eq',
      phoneFormat: m[2],
      extractFioPhone: false,
    };
  }
  return { action: 'compare_eq', phoneFormat: null, extractFioPhone: false };
}

function vprManualGenId() {
  return `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/** Из сохранённых цепочек строит правила fileA/colA → fileB/colB для API. */
function expandManualColumnChainsToRules(chains) {
  const out = [];
  for (const ch of chains || []) {
    const picks = ch.picks || [];
    const { action, phoneFormat, extractFioPhone } = parseManualLinkActionToRuleFields(ch.linkAction);
    for (let i = 0; i < picks.length - 1; i += 1) {
      const rule = {
        fileA: picks[i].fileIdx,
        action,
        fileB: picks[i + 1].fileIdx,
      };
      if (picks[i].addedSlotIndex != null && picks[i].addedSlotIndex !== '') {
        rule.addedColA = Number(picks[i].addedSlotIndex);
        rule.colA = 0;
      } else {
        rule.colA = picks[i].colIdx;
        if (picks[i].colPart) rule.colApart = picks[i].colPart;
      }
      if (picks[i + 1].addedSlotIndex != null && picks[i + 1].addedSlotIndex !== '') {
        rule.addedColB = Number(picks[i + 1].addedSlotIndex);
        rule.colB = 0;
      } else {
        rule.colB = picks[i + 1].colIdx;
        if (picks[i + 1].colPart) rule.colBpart = picks[i + 1].colPart;
      }
      if (phoneFormat) rule.phoneFormat = phoneFormat;
      if (extractFioPhone) rule.extractFioPhone = true;
      out.push(rule);
    }
  }
  return out;
}

function pickIsAddedSlot(p) {
  return p.addedSlotIndex != null && p.addedSlotIndex !== '';
}

function findManualLinkMeta(chains, draft, fileIdx, colIdx, colPart, addedSlotIndex) {
  const useAdded = addedSlotIndex !== undefined && addedSlotIndex !== null && addedSlotIndex !== '';
  if (useAdded) {
    const ai = Number(addedSlotIndex);
    if (!Number.isFinite(ai) || ai < 0) return null;
    const sameAdded = (p) => p.fileIdx === fileIdx && Number(p.addedSlotIndex) === ai;
    if (draft?.picks?.some(sameAdded)) {
      return { color: draft.color, label: 'черновик', isDraft: true };
    }
    for (let ci = 0; ci < (chains || []).length; ci += 1) {
      const ch = chains[ci];
      if (ch.picks?.some(sameAdded)) {
        return { color: ch.color, label: `связка ${ci + 1}`, isDraft: false };
      }
    }
    return null;
  }
  const notAddedPick = (p) => !pickIsAddedSlot(p);
  const samePick = (p) => p.fileIdx === fileIdx && notAddedPick(p) && p.colIdx === colIdx && String(p.colPart || '') === String(colPart || '');
  if (draft?.picks?.some(samePick)) {
    return { color: draft.color, label: 'черновик', isDraft: true };
  }
  for (let ci = 0; ci < (chains || []).length; ci += 1) {
    const ch = chains[ci];
    if (ch.picks?.some(samePick)) {
      return { color: ch.color, label: `связка ${ci + 1}`, isDraft: false };
    }
  }
  return null;
}

function findAnyManualLinkMetaForCol(chains, draft, fileIdx, colIdx, partChoices) {
  if (partChoices && partChoices.length > 0) {
    for (const c of partChoices) {
      const m = findManualLinkMeta(chains, draft, fileIdx, colIdx, c.part, undefined);
      if (m) return m;
    }
    return null;
  }
  return findManualLinkMeta(chains, draft, fileIdx, colIdx, undefined, undefined);
}

function parseManualAddedExtractIdx(v) {
  if (v === '' || v == null) return null;
  const n = parseInt(String(v), 10);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/** Для столбца с «Извлечь телефон» / «Разбить столбец» — две логические части в связке. */
function manualColPartChoices(colIdx, fileConfig, addedList) {
  if (!fileConfig?.params) return null;
  const act = fileConfig.action;
  if (act === 'extract_phone') {
    const ec = Math.max(0, parseInt(fileConfig.params.extractPhoneColIndex, 10) || 0);
    if (ec !== colIdx) return null;
    const arr = addedList || [];
    const fi = parseManualAddedExtractIdx(fileConfig.params.extractFioAddedColumnIndex);
    const pi = parseManualAddedExtractIdx(fileConfig.params.extractPhoneAddedColumnIndex);
    const fioName = (fi != null && arr[fi]) ? String(arr[fi].name || arr[fi].letter || '').trim() : '';
    const phoneName = (pi != null && arr[pi]) ? String(arr[pi].name || arr[pi].letter || '').trim() : '';
    const baseLetter = colIndexToLetter(colIdx);
    return [
      {
        part: 'fio',
        short: 'Ф',
        title: fioName ? `«${fioName}» (извлечённое ФИО)` : 'ФИО после извлечения',
        ruleOptionLabel: `${baseLetter} — ${fioName || 'ФИО'} (извлечение)`,
      },
      {
        part: 'phone',
        short: 'Т',
        title: phoneName ? `«${phoneName}» (извлечённый телефон)` : 'Телефон после извлечения',
        ruleOptionLabel: `${baseLetter} — ${phoneName || 'телефон'} (извлечение)`,
      },
    ];
  }
  if (act === 'split_column') {
    const sc = Math.max(0, parseInt(fileConfig.params.splitColIndex, 10) || 0);
    if (sc !== colIdx) return null;
    return [
      { part: 'split0', short: '1', title: 'Часть 1 (до разделителя)', ruleOptionLabel: null },
      { part: 'split1', short: '2', title: 'Часть 2 (после разделителя)', ruleOptionLabel: null },
    ];
  }
  return null;
}

function manualColPartPickSuffix(fileConfig, colPart, addedList) {
  if (!colPart) return '';
  const act = fileConfig?.action;
  if (act === 'extract_phone') {
    const arr = addedList || [];
    const fi = parseManualAddedExtractIdx(fileConfig?.params?.extractFioAddedColumnIndex);
    const pi = parseManualAddedExtractIdx(fileConfig?.params?.extractPhoneAddedColumnIndex);
    if (colPart === 'fio' && fi != null && arr[fi]) return ` (${String(arr[fi].name || arr[fi].letter || 'ФИО').slice(0, 24)})`;
    if (colPart === 'phone' && pi != null && arr[pi]) return ` (${String(arr[pi].name || arr[pi].letter || 'тел.').slice(0, 24)})`;
    if (colPart === 'fio') return ' (ФИО)';
    if (colPart === 'phone') return ' (тел.)';
  }
  if (act === 'split_column') {
    if (colPart === 'split0') return ' (ч.1)';
    if (colPart === 'split1') return ' (ч.2)';
  }
  return '';
}

function parseManualRuleColSelectValue(v) {
  const s = String(v);
  const sep = s.indexOf('__');
  if (sep >= 0) {
    const col = parseInt(s.slice(0, sep), 10);
    const part = s.slice(sep + 2);
    if (['fio', 'phone', 'split0', 'split1'].includes(part)) {
      return { col: Number.isNaN(col) ? 0 : col, part };
    }
  }
  const col = parseInt(s, 10);
  return { col: Number.isNaN(col) ? 0 : col, part: null };
}

function manualRuleColSelectValue(col, colPart) {
  if (colPart === 'fio' || colPart === 'phone' || colPart === 'split0' || colPart === 'split1') {
    return `${col}__${colPart}`;
  }
  return String(col);
}

function mapVprManualRuleToApiPayload(r, loadedIndices) {
  const fileA = loadedIndices.indexOf(r.fileA);
  const fileB = loadedIndices.indexOf(r.fileB);
  if (fileA < 0 || fileB < 0) return null;
  const o = {
    fileA,
    colA: r.colA ?? 0,
    action: r.action,
    fileB,
    colB: r.colB ?? 0,
  };
  if (r.phoneFormat) o.phoneFormat = r.phoneFormat;
  if (r.extractFioPhone) o.extractFioPhone = true;
  if (['fio', 'phone', 'split0', 'split1'].includes(r.colApart)) o.colApart = r.colApart;
  if (['fio', 'phone', 'split0', 'split1'].includes(r.colBpart)) o.colBpart = r.colBpart;
  if (r.addedColA != null && r.addedColA !== '' && Number.isFinite(Number(r.addedColA)) && Number(r.addedColA) >= 0) o.addedColA = Number(r.addedColA);
  if (r.addedColB != null && r.addedColB !== '' && Number.isFinite(Number(r.addedColB)) && Number(r.addedColB) >= 0) o.addedColB = Number(r.addedColB);
  return o;
}

/** Подпись добавленного столбца (+) в связке. */
function manualAddedPickCaption(addedColumns, fileIdx, slotIdx, maxLen = 40) {
  const ac = (addedColumns[fileIdx] || [])[slotIdx];
  if (!ac) return `+${slotIdx + 1}`;
  const s = String(ac.name || ac.letter || '').trim() || `+${slotIdx + 1}`;
  return s.length > maxLen ? `${s.slice(0, maxLen)}…` : s;
}

/** Короткая подпись столбца для черновика / списка связок (заголовок или переименование). */
function manualPickColumnCaption(files, renames, fileIdx, colIdx, maxLen = 40) {
  const hdr = files[fileIdx]?.headers?.[colIdx];
  const name = String((renames[fileIdx] || {})[colIdx] ?? hdr ?? '').trim();
  if (!name) return '';
  return name.length > maxLen ? `${name.slice(0, maxLen)}…` : name;
}

/** Операторы условий применения манипуляции к строке (по столбцу в файле). */
const MANUAL_MANIPULATION_CONDITION_OPS = [
  { value: 'empty', label: 'Пусто', needsValue: false },
  { value: 'not_empty', label: 'Не пусто', needsValue: false },
  { value: 'equals', label: 'Равно тексту', needsValue: true },
  { value: 'not_equals', label: 'Не равно тексту', needsValue: true },
  { value: 'contains', label: 'Содержит', needsValue: true },
  { value: 'not_contains', label: 'Не содержит', needsValue: true },
  { value: 'num_gt', label: 'Число больше чем (вручную)', needsValue: true },
  { value: 'num_gte', label: 'Число ≥ (больше или равно)', needsValue: true },
  { value: 'num_lt', label: 'Число меньше чем', needsValue: true },
  { value: 'num_lte', label: 'Число ≤ (меньше или равно)', needsValue: true },
  { value: 'num_eq', label: 'Число равно', needsValue: true },
  { value: 'num_neq', label: 'Число не равно', needsValue: true },
];

/** Парсинг числа из ячейки / порога (запятая как десятичный разделитель). */
function parseManualPreviewNumber(str) {
  if (str == null || String(str).trim() === '') return NaN;
  const t = String(str).trim().replace(/\s/g, '').replace(/,/g, '.');
  const n = parseFloat(t);
  return Number.isFinite(n) ? n : NaN;
}

const MANUAL_PREVIEW_COMPARE_OPS = [
  { value: 'gt', label: '> больше порога' },
  { value: 'gte', label: '≥ не меньше порога' },
  { value: 'lt', label: '< меньше порога' },
  { value: 'lte', label: '≤ не больше порога' },
  { value: 'eq', label: '= равно порогу' },
  { value: 'neq', label: '≠ не равно порогу' },
];

/** Правила отображения превью: строка показывается только если все правила для этой вкладки выполнены (числовое сравнение). */
function manualPreviewRowPasses(row, headers, rules) {
  if (!rules || rules.length === 0) return true;
  const hdrs = headers || [];
  for (const r of rules) {
    let colIdx = -1;
    if (r.columnMode === 'header_contains') {
      const sub = (r.headerContains || '').trim().toLowerCase();
      if (!sub) return false;
      colIdx = hdrs.findIndex((h) => String(h).toLowerCase().includes(sub));
    } else {
      colIdx = parseInt(r.columnIndex, 10);
      if (Number.isNaN(colIdx) || colIdx < 0) return false;
    }
    if (colIdx < 0 || colIdx >= hdrs.length) return false;
    const cell = row[colIdx] ?? '';
    let n = parseManualPreviewNumber(cell);
    if (r.useAbs) n = Math.abs(n);
    const t = parseManualPreviewNumber(r.threshold);
    const op = r.compareOp || 'gte';
    if (!Number.isFinite(n) || !Number.isFinite(t)) return false;
    let ok = false;
    switch (op) {
      case 'gt': ok = n > t; break;
      case 'gte': ok = n >= t; break;
      case 'lt': ok = n < t; break;
      case 'lte': ok = n <= t; break;
      case 'eq': ok = n === t; break;
      case 'neq': ok = n !== t; break;
      default: ok = false;
    }
    if (!ok) return false;
  }
  return true;
}

function createDefaultManualFileParams() {
  return {
    replaceFind: '',
    replaceWith: '',
    phoneFormat: 'ua',
    splitColIndex: 0,
    splitDelimiter: ',',
    splitValues: '',
    extractPhoneColIndex: 0,
    extractPhoneFormat: 'ua',
    extractFioAddedColumnIndex: '',
    extractPhoneAddedColumnIndex: '',
    fallbackTargetColIndex: 0,
    fallbackSourceColIndex: 1,
    crossPhoneColIndex: '',
    crossFioColIndex: '',
    manipulationConditions: [],
    mergeSheetsInclude: [],
    phoneFormatColIndices: [],
    replaceTextColIndices: [],
  };
}

function defaultMergeSheetsIncludeForEntry(entry) {
  return getMergeSourceSheets(entry).names;
}

function ensureManualMergeColsSelected(headers, selectedCols) {
  const hdrs = headers || [];
  const out = new Set((selectedCols || []).filter((c) => Number.isFinite(c) && c >= 0));
  hdrs.forEach((h, i) => {
    const k = String(h || '').trim();
    if (k === MERGE_COL_SOURCE_TAB || k === MERGE_COL_DATE_FROM_TAB) out.add(i);
  });
  return [...out].sort((a, b) => a - b);
}

function getManualHeaderLabelForDetect(headers, renames, ci) {
  const renamed = renames?.[ci];
  if (renamed != null && String(renamed).trim() !== '') return String(renamed).trim();
  return String(headers?.[ci] ?? '').trim();
}

/** Столбец ФИО/контакта задан вручную или определяется по заголовку (в т.ч. «Контакт отримувача»). */
function manualCrossFioColConfigured(fioSlot, headers, renames) {
  if (!fioSlot) return false;
  const rawCol = fioSlot.col;
  if (rawCol !== '' && rawCol != null) {
    const n = Number(rawCol);
    if (Number.isFinite(n) && n >= 0) return true;
  }
  if (Array.isArray(headers) && headers.length) {
    return detectManualCrossCol(headers, 'fio', renames) >= 0;
  }
  return false;
}

function fillManualCrossFioFromDetect(fioBySlot, slotIndices, manualFiles, manualRenames) {
  const next = [...fioBySlot];
  let changed = false;
  slotIndices.forEach((i) => {
    if (manualCrossFioColConfigured(next[i], manualFiles[i]?.headers, manualRenames[i])) return;
    const det = detectManualCrossCol(manualFiles[i]?.headers || [], 'fio', manualRenames[i]);
    if (det >= 0) {
      next[i] = { col: det, part: next[i]?.part || '' };
      changed = true;
    }
  });
  return { fioBySlot: next, changed };
}

function listManualCrossFioMissingSlotLabels(slotIndices, fioBySlot, manualFiles, manualRenames) {
  return slotIndices
    .filter((i) => !manualCrossFioColConfigured(fioBySlot[i], manualFiles[i]?.headers, manualRenames[i]))
    .map((i) => {
      const fn = fixVprDisplayFileName(manualFiles[i]?.fileName || '');
      const sheet = manualFiles[i]?.excelSheetName;
      const extra = sheet ? ` → ${sheet}` : (fn ? ` (${fn.slice(0, 28)})` : '');
      return `Файл ${i + 1}${extra}`;
    });
}

function detectManualCrossCol(headers, kind, renames) {
  if (!Array.isArray(headers)) return kind === 'phone' ? 0 : -1;
  const normalized = headers.map((h, ci) => getManualHeaderLabelForDetect(headers, renames, ci).toLowerCase());
  const phoneNames = ['телефон', 'тел', 'phone', 'номер телефона', 'моб', 'мобильный', 'телефон клиента'];
  const fioNames = [
    'фио', 'ф.і.о', 'ф і о', 'піб', 'pib', 'ім\'я', 'имя', 'контакт', 'отримувач', 'отримувача',
    'получатель', 'получат', 'клієнт', 'клиент', 'контакт получателя', 'контакт отримувача', 'recipient',
  ];
  const dateNames = ['дата', 'date', 'дата создания', 'дата документа', 'дата отправки', 'дата відправлення'];
  const ndNames = ['нд', 'недозвон', 'статус', 'комментар', 'примечан', 'результат', 'примітк'];
  let names = fioNames;
  if (kind === 'phone') names = phoneNames;
  else if (kind === 'date') names = dateNames;
  else if (kind === 'nd') names = ndNames;
  for (const n of names) {
    const i = normalized.findIndex((h) => h === n || (h && h.includes(n)));
    if (i >= 0) return i;
  }
  return kind === 'phone' ? 0 : -1;
}

function manualCrossColSelectValue(col, part) {
  if (part === 'fio' || part === 'phone') return `${col}:${part}`;
  return String(col);
}

function parseManualCrossColSelectValue(val) {
  const s = String(val ?? '');
  const m = /^(\d+):(fio|phone)$/.exec(s);
  if (m) return { col: parseInt(m[1], 10), part: m[2] };
  const n = parseInt(s, 10);
  return { col: Number.isFinite(n) ? n : 0, part: '' };
}

function buildManualCrossFileColSelectOptions(fileIdx, manualFiles, manualDeletedCols, manualRenames, manualFileConfigs, manualAddedColumns) {
  const headers = manualFiles[fileIdx]?.headers || [];
  const opts = [];
  headers.forEach((h, ci) => {
    if ((manualDeletedCols[fileIdx] || []).indexOf(ci) >= 0) return;
    const base = `${colIndexToLetter(ci)} — ${(manualRenames[fileIdx] || {})[ci] ?? h ?? `Кол.${ci + 1}`}`;
    const partChoices = manualColPartChoices(ci, manualFileConfigs[fileIdx], manualAddedColumns[fileIdx]);
    if (partChoices) {
      partChoices.forEach((c) => {
        const lab = c.part === 'fio' ? `${base} (ФИО)` : c.part === 'phone' ? `${base} (телефон)` : base;
        opts.push({ value: manualCrossColSelectValue(ci, c.part), label: lab });
      });
    } else {
      opts.push({ value: manualCrossColSelectValue(ci, ''), label: base });
    }
  });
  return opts;
}

function manualRowHasData(row) {
  if (!Array.isArray(row)) return false;
  return row.some((c) => String(c ?? '').trim() !== '');
}

function parseManualWorksheetToSheetData(worksheet) {
  if (!worksheet) return { headers: [], rows: [] };
  const data = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '', raw: false, blankrows: false });
  if (!data.length) return { headers: [], rows: [] };
  const headers = (data[0] || []).map((c) => String(c ?? '').trim());
  const rows = (data.length > 1 ? data.slice(1) : [])
    .map((row) => (Array.isArray(row) ? row : []).map((c) => (c != null ? String(c) : '')))
    .filter(manualRowHasData);
  return { headers, rows };
}

/** Все вкладки Excel-файла для «Ручное управление». */
function parseManualExcelWorkbookAllSheets(arrayBuffer) {
  const wb = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });
  const rawNames = wb.SheetNames && wb.SheetNames.length > 0 ? [...wb.SheetNames] : ['Лист1'];
  const sheetNames = [];
  const sheetsByName = {};
  const usedKeys = new Set();
  for (let i = 0; i < rawNames.length; i++) {
    const originalName = rawNames[i];
    let key = String(originalName || `Лист${i + 1}`).trim() || `Лист${i + 1}`;
    if (usedKeys.has(key)) {
      let n = 2;
      while (usedKeys.has(`${key} (${n})`)) n += 1;
      key = `${key} (${n})`;
    }
    usedKeys.add(key);
    const { headers, rows } = parseManualWorksheetToSheetData(wb.Sheets[originalName]);
    sheetsByName[key] = { headers, rows, sheetIndex: i, originalSheetName: originalName };
    sheetNames.push(key);
  }
  return { sheetNames, sheetsByName };
}

function getManualEntrySheetCount(entry) {
  if (!entry) return 0;
  const n = (entry.sheetNames || []).length;
  if (n > 0) return n;
  return Object.keys(entry.sheetsByName || {}).length;
}

/** Слот уже занят разнесением другого файла — не перезаписывать. */
function isSlotLockedByDistribution(slotIdx, existingRanges, excludeSourceSlot) {
  return (existingRanges || []).some((r) => {
    if (Number(r.sourceSlot) === Number(excludeSourceSlot)) return false;
    const rs = Number(r.startSlot) || 0;
    const rf = Number(r.filled) || 0;
    return slotIdx >= rs && slotIdx < rs + rf;
  });
}

/** Первый свободный слот после уже разнесённых вкладок. */
function getNextDistributeStartSlot(manualFiles, maxSlots, existingRanges) {
  let cursor = 0;
  if (Array.isArray(existingRanges) && existingRanges.length > 0) {
    cursor = Math.max(
      0,
      ...existingRanges.map((r) => (Number(r.startSlot) || 0) + (Number(r.filled) || 0)),
    );
  }
  while (cursor < maxSlots) {
    if (isSlotLockedByDistribution(cursor, existingRanges, -1)) {
      cursor += 1;
      continue;
    }
    if (!manualFiles[cursor]) break;
    cursor += 1;
  }
  return Math.min(Math.max(0, cursor), maxSlots - 1);
}

/**
 * Разнести одну книгу по слотам, не трогая уже разнесённые другие файлы.
 * Слот источника остаётся книгой-мастером (без строк в сравнении); каждая вкладка — ровно один слот.
 */
function distributeOneManualWorkbookAppend(
  manualFiles,
  sourceSlotIndex,
  startSlot = 0,
  maxSlots = 4,
  existingRanges = [],
) {
  const srcSlot = Number(sourceSlotIndex);
  if (!Number.isFinite(srcSlot) || srcSlot < 0 || srcSlot >= maxSlots) {
    return { ok: false, error: 'Выберите файл с 2+ вкладками для разнесения.' };
  }

  const entry = manualFiles[srcSlot];
  if (!entry?.sheetsByName) {
    return { ok: false, error: `Файл ${srcSlot + 1}: нет книги Excel с вкладками.` };
  }

  const names = entry.sheetNames || Object.keys(entry.sheetsByName || {});
  const sheetCount = names.length;
  if (sheetCount < 2) {
    const only = names[0] || '—';
    return {
      ok: false,
      error: `Файл ${srcSlot + 1}: только 1 вкладка («${only}»). Используйте «Разбить по дате» в карточке файла.`,
    };
  }

  const prevFiles = (manualFiles || []).slice(0, maxSlots);
  const rangesBefore = (existingRanges || []).filter((r) => Number(r.sourceSlot) !== srcSlot);

  let start = Math.max(0, Math.min(maxSlots - 1, Number(startSlot) || 0));
  start = Math.max(start, srcSlot + 1, getNextDistributeStartSlot(prevFiles, maxSlots, existingRanges));

  let availableSlots = 0;
  for (let c = start; c < maxSlots; c++) {
    if (c === srcSlot) continue;
    if (isSlotLockedByDistribution(c, rangesBefore, srcSlot)) continue;
    availableSlots += 1;
  }
  if (availableSlots < sheetCount) {
    const nextFree = getNextDistributeStartSlot(prevFiles, maxSlots, existingRanges);
    return {
      ok: false,
      error: `Не хватило слотов для файла ${srcSlot + 1}: нужно ${sheetCount} свободных слотов под вкладки (сейчас ${availableSlots}). `
        + `Слот ${srcSlot + 1} останется книгой-мастером. Увеличьте «Количество слотов» (сейчас ${maxSlots}) или начните с слота ${nextFree + 1}. `
        + 'После разнесения первого файла загрузите второй в пустой слот и разнесите его отдельно.',
    };
  }

  const nextFiles = prevFiles.slice();
  const selectedCols = prevFiles.map((f) => (f?.headers || []).map((_, ci) => ci));
  const exportDateBySlot = Array.from({ length: maxSlots }, () => '');
  const phoneBySlot = Array.from({ length: maxSlots }, () => createEmptyManualCrossPhoneSlot());
  const fioBySlot = Array.from({ length: maxSlots }, () => createEmptyManualCrossFioSlot());
  const ndBySlot = Array.from({ length: maxSlots }, () => createEmptyManualCrossNdSlot());
  const dateBySlot = Array.from({ length: maxSlots }, () => createEmptyManualCrossPhoneSlot());
  const distributedNames = [];
  let cursor = start;
  const rangeStart = start;
  let filledForSource = 0;

  for (let si = 0; si < names.length; si++) {
    while (
      cursor < maxSlots
      && (cursor === srcSlot || isSlotLockedByDistribution(cursor, rangesBefore, srcSlot))
    ) {
      cursor += 1;
    }
    if (cursor >= maxSlots) break;
    const sn = names[si];
    const linked = linkManualSlotToWorkbook(entry, sn, srcSlot);
    nextFiles[cursor] = linked;
    selectedCols[cursor] = (linked?.headers || []).map((_, ci) => ci);
    exportDateBySlot[cursor] = guessExportDateFromSheetName(sn);
    const hdrs = linked?.headers || [];
    phoneBySlot[cursor] = { col: detectManualCrossCol(hdrs, 'phone'), part: '' };
    fioBySlot[cursor] = {
      col: detectManualCrossCol(hdrs, 'fio') >= 0 ? detectManualCrossCol(hdrs, 'fio') : '',
      part: '',
    };
    dateBySlot[cursor] = {
      col: detectManualCrossCol(hdrs, 'date') >= 0 ? detectManualCrossCol(hdrs, 'date') : '',
      part: '',
    };
    const ndCol = detectManualNdCol(hdrs);
    ndBySlot[cursor] = { col: ndCol >= 0 ? ndCol : '' };
    distributedNames.push({ slot: cursor, sheet: sn, fromFile: srcSlot });
    cursor += 1;
    filledForSource += 1;
  }

  if (filledForSource < sheetCount) {
    return {
      ok: false,
      error: `Разнесено только ${filledForSource} из ${sheetCount} вкладок — увеличьте число слотов.`,
    };
  }

  nextFiles[srcSlot] = createManualWorkbookMasterEntry(entry, srcSlot);
  selectedCols[srcSlot] = [];
  exportDateBySlot[srcSlot] = '';
  phoneBySlot[srcSlot] = createEmptyManualCrossPhoneSlot();
  fioBySlot[srcSlot] = createEmptyManualCrossFioSlot();
  ndBySlot[srcSlot] = createEmptyManualCrossNdSlot();
  dateBySlot[srcSlot] = createEmptyManualCrossPhoneSlot();

  const oldSameSource = (existingRanges || []).find((r) => Number(r.sourceSlot) === srcSlot);
  if (oldSameSource) {
    for (let t = 0; t < maxSlots; t++) {
      const f = nextFiles[t];
      if (!f) continue;
      if (Number(f.workbookSourceSlot) === srcSlot && t !== srcSlot) {
        const inNewRange = t >= rangeStart && t < rangeStart + filledForSource;
        if (!inNewRange) nextFiles[t] = null;
      }
    }
  }

  for (let t = rangeStart + filledForSource; t < maxSlots; t++) {
    const f = prevFiles[t];
    if (!f) continue;
    if (Number(f.workbookSourceSlot) === srcSlot) nextFiles[t] = null;
  }

  const newRange = { sourceSlot: srcSlot, startSlot: rangeStart, filled: filledForSource, masterSlot: srcSlot };
  const nextStart = getNextDistributeStartSlot(nextFiles, maxSlots, [...rangesBefore, newRange]);

  let hint = `Файл ${srcSlot + 1}: слот ${srcSlot + 1} — книга-мастер (не в сравнении); `
    + `${sheetCount} вкладок → слоты ${distributedNames.map((d) => d.slot + 1).join(', ')}. Состояние сохранено.`;
  if (nextStart < maxSlots) {
    hint += ` Загрузите следующий файл в слот ${nextStart + 1}, выберите его и нажмите «Разнести этот файл», затем «Запустить».`;
  } else {
    hint += ' Увеличьте число слотов, если нужен ещё один файл.';
  }

  return {
    ok: true,
    hint,
    names: distributedNames,
    files: nextFiles,
    selectedCols,
    exportDateBySlot,
    phoneBySlot,
    fioBySlot,
    ndBySlot,
    dateBySlot,
    range: newRange,
    nextStartSlot: nextStart,
    sourceSlot: srcSlot,
    masterSlot: srcSlot,
  };
}

/** Показывать «та же книга» только для слотов, куда реально разнесли вкладки. */
function getManualWorkbookSourceForSharedSlot(
  manualFiles,
  slotIdx,
  distributeSourceSlots,
  singleWorkbookMode,
  distributedRanges,
) {
  if (!singleWorkbookMode || isManualWorkbookMasterSlot(manualFiles[slotIdx])) return null;
  const slotEntry = manualFiles[slotIdx];
  if (slotEntry?.workbookSourceSlot != null && Number(slotEntry.workbookSourceSlot) !== slotIdx) {
    const srcFromEntry = Number(slotEntry.workbookSourceSlot);
    const masterEntry = manualFiles[srcFromEntry];
    if (masterEntry?.sheetsByName) return { slot: srcFromEntry, entry: masterEntry };
  }
  if (!slotEntry) return null;

  const ranges = Array.isArray(distributedRanges)
    ? distributedRanges
    : distributedRanges
      ? [distributedRanges]
      : [];

  for (const range of ranges) {
    const rangeStart = Number(range.startSlot) || 0;
    const filled = Number(range.filled) || 0;
    if (slotIdx < rangeStart || slotIdx >= rangeStart + filled) continue;
    const src = range.sourceSlot != null ? Number(range.sourceSlot) : null;
    if (src == null || src === slotIdx) continue;
    const entry = manualFiles[src];
    if (!entry?.sheetsByName) continue;
    return { slot: src, entry };
  }

  const sources = Array.isArray(distributeSourceSlots)
    ? distributeSourceSlots
    : distributeSourceSlots != null
      ? [Number(distributeSourceSlots)]
      : [];
  for (const src of sources) {
    if (src == null || src === slotIdx) continue;
    const entry = manualFiles[src];
    if (!entry?.sheetsByName) continue;
    return { slot: src, entry };
  }
  return null;
}

const MANUAL_SLOT_COUNT_OPTIONS = [4, 6, 8, 10, 12, 16, 20];
const MANUAL_SLOT_COUNT_DEFAULT = 4;
const MANUAL_SLOT_COUNT_MAX = 20;

function manualSlotIndices(count) {
  const n = Math.max(1, Math.min(MANUAL_SLOT_COUNT_MAX, Number(count) || MANUAL_SLOT_COUNT_DEFAULT));
  return Array.from({ length: n }, (_, i) => i);
}

function createDefaultManualFileConfigEntry() {
  return {
    action: 'discrepancies',
    partnerFiles: [],
    method: 'compare_eq',
    params: createDefaultManualFileParams(),
    actionPipeline: [],
  };
}

function cloneManualSlotEntry(entry) {
  if (!entry) return null;
  const sheetsByName = entry.sheetsByName
    ? Object.fromEntries(
      Object.entries(entry.sheetsByName).map(([k, sh]) => [
        k,
        {
          headers: (sh.headers || []).slice(),
          rows: (sh.rows || []).map((r) => (Array.isArray(r) ? r.slice() : [])),
        },
      ]),
    )
    : undefined;
  return {
    ...entry,
    headers: (entry.headers || []).slice(),
    rows: (entry.rows || []).map((r) => (Array.isArray(r) ? r.slice() : [])),
    sheetsByName,
    sheetNames: entry.sheetNames ? [...entry.sheetNames] : undefined,
  };
}

function syncManualEntryActiveSheet(entry) {
  if (!entry?.sheetsByName) return;
  const sn = entry.excelSheetName || entry.sheetNames?.[0];
  if (sn && entry.sheetsByName[sn]) {
    entry.sheetsByName[sn] = { headers: entry.headers, rows: entry.rows };
  }
}

function createInitialManualFileConfigs(count = MANUAL_SLOT_COUNT_DEFAULT) {
  return manualSlotIndices(count).map(() => createDefaultManualFileConfigEntry());
}

function createEmptyManualCrossPhoneSlot() {
  return { col: null, part: '' };
}

function createEmptyManualCrossFioSlot() {
  return { col: '', part: '' };
}

function createEmptyManualCrossNdSlot() {
  return { col: '' };
}

function createDefaultManualCrossFileMatch(slotCount = MANUAL_SLOT_COUNT_DEFAULT) {
  const n = manualSlotIndices(slotCount).length;
  return {
    enabled: true,
    matchMode: 'phone',
    phoneBySlot: Array.from({ length: n }, () => createEmptyManualCrossPhoneSlot()),
    fioBySlot: Array.from({ length: n }, () => createEmptyManualCrossFioSlot()),
    ndBySlot: Array.from({ length: n }, () => createEmptyManualCrossNdSlot()),
    dateBySlot: Array.from({ length: n }, () => createEmptyManualCrossPhoneSlot()),
    exportDateBySlot: Array.from({ length: n }, () => ''),
    includeInFullMatchBySlot: Array.from({ length: n }, () => true),
    noCallRule: 'auto',
    leadingOnlyNd: false,
    splitByDateBySlot: Array.from({ length: n }, () => false),
  };
}

function resizeManualCrossFileMatch(prev, newCount) {
  const n = manualSlotIndices(newCount).length;
  const pad = (arr, factory) => {
    const next = (arr || []).slice(0, n);
    while (next.length < n) next.push(factory());
    return next;
  };
  return {
    ...prev,
    phoneBySlot: pad(prev.phoneBySlot, createEmptyManualCrossPhoneSlot),
    fioBySlot: pad(prev.fioBySlot, createEmptyManualCrossFioSlot),
    ndBySlot: pad(prev.ndBySlot, createEmptyManualCrossNdSlot),
    dateBySlot: pad(prev.dateBySlot, createEmptyManualCrossPhoneSlot),
    exportDateBySlot: pad(prev.exportDateBySlot, () => ''),
    includeInFullMatchBySlot: pad(prev.includeInFullMatchBySlot, () => true),
    splitByDateBySlot: pad(prev.splitByDateBySlot, () => false),
  };
}

function resizeManualSlotArray(arr, newCount, emptyFactory) {
  const n = manualSlotIndices(newCount).length;
  const next = (arr || []).slice(0, n);
  while (next.length < n) next.push(emptyFactory());
  return next;
}

/** Слоты с одной и той же книгой (дубликат загрузки) — для подсказки в UI. */
function findManualDuplicateWorkbookSlots(manualFiles, slotCount = MANUAL_SLOT_COUNT_MAX) {
  const byName = new Map();
  const dups = [];
  for (let i = 0; i < slotCount; i++) {
    const e = manualFiles[i];
    if (!e?.fileName) continue;
    const key = String(e.fileName).trim().toLowerCase();
    if (!key) continue;
    const sheetN = getManualEntrySheetCount(e);
    if (sheetN < 2) continue;
    if (byName.has(key)) dups.push({ name: e.fileName, slots: [byName.get(key), i] });
    else byName.set(key, i);
  }
  return dups;
}

function buildManualFileEntryFromExcel(f, sheetsByName, sheetNames, sheetName) {
  const names = sheetNames && sheetNames.length > 0 ? sheetNames : Object.keys(sheetsByName);
  const sn = sheetName && names.includes(sheetName) ? sheetName : (names[0] || 'Лист1');
  const sh = sheetsByName[sn] || { headers: [], rows: [] };
  const sourceClone = cloneManualSheetsByName(sheetsByName);
  return {
    file: f,
    fileName: f.name,
    sheetNames: names,
    sheetsByName,
    excelSheetName: sn,
    headers: sh.headers,
    rows: sh.rows,
    _sourceSheetsByName: sourceClone,
    _sourceSheetNames: [...names],
  };
}

function applyManualExcelSheetToEntry(entry, sheetName) {
  if (!entry?.sheetsByName) return entry;
  const names = entry.sheetNames || Object.keys(entry.sheetsByName);
  const sn = sheetName && names.includes(sheetName) ? sheetName : (names[0] || entry.excelSheetName);
  const sh = entry.sheetsByName[sn] || { headers: [], rows: [] };
  return { ...entry, excelSheetName: sn, headers: sh.headers, rows: sh.rows };
}

/** Слот использует книгу из masterIdx и выбранную вкладку (режим «один Excel — 4 листа»). */
function linkManualSlotToWorkbook(masterEntry, sheetName, masterSlotIdx) {
  if (!masterEntry?.sheetsByName) return null;
  const base = {
    file: masterEntry.file,
    fileName: masterEntry.fileName,
    sheetNames: masterEntry.sheetNames,
    sheetsByName: masterEntry.sheetsByName,
    workbookSourceSlot: masterSlotIdx != null ? masterSlotIdx : 0,
    _sourceSheetsByName: masterEntry._sourceSheetsByName || cloneManualSheetsByName(masterEntry.sheetsByName),
    _sourceSheetNames: masterEntry._sourceSheetNames || [...(masterEntry.sheetNames || [])],
  };
  return applyManualExcelSheetToEntry(base, sheetName);
}

function guessExportDateFromSheetName(sheetName) {
  const s = String(sheetName || '').trim();
  let m = s.match(/(\d{1,2})[.\-/](\d{1,2})(?:[.\-/](\d{2,4}))?/);
  if (m) {
    let y = m[3] ? parseInt(m[3], 10) : new Date().getFullYear();
    if (y < 100) y += 2000;
    return `${y}-${String(m[2]).padStart(2, '0')}-${String(m[1]).padStart(2, '0')}`;
  }
  m = s.match(/^(\d{2})(\d{2})(\d{2,4})?$/);
  if (m) {
    let y = m[3] ? parseInt(m[3], 10) : new Date().getFullYear();
    if (y < 100) y += 2000;
    return `${y}-${m[2]}-${m[1]}`;
  }
  return '';
}

function getManualWorkbookMaster(manualFiles, preferSlot = null) {
  if (preferSlot != null && manualFiles[preferSlot]?.sheetsByName) {
    return { slot: preferSlot, entry: manualFiles[preferSlot] };
  }
  for (let i = 0; i < 4; i++) {
    const e = manualFiles[i];
    if (e?.sheetsByName && (e.sheetNames?.length > 0 || Object.keys(e.sheetsByName).length > 0)) {
      return { slot: i, entry: e };
    }
  }
  return null;
}

function getManualFileBlobForSlot(slotIdx, manualFiles) {
  const entry = manualFiles[slotIdx];
  if (!entry) return null;
  const masterIdx = entry.workbookSourceSlot != null ? Number(entry.workbookSourceSlot) : slotIdx;
  return manualFiles[masterIdx]?.file || entry.file || null;
}

/** Сколько вкладок (листов) в книге Excel для слота (с учётом «та же книга»). */
function getManualWorkbookSheetCountForSlot(manualFiles, slotIdx) {
  const entry = manualFiles[slotIdx];
  if (!entry) return 1;
  const masterIdx = entry.workbookSourceSlot != null ? Number(entry.workbookSourceSlot) : slotIdx;
  const master = manualFiles[masterIdx] || entry;
  const { names } = getMergeSourceSheets(master);
  if (names.length > 0) return names.length;
  const n = (master.sheetNames || []).length;
  return n > 0 ? n : 1;
}


function getManualSheetRowCount(sheet) {
  if (!sheet || !Array.isArray(sheet.rows)) return 0;
  return sheet.rows.filter(manualRowHasData).length;
}

function formatManualSheetTabLabel(name, rowCount) {
  const label = String(name || 'Лист').trim() || 'Лист';
  const n = Number.isFinite(rowCount) && rowCount >= 0 ? rowCount : 0;
  return `${label} (${n})`;
}

/** Сворачиваемая пошаговая инструкция в «Ручное управление». */
function VprManualGuidePanel({ title, open, onToggle, children }) {
  return (
    <div
      style={{
        marginBottom: '10px',
        borderRadius: '10px',
        border: '2px solid rgba(33, 147, 176, 0.45)',
        background: 'rgba(240, 249, 255, 0.95)',
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 14px',
          border: 'none',
          background: 'rgba(255, 255, 255, 0.6)',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 700,
          color: '#0e7490',
          textAlign: 'left',
        }}
      >
        {open ? <FiChevronUp size={18} aria-hidden /> : <FiChevronDown size={18} aria-hidden />}
        <span style={{ flex: 1 }}>{title}</span>
        <span style={{ fontSize: '12px', fontWeight: 500, color: '#64748b' }}>{open ? 'Свернуть' : 'Развернуть'}</span>
      </button>
      {open && (
        <div
          style={{
            padding: '12px 14px 14px',
            borderTop: '1px solid rgba(33, 147, 176, 0.25)',
            fontSize: '13px',
            lineHeight: 1.55,
            color: '#0f172a',
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

function fixVprDisplayFileName(name) {
  if (!name || typeof name !== 'string') return '';
  const s = name.trim();
  if (/[ÐÑÃ]/.test(s)) {
    try {
      const bytes = new Uint8Array([...s].map((c) => c.charCodeAt(0) & 0xff));
      const fixed = new TextDecoder('utf-8').decode(bytes);
      if (fixed && !/[ÐÑÃ]/.test(fixed)) return fixed;
    } catch (_) {}
  }
  return s;
}

function manualFileConfigForApi(slotIdx, manualFileConfigs, manualFiles, manualSelectedCols) {
  const raw = manualFileConfigs[slotIdx] || {};
  const c = syncManualActionPipelineForRun(raw, manualSelectedCols?.[slotIdx] || []);
  const sheet = manualFiles[slotIdx]?.excelSheetName;
  const pipeline = Array.isArray(c.actionPipeline) ? c.actionPipeline : [];
  return {
    action: c.action || 'discrepancies',
    partnerFiles: (c.partnerFiles || []).map((p) => p),
    method: c.method || 'compare_eq',
    params: c.params || {},
    actionPipeline: pipeline.map((step) => ({
      action: step.action,
      method: step.method,
      params: step.params || {},
      selectedCols: Array.isArray(step.selectedCols) ? step.selectedCols.slice() : [],
    })),
    excelSheetName: sheet != null && String(sheet).trim() !== '' ? String(sheet).trim() : null,
  };
}

function patchManualCrossFileMatchForSlot(prev, slotIdx, headers, renames) {
  const phoneCol = detectManualCrossCol(headers, 'phone', renames);
  const phoneBySlot = [...prev.phoneBySlot];
  const fioBySlot = [...prev.fioBySlot];
  const dateBySlot = [...prev.dateBySlot];
  phoneBySlot[slotIdx] = { col: phoneCol, part: '' };
  const modeNorm = normalizeVprCrossMatchMode(prev.matchMode);
  const curFio = fioBySlot[slotIdx] || createEmptyManualCrossFioSlot();
  if (modeNorm === 'phone_fio' || modeNorm === 'phone_nd' || modeNorm === 'leading_phone_fio') {
    if (!manualCrossFioColConfigured(curFio, headers, renames)) {
      const fioCol = detectManualCrossCol(headers, 'fio', renames);
      fioBySlot[slotIdx] = { col: fioCol >= 0 ? fioCol : '', part: curFio.part || '' };
    }
  } else {
    fioBySlot[slotIdx] = { col: '', part: '' };
  }
  const ndBySlot = [...(prev.ndBySlot || [])];
  if (modeNorm === 'phone_nd') {
    const ndCol = detectManualNdCol(headers);
    ndBySlot[slotIdx] = { col: ndCol >= 0 ? ndCol : '' };
  } else if (modeNorm === 'leading_phone_fio') {
    const ndCol = detectManualNdCol(headers);
    ndBySlot[slotIdx] = { col: ndCol >= 0 ? ndCol : '' };
  } else {
    ndBySlot[slotIdx] = createEmptyManualCrossNdSlot();
  }
  const dateCol = detectManualCrossCol(headers, 'date');
  dateBySlot[slotIdx] = { col: dateCol >= 0 ? dateCol : '', part: '' };
  return { ...prev, phoneBySlot, fioBySlot, ndBySlot, dateBySlot };
}

function buildManualCrossFileMatchPayload(
  loadedIndices,
  manualCrossFileMatch,
  manualFileConfigs,
  manualFiles,
  manualMainFileIndex = 0,
  manualRenames = [],
) {
  const enabledByAction = loadedIndices.some((i) => (manualFileConfigs[i]?.action || '') === 'cross_file_phone');
  if (!manualCrossFileMatch.enabled && !enabledByAction) return { enabled: false };
  const matchMode = normalizeVprCrossMatchMode(manualCrossFileMatch.matchMode);
  const phoneOnly = matchMode === 'phone';
  const mainMapped = loadedIndices.indexOf(manualMainFileIndex);
  return {
    enabled: true,
    matchMode,
    noCallRule: manualCrossFileMatch.noCallRule || 'auto',
    leadingFileIndex: mainMapped >= 0 ? mainMapped : 0,
    leadingOnlyNd: !!manualCrossFileMatch.leadingOnlyNd,
    files: loadedIndices.map((slotIdx) => {
      const p = manualCrossFileMatch.phoneBySlot[slotIdx] || {};
      const f = manualCrossFileMatch.fioBySlot[slotIdx] || {};
      const d = manualCrossFileMatch.dateBySlot[slotIdx] || {};
      const includeInFullMatch = manualCrossFileMatch.includeInFullMatchBySlot[slotIdx] !== false;
      const sheetName = (manualFiles[slotIdx]?.excelSheetName || '').trim();
      const exportFromUi = (manualCrossFileMatch.exportDateBySlot[slotIdx] || '').trim();
      const exportDate = exportFromUi || (getManualWorkbookSheetCountForSlot(manualFiles, slotIdx) > 1 ? guessExportDateFromSheetName(sheetName) : '');
      const displayFileName = fixVprDisplayFileName(manualFiles[slotIdx]?.fileName || '');
      const workbookSheetCount = getManualWorkbookSheetCountForSlot(manualFiles, slotIdx);
      const multiSheetBook = workbookSheetCount > 1;
      const out = {
        fileLabel: multiSheetBook && sheetName ? `${displayFileName} → ${sheetName}` : (displayFileName || `Файл ${slotIdx + 1}`),
        fileName: displayFileName,
        excelSheetName: multiSheetBook ? sheetName : null,
        workbookSheetCount,
        exportDate,
        includeInFullMatch,
      };
      if (p.col != null && p.col !== '') {
        out.crossPhoneColIndex = p.col;
        if (p.part) out.crossPhoneColPart = p.part;
      }
      if (!phoneOnly && f.col != null && f.col !== '') {
        out.crossFioColIndex = f.col;
        if (f.part) out.crossFioColPart = f.part;
      }
      if (matchMode === 'phone_nd' || matchMode === 'leading_phone_fio') {
        const nd = manualCrossFileMatch.ndBySlot?.[slotIdx] || {};
        if (nd.col != null && nd.col !== '') out.crossNdColIndex = nd.col;
        if (f.col != null && f.col !== '') {
          out.crossFioColIndex = f.col;
          if (f.part) out.crossFioColPart = f.part;
        } else {
          const fioCol = detectManualCrossCol(
            manualFiles[slotIdx]?.headers || [],
            'fio',
            manualRenames[slotIdx],
          );
          if (fioCol >= 0) out.crossFioColIndex = fioCol;
        }
      }
      if (d.col != null && d.col !== '') {
        out.crossDateColIndex = d.col;
      }
      if (manualCrossFileMatch.splitByDateBySlot?.[slotIdx] && d.col != null && d.col !== '') {
        out.splitByDateColIndex = d.col;
      }
      return out;
    }),
  };
}

function previewManualUniqueDateGroups(entry, dateCol) {
  if (!entry?.rows?.length || dateCol == null || dateCol === '') return [];
  const ci = Number(dateCol);
  if (!Number.isFinite(ci) || ci < 0) return [];
  const counts = new Map();
  for (const row of entry.rows) {
    const raw = row[ci];
    const key = raw != null && String(raw).trim() !== '' ? String(raw).trim() : '(без даты)';
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  return [...counts.entries()].map(([date, count]) => ({ date, count }));
}

// Буква столбца в стиле Excel: 0 → A, 1 → B, …, 26 → AA
function colIndexToLetter(i) {
  if (i < 0) return '';
  let s = '';
  let n = i;
  while (n >= 0) {
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26) - 1;
  }
  return s || 'A';
}

export default function VprModal({ isOpen, onRequestClose, isMobile = false }) {
  const { state } = useApp();
  const isAdmin = /^admin$/i.test(String(state?.user?.role || '').trim());
  const mobile = !!isMobile;

  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  /** sheets = { "Лист1": { headers: string[], rows: string[][] }, ... } */
  const [sheets, setSheets] = useState({});
  const [activeSheet, setActiveSheet] = useState('');
  const [error, setError] = useState('');
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState(1); // 1 asc, -1 desc
  const [filterValues, setFilterValues] = useState({}); // { colIndex: '' }
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const fileInputRef = React.useRef(null);

  // Режим: один файл или слияние 4 файлов в шаблон «впр»
  const [vprMode, setVprMode] = useState('single'); // 'single' | 'merge' | 'discrepancies'
  const [mergeFile1, setMergeFile1] = useState(null);
  const [mergeFile2, setMergeFile2] = useState(null);
  const [mergeFile3, setMergeFile3] = useState(null);
  const [mergeTemplate, setMergeTemplate] = useState(null);
  const [mergeFile1Name, setMergeFile1Name] = useState('');
  const [mergeFile2Name, setMergeFile2Name] = useState('');
  const [mergeFile3Name, setMergeFile3Name] = useState('');
  const [mergeTemplateName, setMergeTemplateName] = useState('');
  const [mergeLoading, setMergeLoading] = useState(false);
  const [mergeError, setMergeError] = useState('');
  const mergeInput1Ref = React.useRef(null);
  const mergeInput2Ref = React.useRef(null);
  const mergeInput3Ref = React.useRef(null);
  const mergeTemplateRef = React.useRef(null);
  const [mergeHelpOpen, setMergeHelpOpen] = useState(false);

  // Режим: расхождения посылок (реестр УКРПОЧТА + реестр НП + загрузка 1С)
  const [discFileUkr, setDiscFileUkr] = useState(null);
  const [discFileNp, setDiscFileNp] = useState(null);
  const [discFileOneC, setDiscFileOneC] = useState(null);
  const [discFileUkrExpress, setDiscFileUkrExpress] = useState(null);
  const [discFileUkrName, setDiscFileUkrName] = useState('');
  const [discFileNpName, setDiscFileNpName] = useState('');
  const [discFileOneCName, setDiscFileOneCName] = useState('');
  const [discFileUkrExpressName, setDiscFileUkrExpressName] = useState('');
  const [discLoading, setDiscLoading] = useState(false);
  const [discError, setDiscError] = useState('');
  const [discFilterDate, setDiscFilterDate] = useState(''); // YYYY-MM-DD, пусто = без фильтра
  const [discResult, setDiscResult] = useState(null); // { npRows, ukrRows, rmpRows, npHeaders, ukrHeaders, rmpHeaders, summary, blob, fileName }
  const [discResultTab, setDiscResultTab] = useState('summary'); // 'summary' | 'rewrittenUkr' | 'zagruzka' | 'np' | ...
  const [discResultFilterFio, setDiscResultFilterFio] = useState('');
  const [discResultFilterInvoice, setDiscResultFilterInvoice] = useState('');
  const [discResultFilterPhone, setDiscResultFilterPhone] = useState('');
  const [discResultFilterNalozhkaDiff, setDiscResultFilterNalozhkaDiff] = useState('');
  const [discSendTelegramLoading, setDiscSendTelegramLoading] = useState(false);
  const [discSendTelegramError, setDiscSendTelegramError] = useState('');
  const [discTelegramSettingsOpen, setDiscTelegramSettingsOpen] = useState(false);
  const [discTelegramBotToken, setDiscTelegramBotToken] = useState('');
  const [discTelegramChatId, setDiscTelegramChatId] = useState('');
  const [discTelegramSettingsLoading, setDiscTelegramSettingsLoading] = useState(false);
  const [discTelegramSettingsSaving, setDiscTelegramSettingsSaving] = useState(false);
  const [discTelegramSettingsError, setDiscTelegramSettingsError] = useState('');
  const discInputUkrRef = React.useRef(null);
  const discInputUkrExpressRef = React.useRef(null);
  const discInputNpRef = React.useRef(null);
  const discInputOneCRef = React.useRef(null);

  const discZagruzkaRowsForCalendar = useMemo(() => {
    if (!discResult) return [];
    const rows = discResult.zagruzkaRows;
    if (!Array.isArray(rows)) return [];
    return rows;
  }, [discResult]);

  // Вкладка: Расхождения посылок | Ручное управление (отдельный блок)
  const [vprMainTab, setVprMainTab] = useState('discrepancies'); // 'discrepancies' | 'manual'
  const [manualSlotCount, setManualSlotCount] = useState(MANUAL_SLOT_COUNT_DEFAULT);
  const manualSlotList = useMemo(
    () => manualSlotIndices(manualSlotCount),
    [manualSlotCount],
  );
  const manualInputRefsStorage = useRef({});
  const getManualInputRef = useCallback((idx) => {
    if (!manualInputRefsStorage.current[idx]) {
      manualInputRefsStorage.current[idx] = React.createRef();
    }
    return manualInputRefsStorage.current[idx];
  }, []);
  const [manualFiles, setManualFiles] = useState([]); // [ { file, fileName, headers, rows }, ... ]
  const [manualSelectedCols, setManualSelectedCols] = useState([]); // [ number[] | null, ... ]
  const [manualRules, setManualRules] = useState([]); // [ { fileA, colA, action, fileB, colB }, ... ]
  const [manualFileConfigs, setManualFileConfigs] = useState(() => createInitialManualFileConfigs(MANUAL_SLOT_COUNT_DEFAULT));
  /** Раскрытие панели «Условия» по слоту файла. */
  const [manualConditionsOpen, setManualConditionsOpen] = useState(() =>
    manualSlotIndices(MANUAL_SLOT_COUNT_DEFAULT).map(() => false),
  );
  const [manualFilterDate, setManualFilterDate] = useState('');
  const [manualResult, setManualResult] = useState(null);
  const [manualDeepseekResult, setManualDeepseekResult] = useState(null);
  const [manualResultTab, setManualResultTab] = useState(0);
  const [manualLoading, setManualLoading] = useState(false);
  const [manualError, setManualError] = useState('');
  const [manualDeletedCols, setManualDeletedCols] = useState(() =>
    manualSlotIndices(MANUAL_SLOT_COUNT_DEFAULT).map(() => []),
  );
  const [manualRenames, setManualRenames] = useState(() =>
    manualSlotIndices(MANUAL_SLOT_COUNT_DEFAULT).map(() => ({})),
  );
  const [manualAddedColumns, setManualAddedColumns] = useState(() =>
    manualSlotIndices(MANUAL_SLOT_COUNT_DEFAULT).map(() => []),
  );
  /** Очередь задач: каждая — снимок настроек столбцов/правил/действий (слоты файлов 0–3). */
  const [manualTaskSteps, setManualTaskSteps] = useState([]);
  const [manualMainFileIndex, setManualMainFileIndex] = useState(0);
  const [manualUiSubTab, setManualUiSubTab] = useState('builder');
  /** Один .xlsx: слоты 2–4 берут вкладки из выбранной в зелёном блоке книги. */
  const [manualSingleWorkbookMode, setManualSingleWorkbookMode] = useState(true);
  /** Слот книги с вкладками для очередного «Разнести» (по одному файлу). */
  const [manualDistributeSourceSlot, setManualDistributeSourceSlot] = useState(null);
  /** С какого слота начать вкладки (по умолчанию 2 — слот 1 для отдельного файла). */
  const [manualDistributeStartSlot, setManualDistributeStartSlot] = useState(1);
  /** После «Разнести»: диапазоны слотов по каждому исходному файлу. */
  const [manualDistributedRanges, setManualDistributedRanges] = useState([]);
  const [manualGuideSingleOpen, setManualGuideSingleOpen] = useState(false);
  const [manualGuideTabsOpen, setManualGuideTabsOpen] = useState(false);
  const [manualNewTaskTitle, setManualNewTaskTitle] = useState('');
  const [manualPresetName, setManualPresetName] = useState('');
  const [manualPresetsList, setManualPresetsList] = useState([]);
  /** Режим: клик по маркеру добавляет столбец в цветную цепочку (файл1 → файл2 → …). */
  const [manualCrossFileMatch, setManualCrossFileMatch] = useState(() =>
    createDefaultManualCrossFileMatch(MANUAL_SLOT_COUNT_DEFAULT),
  );
  const [manualLinkMode, setManualLinkMode] = useState(false);
  /** Завершённые цепочки: { id, color, linkAction, picks: [{ fileIdx, colIdx }] } */
  const [manualColumnChains, setManualColumnChains] = useState([]);
  /** Черновик цепочки (тот же цвет, пока не нажали «Завершить»). */
  const [manualChainDraft, setManualChainDraft] = useState(null);
  /** Фильтр строк только в превью вкладок результата (файл .xlsx при скачивании полный). */
  const [manualPreviewDisplayRules, setManualPreviewDisplayRules] = useState([]);
  const [manualPreviewRulesOpen, setManualPreviewRulesOpen] = useState(false);
  const [manualResultTextFilters, setManualResultTextFilters] = useState({ name: '', phone: '', waybill: '' });

  const manualDataSlotIndices = useMemo(
    () => getManualLoadedSlotIndices(manualSlotList, manualFiles),
    [manualSlotList, manualFiles],
  );

  const dismissManualRunResult = useCallback(() => {
    setManualResult(null);
    setManualResultTab(0);
    setManualPreviewDisplayRules([]);
    setManualPreviewRulesOpen(false);
    setManualResultTextFilters({ name: '', phone: '', waybill: '' });
  }, []);

  const clearManualSlot = useCallback((slotIdx) => {
    dismissManualRunResult();
    setManualDistributedRanges((prevRanges) => {
      const asMaster = prevRanges.find((r) => Number(r.sourceSlot) === slotIdx);
      const asChild = prevRanges.find((r) => {
        const rs = Number(r.startSlot) || 0;
        const rf = Number(r.filled) || 0;
        return slotIdx >= rs && slotIdx < rs + rf;
      });
      const range = asMaster || asChild;
      const slotsToClear = [slotIdx];
      if (range) {
        const src = Number(range.sourceSlot);
        const rs = Number(range.startSlot) || 0;
        const rf = Number(range.filled) || 0;
        slotsToClear.length = 0;
        slotsToClear.push(src);
        for (let t = rs; t < rs + rf; t++) slotsToClear.push(t);
      }
      const uniqueSlots = [...new Set(slotsToClear)];
      const clearOne = (arr, empty) => {
        const next = arr.slice(0, manualSlotCount);
        uniqueSlots.forEach((i) => { next[i] = typeof empty === 'function' ? empty() : empty; });
        return next;
      };
      setManualFiles((prev) => clearOne(prev, null));
      setManualSelectedCols((prev) => clearOne(prev, []));
      setManualDeletedCols((prev) => clearOne(prev, []));
      setManualRenames((prev) => clearOne(prev, {}));
      setManualAddedColumns((prev) => clearOne(prev, []));
      setManualFileConfigs((prev) => clearOne(prev, createDefaultManualFileConfigEntry));
      setManualCrossFileMatch((prev) => {
        const phoneBySlot = [...prev.phoneBySlot];
        const fioBySlot = [...prev.fioBySlot];
        const ndBySlot = [...(prev.ndBySlot || [])];
        const dateBySlot = [...prev.dateBySlot];
        const exportDateBySlot = [...prev.exportDateBySlot];
        const includeInFullMatchBySlot = [...prev.includeInFullMatchBySlot];
        uniqueSlots.forEach((i) => {
          phoneBySlot[i] = createEmptyManualCrossPhoneSlot();
          fioBySlot[i] = createEmptyManualCrossFioSlot();
          ndBySlot[i] = createEmptyManualCrossNdSlot();
          dateBySlot[i] = createEmptyManualCrossPhoneSlot();
          exportDateBySlot[i] = '';
          includeInFullMatchBySlot[i] = true;
        });
        return { ...prev, phoneBySlot, fioBySlot, ndBySlot, dateBySlot, exportDateBySlot, includeInFullMatchBySlot };
      });
      if (uniqueSlots.includes(manualDistributeSourceSlot)) setManualDistributeSourceSlot(null);
      setManualError('');
      if (range) return prevRanges.filter((r) => Number(r.sourceSlot) !== Number(range.sourceSlot));
      return prevRanges.filter((r) => Number(r.sourceSlot) !== slotIdx);
    });
  }, [manualSlotCount, manualDistributeSourceSlot, dismissManualRunResult]);

  const applyManualActionForSlot = useCallback((slotIdx) => {
    const cfg = manualFileConfigs[slotIdx];
    const action = cfg?.action || '';
    if (!MANUAL_PIPELINE_ACTIONS.has(action)) {
      setManualError('Для этого действия используйте «Запустить» (расхождения / сравнение по телефону).');
      return;
    }
    const rawEntry = manualFiles[slotIdx];
    if (!rawEntry) {
      setManualError('Сначала загрузите файл в слот ' + (slotIdx + 1));
      return;
    }
    const entry = cloneManualSlotEntry(rawEntry);
    const cols = (manualSelectedCols[slotIdx] || []).slice();
    const res = applyManualActionToEntry(entry, cfg, cols);
    if (!res.ok) {
      setManualError(res.error || 'Не удалось применить действие');
      return;
    }
    syncManualEntryActiveSheet(entry);
    const stepParams = { ...(cfg.params || {}) };
    if (res.phoneFormatColIndices?.length) {
      stepParams.phoneFormatColIndices = res.phoneFormatColIndices.slice();
    } else if (Array.isArray(stepParams.phoneFormatColIndices) && stepParams.phoneFormatColIndices.length > 0) {
      stepParams.phoneFormatColIndices = stepParams.phoneFormatColIndices.slice();
    }
    if (res.replaceTextColIndices?.length) {
      stepParams.replaceTextColIndices = res.replaceTextColIndices.slice();
    } else if (Array.isArray(stepParams.replaceTextColIndices) && stepParams.replaceTextColIndices.length > 0) {
      stepParams.replaceTextColIndices = stepParams.replaceTextColIndices.slice();
    }
    const step = {
      ...serializeManualActionStep({ ...cfg, params: stepParams }),
      selectedCols: (res.selectedCols || cols).slice(),
    };
    dismissManualRunResult();
    if (res.mergeStats?.length) {
      const parts = res.mergeStats.map((s) => `«${s.sheet}»: ${s.rows} строк`);
      const datePart = (res.dateSummary || []).map((d) => `${d.date} (${d.count})`).join(', ');
      setManualError(
        `Объединено ${res.totalRows ?? entry.rows?.length ?? 0} строк: ${parts.join('; ')}. `
        + (datePart ? `В столбце даты: ${datePart}. ` : '')
        + 'В результате прокрутите таблицу вниз или скачайте файл — первые 50 строк в превью могут быть только с одной даты.',
      );
    } else {
      setManualError('');
    }
    setManualFiles((prev) => {
      const next = prev.slice(0, manualSlotCount);
      next[slotIdx] = entry;
      return next;
    });
    if (res.selectedCols) {
      setManualSelectedCols((prev) => {
        const next = prev.slice(0, manualSlotCount);
        next[slotIdx] = ensureManualMergeColsSelected(entry.headers, res.selectedCols);
        return next;
      });
    }
    setManualFileConfigs((prev) => {
      const next = prev.slice();
      const cur = next[slotIdx] || createDefaultManualFileConfigEntry();
      const pipeline = [...(cur.actionPipeline || []), step];
      next[slotIdx] = {
        ...cur,
        actionPipeline: pipeline,
        excelSheetName: entry.excelSheetName || cur.excelSheetName,
      };
      return next;
    });
    setManualCrossFileMatch((prev) => patchManualCrossFileMatchForSlot(prev, slotIdx, entry.headers || [], manualRenames[slotIdx]));
  }, [
    manualFileConfigs,
    manualFiles,
    manualSelectedCols,
    manualRenames,
    manualSlotCount,
    dismissManualRunResult,
  ]);

  const applyManualSlotCount = useCallback((newCount) => {
    const n = MANUAL_SLOT_COUNT_OPTIONS.includes(newCount)
      ? newCount
      : MANUAL_SLOT_COUNT_DEFAULT;
    setManualSlotCount(n);
    setManualFiles((prev) => prev.slice(0, n));
    setManualSelectedCols((prev) => resizeManualSlotArray(prev, n, () => []));
    setManualFileConfigs((prev) => resizeManualSlotArray(prev, n, createDefaultManualFileConfigEntry));
    setManualConditionsOpen((prev) => resizeManualSlotArray(prev, n, () => false));
    setManualDeletedCols((prev) => resizeManualSlotArray(prev, n, () => []));
    setManualRenames((prev) => resizeManualSlotArray(prev, n, () => ({})));
    setManualAddedColumns((prev) => resizeManualSlotArray(prev, n, () => []));
    setManualCrossFileMatch((prev) => resizeManualCrossFileMatch(prev, n));
    setManualMainFileIndex((prev) => (prev >= n ? 0 : prev));
    setManualDistributeStartSlot((prev) => Math.min(prev, Math.max(0, n - 1)));
    if (manualDistributeSourceSlot != null && manualDistributeSourceSlot >= n) {
      setManualDistributeSourceSlot(null);
    }
    setManualDistributedRanges([]);
    dismissManualRunResult();
  }, [manualDistributeSourceSlot, dismissManualRunResult]);
  const [manualPreviewRuleForm, setManualPreviewRuleForm] = useState(() => ({
    sheetTab: 'all',
    columnMode: 'index',
    columnIndex: 0,
    headerContains: '',
    compareOp: 'gte',
    threshold: '1',
    useAbs: true,
  }));

  const manualAllResultSheets = useMemo(() => {
    const base = manualResult?.sheets || [];
    const aiSheets = buildDeepseekResultSheets(manualDeepseekResult);
    return [...base, ...aiSheets];
  }, [manualResult, manualDeepseekResult]);

  const manualDeepseekSummary = useMemo(
    () => getDeepseekComparisonSummary(manualDeepseekResult),
    [manualDeepseekResult],
  );

  useEffect(() => {
    if (!manualResult?.sheets?.length || !manualDeepseekResult?.engine) return;
    const aiSheets = buildDeepseekResultSheets(manualDeepseekResult);
    if (aiSheets.length) setManualResultTab(manualResult.sheets.length);
  }, [manualResult, manualDeepseekResult]);

  const manualPreviewFiltered = useMemo(() => {
    if (!manualAllResultSheets.length) return null;
    const sh = manualAllResultSheets[manualResultTab];
    if (!sh) return null;
    const allRows = sh.rows || [];
    const headers = sh.headers || [];
    const rules = manualPreviewDisplayRules.filter((r) => r.sheetTab === 'all' || Number(r.sheetTab) === manualResultTab);
    let rows = rules.length > 0
      ? allRows.filter((row) => manualPreviewRowPasses(row, headers, rules))
      : allRows.slice();
    const hasTextFilter = ['name', 'phone', 'waybill'].some((k) => String(manualResultTextFilters[k] || '').trim() !== '');
    if (hasTextFilter) {
      rows = rows.filter((row) => manualResultRowPassesTextFilters(row, headers, manualResultTextFilters));
    }
    const filtered = rules.length > 0 || hasTextFilter;
    return { rows, sourceTotal: allRows.length, filtered };
  }, [manualAllResultSheets, manualResultTab, manualPreviewDisplayRules, manualResultTextFilters]);

  // Refs для синхронизации горизонтального скролла (полоса всегда внизу viewport)
  const discScrollContentRef = useRef(null);
  const discHScrollRef = useRef(null);
  const manualScrollContentRef = useRef(null);
  const manualHScrollRef = useRef(null);
  const [discContentScrollWidth, setDiscContentScrollWidth] = useState(0);
  const [manualContentScrollWidth, setManualContentScrollWidth] = useState(0);
  const discScrollSyncRef = useRef(false);
  const manualScrollSyncRef = useRef(false);

  // Фон модалки ВПР из настроек (чтобы не зависеть от порядка вызова applyFrontendSettings после обновления страницы)
  const [vprBackgroundState, setVprBackgroundState] = useState(() => {
    const s = loadFrontendSettings();
    return normalizeVprBackground(s?.modals?.vpr?.background);
  });
  useEffect(() => {
    const s = loadFrontendSettings();
    setVprBackgroundState(normalizeVprBackground(s?.modals?.vpr?.background));
  }, [isOpen]);
  useEffect(() => {
    const handler = () => {
      const s = loadFrontendSettings();
      setVprBackgroundState(normalizeVprBackground(s?.modals?.vpr?.background));
    };
    window.addEventListener('frontendSettingsUpdated', handler);
    return () => window.removeEventListener('frontendSettingsUpdated', handler);
  }, []);

  // На удалённом клиенте applyFrontendSettings может ещё не вызваться — при открытии модалки принудительно выставляем фон в :root
  useEffect(() => {
    if (!isOpen) return;
    const bg = normalizeVprBackground(loadFrontendSettings()?.modals?.vpr?.background);
    document.documentElement.style.setProperty('--modal-vpr-background', bg);
    document.documentElement.style.setProperty('--modal-vpr-discrepancies-background', bg);
  }, [isOpen]);

  // Цвета подписей «1. реестр…», описаний под полями и подписи «Дата» — из настроек, инлайном (как фон), чтобы гарантированно применялись
  const getVprColors = useCallback((settingsOverride) => {
    const s = (settingsOverride?.modals?.vpr != null ? settingsOverride.modals.vpr : loadFrontendSettings()?.modals?.vpr) || {};
    const text = s.textColor || '#1a5f6e';
    const label = (s.fileLabelColor != null && s.fileLabelColor !== '') ? s.fileLabelColor : text;
    const desc = (s.fileDescriptionColor != null && s.fileDescriptionColor !== '') ? s.fileDescriptionColor : label;
    const dateLabel = (s.dateLabelColor != null && s.dateLabelColor !== '') ? s.dateLabelColor : text;
    return { fileLabel: label, fileDescription: desc, dateLabel };
  }, []);
  const [vprFileLabelColor, setVprFileLabelColor] = useState(() => getVprColors().fileLabel);
  const [vprFileDescriptionColor, setVprFileDescriptionColor] = useState(() => getVprColors().fileDescription);
  const [vprDateLabelColor, setVprDateLabelColor] = useState(() => getVprColors().dateLabel);
  useEffect(() => {
    const { fileLabel, fileDescription, dateLabel } = getVprColors();
    setVprFileLabelColor(fileLabel);
    setVprFileDescriptionColor(fileDescription);
    setVprDateLabelColor(dateLabel);
  }, [isOpen, getVprColors]);

  // Обновить ширину контента для горизонтальной полосы (расхождения и ручное)
  useEffect(() => {
    if (!discResult || mobile) return;
    const t = setTimeout(() => {
      const el = discScrollContentRef.current;
      if (el) {
        setDiscContentScrollWidth(el.scrollWidth);
        if (discHScrollRef.current) discHScrollRef.current.scrollLeft = el.scrollLeft;
      }
    }, 100);
    return () => clearTimeout(t);
  }, [discResult, discResultTab, mobile]);
  useEffect(() => {
    if (!manualResult?.sheets?.length || mobile) return;
    const t = setTimeout(() => {
      const el = manualScrollContentRef.current;
      if (el) {
        setManualContentScrollWidth(el.scrollWidth);
        if (manualHScrollRef.current) manualHScrollRef.current.scrollLeft = el.scrollLeft;
      }
    }, 100);
    return () => clearTimeout(t);
  }, [manualResult, manualResultTab, mobile, manualPreviewDisplayRules]);
  useEffect(() => {
    const handler = (e) => {
      const settings = e?.detail?.settings;
      const { fileLabel, fileDescription, dateLabel } = getVprColors(settings);
      setVprFileLabelColor(fileLabel);
      setVprFileDescriptionColor(fileDescription);
      setVprDateLabelColor(dateLabel);
    };
    window.addEventListener('frontendSettingsUpdated', handler);
    return () => window.removeEventListener('frontendSettingsUpdated', handler);
  }, [getVprColors]);

  useEffect(() => {
    if (!isOpen || vprMainTab !== 'manual') return;
    try {
      const raw = localStorage.getItem(VPR_MANUAL_PRESETS_KEY);
      if (raw) setManualPresetsList(JSON.parse(raw));
    } catch {
      setManualPresetsList([]);
    }
  }, [isOpen, vprMainTab]);

  const persistManualPresets = useCallback((list) => {
    try {
      localStorage.setItem(VPR_MANUAL_PRESETS_KEY, JSON.stringify(list));
      setManualPresetsList(list);
    } catch {
      /* noop */
    }
  }, []);

  const currentSheet = sheets[activeSheet] || null;
  const headers = currentSheet?.headers ?? [];
  const rows = currentSheet?.rows ?? [];
  const sheetNames = Object.keys(sheets);

  const filterDiscRows = useCallback((rowsArr, fio, invoice, phone) => {
    if (!rowsArr || !rowsArr.length) return rowsArr;
    const f = (fio || '').trim().toLowerCase();
    const i = (invoice || '').trim().toLowerCase();
    const p = (phone || '').trim().toLowerCase();
    if (!f && !i && !p) return rowsArr;
    return rowsArr.filter((row) => {
      const text = (Array.isArray(row) ? row : []).map((c) => String(c ?? '')).join(' ').toLowerCase();
      if (f && !text.includes(f)) return false;
      if (i && !text.includes(i)) return false;
      if (p && !text.includes(p)) return false;
      return true;
    });
  }, []);

  const discNalozhkaRowsFiltered = useMemo(() => {
    if (!discResult) return [];
    const base = filterDiscRows(
      discResult.nalozhkaRows || [],
      discResultFilterFio,
      discResultFilterInvoice,
      discResultFilterPhone
    );
    const raw = String(discResultFilterNalozhkaDiff || '').trim().replace(',', '.');
    if (!raw) return base;
    const target = parseFloat(raw);
    if (!Number.isFinite(target)) return base;
    const targetCents = Math.round(target * 100);
    return base.filter((row) => {
      const cell = row[3];
      let n;
      if (typeof cell === 'number' && Number.isFinite(cell)) n = cell;
      else n = parseFloat(String(cell ?? '').replace(/\s/g, '').replace(',', '.'));
      if (!Number.isFinite(n)) return false;
      return Math.round(n * 100) === targetCents;
    });
  }, [
    discResult,
    discResultFilterFio,
    discResultFilterInvoice,
    discResultFilterPhone,
    discResultFilterNalozhkaDiff,
    filterDiscRows,
  ]);

  const handleFileChange = useCallback((e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setError('');
    setFileName(f.name);

    if (isExcelFile(f)) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const ab = ev.target?.result;
          if (!(ab instanceof ArrayBuffer)) {
            setError('Не удалось прочитать файл Excel.');
            return;
          }
          const wb = XLSX.read(ab, { type: 'array', cellDates: true });
          if (!wb.SheetNames || wb.SheetNames.length === 0) {
            setError('В файле Excel нет листов.');
            return;
          }
          const nextSheets = {};
          for (const name of wb.SheetNames) {
            const sheet = wb.Sheets[name];
            const data = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: false });
            const head = (data[0] || []).map((c) => (c != null ? String(c) : ''));
            const body = (data.length > 1 ? data.slice(1) : []).map((row) => (Array.isArray(row) ? row : []).map((c) => (c != null ? String(c) : '')));
            nextSheets[name] = { headers: head, rows: body };
          }
          setSheets(nextSheets);
          setActiveSheet(wb.SheetNames[0]);
          setFile(f);
          setSortKey(null);
          setFilterValues({});
        } catch (err) {
          setError('Ошибка чтения Excel: ' + (err.message || 'неизвестная ошибка'));
        }
      };
      reader.readAsArrayBuffer(f);
    } else {
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const text = ev.target?.result;
          if (typeof text !== 'string') {
            setError('Поддерживаются CSV (UTF-8) или Excel (.xlsx, .xls).');
            return;
          }
          const lines = parseCSV(text);
          if (lines.length === 0) {
            setError('Файл пуст или неверный формат.');
            return;
          }
          const head = lines[0];
          const body = lines.slice(1);
          setSheets({ [DEFAULT_SHEET_NAME]: { headers: head, rows: body } });
          setActiveSheet(DEFAULT_SHEET_NAME);
          setFile(f);
          setSortKey(null);
          setFilterValues({});
        } catch (err) {
          setError('Ошибка чтения файла: ' + (err.message || 'неизвестная ошибка'));
        }
      };
      reader.readAsText(f, 'UTF-8');
    }
    e.target.value = '';
  }, []);

  const handleSort = useCallback((colIndex) => {
    if (sortKey === colIndex) {
      setSortDir((d) => -d);
    } else {
      setSortKey(colIndex);
      setSortDir(1);
    }
  }, [sortKey]);

  /** Список { rowIndex, row } после фильтра и сортировки (rowIndex = 0-based в rows, для формул строка = rowIndex+1). */
  const filteredAndSortedRows = useMemo(() => {
    let list = rows.map((r, idx) => ({ rowIndex: idx, row: r.slice() }));
    Object.entries(filterValues).forEach(([colStr, val]) => {
      const col = parseInt(colStr, 10);
      const v = (val || '').toLowerCase().trim();
      if (v && !Number.isNaN(col)) {
        list = list.filter(({ row }) => String(row[col] ?? '').toLowerCase().includes(v));
      }
    });
    if (sortKey != null && headers[sortKey] !== undefined) {
      list.sort((a, b) => {
        const va = getDisplayValue(sheets, activeSheet, sortKey, a.rowIndex + 1);
        const vb = getDisplayValue(sheets, activeSheet, sortKey, b.rowIndex + 1);
        const type = detectType(va);
        return sortDir * compareValues(va, vb, type);
      });
    }
    return list;
  }, [rows, headers, sortKey, sortDir, filterValues, sheets, activeSheet]);

  const handleExportCSV = useCallback(() => {
    if (headers.length === 0 || !sheets[activeSheet]) return;
    const sep = ';';
    const escape = (cell) => {
      const s = String(cell ?? '');
      return s.includes(sep) || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const displayRow = (rowIndex, rawRow) =>
      rawRow.map((_, ci) => getDisplayValue(sheets, activeSheet, ci, rowIndex + 1)).map(escape).join(sep);
    const csv = [headers.map((h) => escape(h)).join(sep), ...filteredAndSortedRows.map(({ rowIndex, row }) => displayRow(rowIndex, row))].join('\r\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = (fileName ? fileName.replace(/\.[^.]+$/, '') : 'vpr') + '_export.csv';
    a.click();
    URL.revokeObjectURL(url);
  }, [headers, sheets, activeSheet, filteredAndSortedRows, fileName]);

  const handleExportXLSX = useCallback(() => {
    if (!sheets[activeSheet]) return;
    const baseName = (fileName ? fileName.replace(/\.[^.]+$/, '') : 'vpr') + '_export';
    const wb = XLSX.utils.book_new();
    const data = [
      headers,
      ...rows.map((r, ri) => headers.map((_, ci) => getDisplayValue(sheets, activeSheet, ci, ri + 1))),
    ];
    const ws = XLSX.utils.aoa_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, (activeSheet || 'Лист1').slice(0, 31));
    XLSX.writeFile(wb, baseName + '.xlsx');
  }, [sheets, activeSheet, headers, rows, fileName]);

  const handleRunAI = useCallback(async () => {
    const prompt = (aiPrompt || '').trim();
    if (!prompt) {
      setAiError('Введите промпт для ИИ.');
      return;
    }
    if (!activeSheet || !sheets[activeSheet]) {
      setAiError('Сначала загрузите файл и выберите лист.');
      return;
    }
    setAiError('');
    setAiLoading(true);
    try {
      const { data } = await api.post('/api/vpr-ai', {
        prompt,
        sheetName: activeSheet,
        headers: currentSheet.headers,
        rows: currentSheet.rows,
      });
      if (data.headers && Array.isArray(data.rows)) {
        setSheets((prev) => ({
          ...prev,
          [activeSheet]: { headers: data.headers, rows: data.rows },
        }));
        setSortKey(null);
        setFilterValues({});
        setAiPrompt('');
      } else {
        setAiError('ИИ вернул некорректный формат таблицы.');
      }
    } catch (err) {
      setAiError(err.response?.data?.error || err.message || 'Ошибка запроса к ИИ.');
    } finally {
      setAiLoading(false);
    }
  }, [aiPrompt, activeSheet, sheets, currentSheet]);


  const handleRunMerge = useCallback(async () => {
    if (!mergeFile1 || !mergeFile2 || !mergeFile3 || !mergeTemplate) {
      setMergeError('Выберите все четыре файла.');
      return;
    }
    if (!isMergeSourceFile(mergeFile1) || !isMergeSourceFile(mergeFile2)) {
      setMergeError('Файлы 1 и 2 должны быть CSV или Excel (.xlsx, .xls).');
      return;
    }
    if (!isMergeTemplateFile(mergeFile3) || !isMergeTemplateFile(mergeTemplate)) {
      setMergeError('Файлы 3 и шаблон должны быть Excel (.xlsx, .xls).');
      return;
    }
    setMergeError('');
    setMergeLoading(true);
    try {
      const form = new FormData();
      form.append('file1', mergeFile1);
      form.append('file2', mergeFile2);
      form.append('file3', mergeFile3);
      form.append('template', mergeTemplate);
      const { data, status, headers } = await api.post('/api/vpr-merge', form, {
        responseType: 'blob',
        headers: { 'Content-Type': undefined },
      });
      const contentType = headers?.['content-type'] || '';
      if (status !== 200 || contentType.includes('application/json')) {
        const text = typeof data === 'string' ? data : await (data instanceof Blob ? new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(r.result);
          r.onerror = rej;
          r.readAsText(data);
        }) : '');
        let msg = 'Ошибка слияния.';
        try {
          const j = JSON.parse(text);
          if (j && typeof j.error === 'string') msg = j.error;
        } catch (_) {}
        setMergeError(msg);
        return;
      }
      const blob = data instanceof Blob ? data : new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vpr_${Date.now()}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      if (err.response?.data instanceof Blob) {
        try {
          const text = await new Promise((res, rej) => {
            const r = new FileReader();
            r.onload = () => res(r.result);
            r.onerror = rej;
            r.readAsText(err.response.data);
          });
          const j = JSON.parse(text);
          setMergeError(j?.error || err.message || 'Ошибка запроса.');
        } catch (_) {
          setMergeError(err.message || 'Ошибка запроса.');
        }
      } else {
        setMergeError(err.response?.data?.error || err.message || 'Ошибка запроса.');
      }
    } finally {
      setMergeLoading(false);
    }
  }, [mergeFile1, mergeFile2, mergeFile3, mergeTemplate]);

  const handleRunDiscrepancies = useCallback(async () => {
    if (!discFileUkr || !discFileNp || !discFileOneC) {
      setDiscError('Выберите все три файла: реестр УКРПОЧТА, реестр НП, загрузка 1С.');
      return;
    }
    setDiscError('');
    setDiscLoading(true);
    try {
      const form = new FormData();
      form.append('ukr', discFileUkr);
      form.append('np', discFileNp);
      form.append('onec', discFileOneC);
      if (discFileUkrExpress) {
        form.append('ukrExpress', discFileUkrExpress);
      }
      if (discFilterDate) {
        form.append('filterDate', discFilterDate);
      }
      const apiUrl = discFilterDate ? `/api/vpr-discrepancies?filterDate=${encodeURIComponent(discFilterDate)}` : '/api/vpr-discrepancies';
      const { data, status, headers } = await api.post(apiUrl, form, {
        responseType: 'blob',
        headers: { 'Content-Type': undefined },
      });
      const contentType = headers?.['content-type'] || '';
      if (status !== 200 || contentType.includes('application/json')) {
        const text = typeof data === 'string' ? data : await (data instanceof Blob ? new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(r.result);
          r.onerror = rej;
          r.readAsText(data);
        }) : '');
        let msg = 'Ошибка поиска расхождений.';
        try {
          const j = JSON.parse(text);
          if (j && typeof j.error === 'string') msg = j.error;
        } catch (_) {}
        setDiscError(msg);
        return;
      }
      const blob = data instanceof Blob ? data : new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const fileName = `расхождения_посылок_${Date.now()}.xlsx`;

      // Парсим xlsx для отображения в модалке (скачивание только по кнопке «Скачать файл»)
      try {
        const ab = await blob.arrayBuffer();
        const wb = XLSX.read(ab, { type: 'arraybuffer', cellDates: true });
        const npSheet = wb.SheetNames.find((n) => /расхождения\s*нп/i.test(n)) || wb.SheetNames[0];
        const normSheetTitle = (s) => String(s ?? '').trim().normalize('NFC').toLowerCase();
        /** Лист расхождений по телефонам: как «Расхождения телефоны» с сервера + варианты EN/укр. */
        const isPhoneDiscSheetName = (n) => {
          const raw = String(n ?? '').trim();
          if (/расхождения\s*нп/i.test(raw)) return false;
          const t = normSheetTitle(n);
          if (t.includes('расхожд') && t.includes('телефон')) return true;
          const hasPhone =
            t.includes('телефон') || /\bphones?\b/.test(t) || t.includes('phone') || t.includes('тел.');
          const hasDisc =
            t.includes('расхожд') || t.includes('розбіж') || t.includes('discrep') || t.includes('mismatch');
          return hasPhone && hasDisc;
        };
        let phoneDiscSheet = wb.SheetNames.find((n) => isPhoneDiscSheetName(n)) || null;
        if (!phoneDiscSheet) {
          const npIdx = wb.SheetNames.findIndex((n) => /расхождения\s*нп/i.test(String(n)));
          if (npIdx >= 0 && npIdx + 1 < wb.SheetNames.length) {
            const next = wb.SheetNames[npIdx + 1];
            const nt = normSheetTitle(next);
            if (nt.includes('телефон') || nt.includes('phone')) phoneDiscSheet = next;
          }
        }
        const rmpSheet = wb.SheetNames.find((n) => /расхождения\s*rmp/i.test(n)) || null;
        const dropperSheet = wb.SheetNames.find((n) => /дропер/i.test(n)) || null;
        const findEmptyTtnSheetName = (names) => {
          if (!names || !names.length) return null;
          const norm = (s) => String(s).trim().replace(/\s+/g, ' ');
          const byExact = names.find((n) => /^пустой\s*ттн/i.test(norm(n)));
          if (byExact) return byExact;
          return (
            names.find((n) => {
              const s = String(n).trim();
              const low = s.toLowerCase();
              if (low.includes('пустой') && low.includes('ттн')) return true;
              return /ттн\s*\(?\s*1[сcCС]\s*\)?/i.test(s);
            }) || null
          );
        };
        const emptyTtnSheet = findEmptyTtnSheetName(wb.SheetNames);
        const zagruzkaSheet = wb.SheetNames.find((n) => /^загрузка$/i.test(String(n).trim())) || null;
        const nalozhkaSheet = wb.SheetNames.find((n) => /наложка/i.test(String(n).trim())) || null;
        const toRows = (name) => {
          if (!name) return { headers: [], rows: [] };
          const sheet = wb.Sheets[name];
          if (!sheet) return { headers: [], rows: [] };
          const data = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: false });
          const headers = (data[0] || []).map((c) => (c != null ? String(c) : ''));
          const rows = (data.length > 1 ? data.slice(1) : []).map((row) => (Array.isArray(row) ? row : []).map((c) => (c != null ? String(c) : '')));
          return { headers, rows };
        };
        const np = toRows(npSheet);
        const phoneDisc = phoneDiscSheet ? toRows(phoneDiscSheet) : { headers: [], rows: [] };
        const rmp = toRows(rmpSheet);
        const dropper = toRows(dropperSheet);
        const emptyTtn = emptyTtnSheet ? toRows(emptyTtnSheet) : { headers: [], rows: [] };
        const zagruzka = toRows(zagruzkaSheet);
        const nalozhka = nalozhkaSheet ? toRows(nalozhkaSheet) : { headers: [], rows: [] };
        const rewrittenUkrSheet = wb.SheetNames.find((n) => /укрпочта\s*переписан|переписанный/i.test(n)) || null;
        const rewrittenUkr = rewrittenUkrSheet ? toRows(rewrittenUkrSheet) : { headers: [], rows: [] };
        const summary = {
          np: (np.rows || []).length,
          phoneDisc: (phoneDisc.rows || []).length,
          nalozhka: (nalozhka.rows || []).length,
          rmp: (rmp.rows || []).length,
          dropper: (dropper.rows || []).length,
          emptyTtn: (emptyTtn.rows || []).length,
          ukrDiscrepancies: (rewrittenUkr.rows || []).length,
        };
        setDiscResult({
          npHeaders: np.headers,
          npRows: np.rows,
          phoneDiscHeaders: phoneDisc.headers,
          phoneDiscRows: phoneDisc.rows,
          ukrHeaders: [],
          ukrRows: [],
          rewrittenUkrHeaders: rewrittenUkr.headers,
          rewrittenUkrRows: rewrittenUkr.rows,
          rmpHeaders: rmp.headers,
          rmpRows: rmp.rows,
          dropperHeaders: dropper.headers,
          dropperRows: dropper.rows,
          emptyTtnHeaders: emptyTtn.headers,
          emptyTtnRows: emptyTtn.rows,
          zagruzkaHeaders: zagruzka.headers,
          zagruzkaRows: zagruzka.rows,
          nalozhkaHeaders: nalozhka.headers,
          nalozhkaRows: nalozhka.rows,
          summary,
          blob,
          fileName,
          filterDate: discFilterDate || '',
        });
      } catch (parseErr) {
        setDiscResult({
          npHeaders: [],
          npRows: [],
          phoneDiscHeaders: [],
          phoneDiscRows: [],
          ukrHeaders: [],
          ukrRows: [],
          rewrittenUkrHeaders: [],
          rewrittenUkrRows: [],
          rmpHeaders: [],
          rmpRows: [],
          dropperHeaders: [],
          dropperRows: [],
          emptyTtnHeaders: [],
          emptyTtnRows: [],
          zagruzkaHeaders: [],
          zagruzkaRows: [],
          nalozhkaHeaders: [],
          nalozhkaRows: [],
          summary: { np: 0, phoneDisc: 0, nalozhka: 0, rmp: 0, dropper: 0, emptyTtn: 0, ukrDiscrepancies: 0 },
          blob,
          fileName,
          filterDate: discFilterDate || '',
        });
      }
    } catch (err) {
      if (err.response?.data instanceof Blob) {
        try {
          const text = await new Promise((res, rej) => {
            const r = new FileReader();
            r.onload = () => res(r.result);
            r.onerror = rej;
            r.readAsText(err.response.data);
          });
          const j = JSON.parse(text);
          setDiscError(j?.error || err.message || 'Ошибка запроса.');
        } catch (_) {
          setDiscError(err.message || 'Ошибка запроса.');
        }
      } else {
        setDiscError(err.response?.data?.error || err.message || 'Ошибка запроса.');
      }
    } finally {
      setDiscLoading(false);
    }
  }, [discFileUkr, discFileNp, discFileOneC, discFileUkrExpress, discFilterDate]);

  const handleDiscDownload = useCallback(() => {
    if (!discResult?.blob || !discResult?.fileName) return;
    const url = URL.createObjectURL(discResult.blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = discResult.fileName;
    a.click();
    URL.revokeObjectURL(url);
  }, [discResult]);

  const handleDiscSendToTelegram = useCallback(async () => {
    if (!discResult?.blob || !discResult?.fileName) return;
    setDiscSendTelegramError('');
    setDiscSendTelegramLoading(true);
    try {
      const form = new FormData();
      form.append('filename', discResult.fileName);
      form.append('filterDate', discResult.filterDate ?? '');
      form.append('file', new File([discResult.blob], discResult.fileName, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      await api.post('/api/vpr-send-to-telegram', form, {
        headers: {
          'Content-Type': undefined,
          'X-Vpr-Filename-Base64': btoa(unescape(encodeURIComponent(discResult.fileName))),
        },
      });
    } catch (err) {
      setDiscSendTelegramError(err.response?.data?.error || err.message || 'Ошибка отправки в Telegram.');
    } finally {
      setDiscSendTelegramLoading(false);
    }
  }, [discResult]);

  const handleDiscAssignTask = useCallback(() => {
    if (!discResult?.blob || !discResult?.fileName) return;
    const file = new File([discResult.blob], discResult.fileName, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    window.dispatchEvent(new CustomEvent('vpr-open-create-task', { detail: { file } }));
  }, [discResult]);

  const openDiscTelegramSettings = useCallback(async () => {
    if (!isAdmin) return;
    setDiscTelegramSettingsError('');
    setDiscTelegramSettingsOpen(true);
    setDiscTelegramSettingsLoading(true);
    try {
      const { data } = await api.get('/api/vpr-telegram-settings');
      setDiscTelegramBotToken(data.botToken ?? '');
      setDiscTelegramChatId(data.chatId ?? '');
    } catch (err) {
      setDiscTelegramSettingsError(err.response?.data?.error || err.message || 'Не удалось загрузить настройки.');
      if (err.response?.status === 403) setDiscTelegramBotToken('');
      setDiscTelegramChatId('');
    } finally {
      setDiscTelegramSettingsLoading(false);
    }
  }, [isAdmin]);

  const saveDiscTelegramSettings = useCallback(async () => {
    setDiscTelegramSettingsError('');
    setDiscTelegramSettingsSaving(true);
    try {
      await api.post('/api/vpr-telegram-settings', { botToken: discTelegramBotToken.trim(), chatId: discTelegramChatId.trim() });
      setDiscTelegramSettingsOpen(false);
    } catch (err) {
      setDiscTelegramSettingsError(err.response?.data?.error || err.message || 'Ошибка сохранения.');
    } finally {
      setDiscTelegramSettingsSaving(false);
    }
  }, [discTelegramBotToken, discTelegramChatId]);

  const handleClose = useCallback(() => {
    setFile(null);
    setFileName('');
    setSheets({});
    setActiveSheet('');
    setError('');
    setAiPrompt('');
    setAiError('');
    setSortKey(null);
    setFilterValues({});
    setVprMode('single');
    setMergeFile1(null);
    setMergeFile2(null);
    setMergeFile3(null);
    setMergeTemplate(null);
    setMergeFile1Name('');
    setMergeFile2Name('');
    setMergeFile3Name('');
    setMergeTemplateName('');
    setMergeError('');
    setMergeHelpOpen(false);
    setDiscFileUkr(null);
    setDiscFileNp(null);
    setDiscFileOneC(null);
    setDiscFileUkrName('');
    setDiscFileNpName('');
    setDiscFileOneCName('');
    setDiscError('');
    setDiscFilterDate('');
    setDiscResult(null);
    setDiscResultTab('summary');
    setDiscSendTelegramError('');
    setDiscResultFilterFio('');
    setDiscResultFilterInvoice('');
    setDiscResultFilterPhone('');
    setDiscResultFilterNalozhkaDiff('');
    setVprMainTab('discrepancies');
    setManualFiles([]);
    setManualSelectedCols([]);
    setManualRules([]);
    setManualFilterDate('');
    setManualResult(null);
    setManualError('');
    setManualSlotCount(MANUAL_SLOT_COUNT_DEFAULT);
    setManualFileConfigs(createInitialManualFileConfigs(MANUAL_SLOT_COUNT_DEFAULT));
    setManualConditionsOpen(manualSlotIndices(MANUAL_SLOT_COUNT_DEFAULT).map(() => false));
    setManualDeletedCols(manualSlotIndices(MANUAL_SLOT_COUNT_DEFAULT).map(() => []));
    setManualRenames(manualSlotIndices(MANUAL_SLOT_COUNT_DEFAULT).map(() => ({})));
    setManualAddedColumns(manualSlotIndices(MANUAL_SLOT_COUNT_DEFAULT).map(() => []));
    setManualTaskSteps([]);
    setManualMainFileIndex(0);
    setManualUiSubTab('builder');
    setManualNewTaskTitle('');
    setManualPresetName('');
    setManualLinkMode(false);
    setManualColumnChains([]);
    setManualChainDraft(null);
    setManualPreviewDisplayRules([]);
    setManualPreviewRulesOpen(false);
    setManualSingleWorkbookMode(false);
    setManualDistributeSourceSlot(null);
    setManualDistributeStartSlot(1);
    setManualDistributedRanges([]);
    setManualGuideSingleOpen(false);
    setManualGuideTabsOpen(false);
    setManualCrossFileMatch(createDefaultManualCrossFileMatch(MANUAL_SLOT_COUNT_DEFAULT));
    onRequestClose();
  }, [onRequestClose]);

  if (!isOpen) return null;

  const resolvedVprBg = vprBackgroundState || VPR_DEFAULT_BACKGROUND;
  // На мобильном — на весь экран; на десктопе — справа от сайдбара
  const modalStyles = mobile
    ? {
        overlay: {
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 100002,
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: '100vh',
          margin: 0,
          padding: 0,
          display: 'flex',
          alignItems: 'stretch',
          justifyContent: 'flex-start',
          pointerEvents: 'auto',
        },
        content: {
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          maxWidth: '100%',
          height: '100%',
          maxHeight: '100vh',
          margin: 0,
          padding: 0,
          inset: 'unset',
          border: 'none',
          background: resolvedVprBg,
          overflow: 'hidden',
          zIndex: 100002,
          display: 'flex',
          alignItems: 'stretch',
          justifyContent: 'flex-start',
          transform: 'none !important',
          pointerEvents: 'auto',
          boxSizing: 'border-box',
        },
      }
    : {
  overlay: {
      backgroundColor: 'rgba(0,0,0,0.45)',
      zIndex: 100001,
      position: 'fixed',
      top: 0,
      right: 0,
      bottom: 0,
      left: '320px',
      width: 'calc(100vw - 320px)',
      height: '100vh',
      margin: 0,
      padding: 0,
      display: 'flex',
      alignItems: 'stretch',
      justifyContent: 'flex-start',
      pointerEvents: 'auto',
    },
    content: {
      position: 'fixed',
      top: 0,
      left: '320px',
      right: 0,
      bottom: 0,
      width: 'calc(100vw - 320px)',
      maxWidth: 'calc(100vw - 320px)',
      height: '100vh',
      maxHeight: '100vh',
      margin: 0,
      padding: 0,
      inset: 'unset',
      border: 'none',
      background: resolvedVprBg,
      overflow: 'hidden',
      zIndex: 100001,
      display: 'flex',
      alignItems: 'stretch',
      justifyContent: 'flex-start',
      transform: 'none !important',
      pointerEvents: 'auto',
      boxSizing: 'border-box',
    },
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      ariaHideApp={false}
      style={modalStyles}
      overlayClassName="ReactModal__Overlay--vpr"
    >
      <div onClick={handleClose} style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'stretch', justifyContent: 'flex-start', overflow: 'hidden', boxSizing: 'border-box' }}>
        <div onClick={(e) => e.stopPropagation()} style={{
          background: resolvedVprBg,
          backgroundImage: 'var(--modal-calendar-background-image, none)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          borderRadius: 0,
          width: '100%',
          maxWidth: '100%',
          height: '100%',
          maxHeight: '100%',
          boxSizing: 'border-box',
          boxShadow: 'none',
          border: 'none',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          padding: mobile ? '0' : '32px 24px',
          overflow: 'hidden',
          margin: 0,
          color: 'var(--modal-vpr-text-color, var(--modal-calendar-text-color, #1a5f6e))',
          fontSize: mobile ? '15px' : 'var(--modal-calendar-text-size, 1.125rem)',
          fontFamily: 'var(--modal-calendar-font-family, inherit)',
        }}>
        {/* Шапка — фиксированная на мобильном */}
        <div style={{
          padding: mobile ? '12px 16px' : '0 0 18px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
          borderBottom: '1px solid rgba(33, 147, 176, 0.25)',
          ...(mobile ? { position: 'sticky', top: 0, zIndex: 10, background: resolvedVprBg, boxShadow: '0 1px 0 rgba(33,147,176,0.2)' } : {}),
        }}>
          <h2 style={{
            margin: 0,
            color: 'var(--modal-vpr-header-text-color, var(--modal-calendar-text-color, #2193b0))',
            fontSize: mobile ? '1.5rem' : 'var(--modal-vpr-header-text-size, var(--modal-calendar-text-size, 1.9rem))',
            fontFamily: 'var(--modal-calendar-font-family, inherit)',
            fontWeight: 600,
          }}>
            ВПР — работа с данными
          </h2>
          <div style={{ position: 'relative', width: 52, flexShrink: 0 }}>
            <ModalCloseButton onClick={handleClose} aria-label="Закрыть" />
          </div>
        </div>

        {/* Вкладки: Расхождения посылок | Ручное управление */}
        <div style={{ display: 'flex', gap: '4px', padding: mobile ? '12px 16px' : '0 0 12px', flexShrink: 0, borderBottom: '1px solid rgba(33, 147, 176, 0.2)' }}>
          <button type="button" onClick={() => setVprMainTab('discrepancies')} style={{ padding: '10px 18px', borderRadius: '10px', border: '2px solid ' + (vprMainTab === 'discrepancies' ? 'var(--modal-vpr-header-text-color, #2193b0)' : 'rgba(33, 147, 176, 0.4)'), background: vprMainTab === 'discrepancies' ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.5)', color: 'var(--modal-vpr-text-color, #1a5f6e)', fontWeight: 600, cursor: 'pointer', fontSize: '14px' }}>
            <FiAlertCircle style={{ verticalAlign: 'middle', marginRight: 6 }} /> Расхождения посылок
          </button>
          <button type="button" onClick={() => setVprMainTab('manual')} style={{ padding: '10px 18px', borderRadius: '10px', border: '2px solid ' + (vprMainTab === 'manual' ? 'var(--modal-vpr-header-text-color, #2193b0)' : 'rgba(33, 147, 176, 0.4)'), background: vprMainTab === 'manual' ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.5)', color: 'var(--modal-vpr-text-color, #1a5f6e)', fontWeight: 600, cursor: 'pointer', fontSize: '14px' }}>
            <FiSettings style={{ verticalAlign: 'middle', marginRight: 6 }} /> Ручное управление
          </button>
        </div>

        {/* Контент с прокруткой на мобильном */}
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: mobile ? '16px' : 0, WebkitOverflowScrolling: 'touch' }}>
        {vprMainTab === 'discrepancies' && (
        <>
        <div style={{
            padding: mobile ? '0 0 20px' : '20px 0',
            flex: '1 1 auto',
            minHeight: 0,
            ...(mobile ? { minWidth: 0, width: '100%' } : {}),
            display: 'flex',
            flexDirection: 'column',
            borderBottom: mobile ? 'none' : '1px solid rgba(33, 147, 176, 0.2)',
            color: 'var(--modal-vpr-text-color, var(--modal-calendar-text-color, #1a5f6e))',
          }}>
          {discResult ? (
            /* Экран результатов расхождений */
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, ...(mobile ? { minWidth: 0, width: '100%' } : {}) }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '16px', flexShrink: 0, ...(mobile ? { flexDirection: 'column', alignItems: 'stretch' } : {}) }}>
                <button type="button" onClick={() => { setDiscResult(null); setDiscResultTab('summary'); setDiscSendTelegramError(''); }} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: mobile ? '12px 14px' : '8px 14px', borderRadius: '8px', border: '2px solid var(--modal-vpr-discrepancies-button-border-color, rgba(33, 147, 176, 0.5))', background: 'var(--modal-vpr-discrepancies-button-background, rgba(255,255,255,0.9))', color: 'var(--modal-vpr-discrepancies-button-text-color, #2193b0)', fontWeight: 600, cursor: 'pointer', fontSize: mobile ? '15px' : '14px', ...(mobile ? { justifyContent: 'center' } : {}) }}>
                  <FiArrowLeft /> Назад
                </button>
                <h3 style={{ margin: 0, fontSize: mobile ? '1.1rem' : '1.25rem', fontWeight: 600, color: 'var(--modal-vpr-discrepancies-header-color, var(--modal-vpr-header-text-color, #2193b0))', ...(mobile ? { textAlign: 'center' } : {}) }}>
                  Результаты расхождений
                </h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', ...(mobile ? { flexDirection: 'column', width: '100%' } : {}) }}>
                  <button type="button" onClick={handleDiscDownload} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: mobile ? '12px 18px' : '10px 18px', borderRadius: '10px', border: '2px solid var(--modal-vpr-discrepancies-download-button-border-color, #2193b0)', background: 'var(--modal-vpr-discrepancies-download-button-background, rgba(33, 147, 176, 0.2))', color: 'var(--modal-vpr-discrepancies-download-button-text-color, #2193b0)', fontWeight: 600, cursor: 'pointer', fontSize: '15px', ...(mobile ? { width: '100%' } : {}) }}>
                    <FiDownload /> Скачать файл
                  </button>
                  <button type="button" onClick={handleDiscSendToTelegram} disabled={discSendTelegramLoading} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: mobile ? '12px 18px' : '10px 18px', borderRadius: '10px', border: '2px solid var(--modal-vpr-discrepancies-telegram-button-border-color, #2d7d4a)', background: discSendTelegramLoading ? 'rgba(45, 125, 74, 0.2)' : 'var(--modal-vpr-discrepancies-telegram-button-background, rgba(45, 125, 74, 0.25))', color: 'var(--modal-vpr-discrepancies-telegram-button-text-color, #1a5f2e)', fontWeight: 600, cursor: discSendTelegramLoading ? 'wait' : 'pointer', fontSize: '15px', ...(mobile ? { width: '100%' } : {}) }}>
                    <FiSend /> {discSendTelegramLoading ? 'Отправка…' : 'Отправить в Telegram'}
                  </button>
                  <button type="button" onClick={handleDiscAssignTask} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: mobile ? '12px 18px' : '10px 18px', borderRadius: '10px', border: '2px solid var(--modal-vpr-discrepancies-assign-task-button-border-color, #7c3aed)', background: 'var(--modal-vpr-discrepancies-assign-task-button-background, rgba(124, 58, 237, 0.2))', color: 'var(--modal-vpr-discrepancies-assign-task-button-text-color, #5b21b6)', fontWeight: 600, cursor: 'pointer', fontSize: '15px', ...(mobile ? { width: '100%' } : {}) }}>
                    <FiCheckSquare /> Назначить задачу
                  </button>
                  {isAdmin && (
                    <button type="button" onClick={openDiscTelegramSettings} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: mobile ? '12px 18px' : '10px 18px', borderRadius: '10px', border: '2px solid var(--modal-vpr-discrepancies-button-border-color, rgba(33, 147, 176, 0.6))', background: 'var(--modal-vpr-discrepancies-button-background, rgba(255,255,255,0.9))', color: 'var(--modal-vpr-discrepancies-button-text-color, #2193b0)', fontWeight: 600, cursor: 'pointer', fontSize: '15px', ...(mobile ? { width: '100%' } : {}) }}>
                      <FiSettings /> Настройка Telegram
                    </button>
                  )}
                </div>
              </div>
              {discSendTelegramError && <p style={{ margin: '0 0 12px', color: '#c53030', fontSize: '14px', flexShrink: 0 }}>{discSendTelegramError}</p>}
              <style>{`.VprModal-disc-tab { border-radius: 10px; border: 2px solid var(--modal-vpr-discrepancies-tab-border-color, rgba(33, 147, 176, 0.5)); }
.VprModal-disc-tab:hover { background: var(--modal-vpr-discrepancies-tab-hover-background, rgba(255,255,255,0.98)) !important; border-color: var(--modal-vpr-discrepancies-tab-hover-border-color, rgba(33, 147, 176, 0.9)) !important; color: var(--modal-vpr-discrepancies-tab-hover-text-color, #2193b0) !important; border-radius: 10px; box-shadow: 0 0 0 1px rgba(33, 147, 176, 0.2); }
.VprModal-disc-table tbody tr:nth-child(odd) { background: var(--modal-vpr-discrepancies-row-bg-odd, #343b46); color: var(--modal-vpr-discrepancies-row-text, #e8eef2); }
.VprModal-disc-table tbody tr:nth-child(even) { background: var(--modal-vpr-discrepancies-row-bg-even, #3d4654); color: var(--modal-vpr-discrepancies-row-text, #e8eef2); }
.VprModal-disc-table tbody td { color: inherit; }`}</style>
              <div
                style={{
                  display: 'flex',
                  gap: '4px',
                  marginBottom: '12px',
                  flexShrink: 0,
                  flexWrap: 'wrap',
                  overflowX: 'auto',
                  overflowY: 'visible',
                  WebkitOverflowScrolling: 'touch',
                  paddingBottom: '2px',
                }}
              >
                <button type="button" className="VprModal-disc-tab" onClick={() => setDiscResultTab('summary')} style={{ flex: '1 1 auto', minWidth: 100, padding: mobile ? '12px 10px' : '10px 16px', background: discResultTab === 'summary' ? 'var(--modal-vpr-discrepancies-tab-active-background, rgba(255,255,255,0.95))' : 'var(--modal-vpr-discrepancies-tab-background, rgba(255,255,255,0.6))', color: 'var(--modal-vpr-discrepancies-tab-text-color, var(--modal-vpr-discrepancies-tab-active-text-color, #2193b0))', fontWeight: 600, cursor: 'pointer', fontSize: mobile ? '13px' : '14px' }}>
                  Краткая информация
                </button>
                {(discResult.rewrittenUkrRows?.length > 0) && (
                  <button type="button" className="VprModal-disc-tab" onClick={() => setDiscResultTab('rewrittenUkr')} style={{ flex: '1 1 auto', minWidth: 100, padding: mobile ? '12px 10px' : '10px 14px', background: discResultTab === 'rewrittenUkr' ? 'var(--modal-vpr-discrepancies-tab-active-background, rgba(255,255,255,0.95))' : 'var(--modal-vpr-discrepancies-tab-background, rgba(255,255,255,0.6))', color: 'var(--modal-vpr-discrepancies-tab-text-color, #2193b0)', fontWeight: 600, cursor: 'pointer', fontSize: mobile ? '13px' : '14px' }}>
                    Реестр УКРПОЧТА ({discResult.rewrittenUkrRows.length})
                  </button>
                )}
                <button type="button" className="VprModal-disc-tab" onClick={() => setDiscResultTab('np')} style={{ flex: '1 1 auto', minWidth: 100, padding: mobile ? '12px 10px' : '10px 20px', background: discResultTab === 'np' ? 'var(--modal-vpr-discrepancies-tab-active-background, rgba(255,255,255,0.95))' : 'var(--modal-vpr-discrepancies-tab-background, rgba(255,255,255,0.6))', color: 'var(--modal-vpr-discrepancies-tab-text-color, #2193b0)', fontWeight: 600, cursor: 'pointer', fontSize: mobile ? '13px' : '14px' }}>
                  Расхождения НП {discResult.npRows?.length ? `(${discResult.npRows.length})` : ''}
                </button>
                <button type="button" className="VprModal-disc-tab" onClick={() => setDiscResultTab('phoneDisc')} style={{ flex: '1 1 auto', minWidth: 100, padding: mobile ? '12px 10px' : '10px 14px', background: discResultTab === 'phoneDisc' ? 'var(--modal-vpr-discrepancies-tab-active-background, rgba(255,255,255,0.95))' : 'var(--modal-vpr-discrepancies-tab-background, rgba(255,255,255,0.6))', color: 'var(--modal-vpr-discrepancies-tab-text-color, #2193b0)', fontWeight: 600, cursor: 'pointer', fontSize: mobile ? '13px' : '14px' }}>
                  Расхождения телефоны {discResult.phoneDiscRows?.length ? `(${discResult.phoneDiscRows.length})` : ''}
                </button>
                <button type="button" className="VprModal-disc-tab" onClick={() => setDiscResultTab('nalozhka')} style={{ flex: '1 1 auto', minWidth: 100, padding: mobile ? '12px 10px' : '10px 16px', background: discResultTab === 'nalozhka' ? 'var(--modal-vpr-discrepancies-tab-active-background, rgba(255,255,255,0.95))' : 'var(--modal-vpr-discrepancies-tab-background, rgba(255,255,255,0.6))', color: 'var(--modal-vpr-discrepancies-tab-text-color, #2193b0)', fontWeight: 600, cursor: 'pointer', fontSize: mobile ? '13px' : '14px' }}>
                  Наложка {discResult.nalozhkaRows?.length ? `(${discResult.nalozhkaRows.length})` : ''}
                </button>
                <button type="button" className="VprModal-disc-tab" onClick={() => setDiscResultTab('rmp')} style={{ flex: '1 1 auto', minWidth: 100, padding: mobile ? '12px 10px' : '10px 20px', background: discResultTab === 'rmp' ? 'var(--modal-vpr-discrepancies-tab-active-background, rgba(255,255,255,0.95))' : 'var(--modal-vpr-discrepancies-tab-background, rgba(255,255,255,0.6))', color: 'var(--modal-vpr-discrepancies-tab-text-color, #2193b0)', fontWeight: 600, cursor: 'pointer', fontSize: mobile ? '13px' : '14px' }}>
                  Расхождения RMP {discResult.rmpRows?.length ? `(${discResult.rmpRows.length})` : ''}
                </button>
                <button type="button" className="VprModal-disc-tab" onClick={() => setDiscResultTab('dropper')} style={{ flex: '1 1 auto', minWidth: 100, padding: mobile ? '12px 10px' : '10px 20px', background: discResultTab === 'dropper' ? 'var(--modal-vpr-discrepancies-tab-active-background, rgba(255,255,255,0.95))' : 'var(--modal-vpr-discrepancies-tab-background, rgba(255,255,255,0.6))', color: 'var(--modal-vpr-discrepancies-tab-text-color, #2193b0)', fontWeight: 600, cursor: 'pointer', fontSize: mobile ? '13px' : '14px' }}>
                  Дроперы {discResult.dropperRows?.length ? `(${discResult.dropperRows.length})` : ''}
                </button>
                <button type="button" className="VprModal-disc-tab" onClick={() => setDiscResultTab('emptyTtn')} style={{ flex: '1 1 auto', minWidth: 100, padding: mobile ? '12px 10px' : '10px 14px', background: discResultTab === 'emptyTtn' ? 'var(--modal-vpr-discrepancies-tab-active-background, rgba(255,255,255,0.95))' : 'var(--modal-vpr-discrepancies-tab-background, rgba(255,255,255,0.6))', color: 'var(--modal-vpr-discrepancies-tab-text-color, #2193b0)', fontWeight: 600, cursor: 'pointer', fontSize: mobile ? '13px' : '14px' }}>
                  Пустой ТТН (1С) {discResult.emptyTtnRows?.length ? `(${discResult.emptyTtnRows.length})` : ''}
                </button>
                <button type="button" className="VprModal-disc-tab" onClick={() => setDiscResultTab('zagruzka')} style={{ flex: '1 1 auto', minWidth: 100, padding: mobile ? '12px 10px' : '10px 14px', background: discResultTab === 'zagruzka' ? 'var(--modal-vpr-discrepancies-tab-active-background, rgba(255,255,255,0.95))' : 'var(--modal-vpr-discrepancies-tab-background, rgba(255,255,255,0.6))', color: 'var(--modal-vpr-discrepancies-tab-text-color, #2193b0)', fontWeight: 600, cursor: 'pointer', fontSize: mobile ? '13px' : '14px' }}>
                  Загрузка ({discZagruzkaRowsForCalendar.length})
                </button>
              </div>
              {discResultTab !== 'summary' && discResultTab !== 'rewrittenUkr' && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '12px', flexShrink: 0, alignItems: 'center' }}>
                  <input
                    type="text"
                    placeholder="ФИО"
                    value={discResultFilterFio}
                    onChange={(e) => setDiscResultFilterFio(e.target.value)}
                    style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid rgba(33, 147, 176, 0.5)', minWidth: 120, fontSize: '14px' }}
                  />
                  <input
                    type="text"
                    placeholder="Номер накладной"
                    value={discResultFilterInvoice}
                    onChange={(e) => setDiscResultFilterInvoice(e.target.value)}
                    style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid rgba(33, 147, 176, 0.5)', minWidth: 140, fontSize: '14px' }}
                  />
                  <input
                    type="text"
                    placeholder="Номер телефона"
                    value={discResultFilterPhone}
                    onChange={(e) => setDiscResultFilterPhone(e.target.value)}
                    style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid rgba(33, 147, 176, 0.5)', minWidth: 130, fontSize: '14px' }}
                  />
                  {discResultTab === 'nalozhka' && (
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder="Разница, грн"
                      value={discResultFilterNalozhkaDiff}
                      onChange={(e) => setDiscResultFilterNalozhkaDiff(e.target.value)}
                      style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid rgba(33, 147, 176, 0.5)', minWidth: 140, fontSize: '14px' }}
                    />
                  )}
                </div>
              )}
              <style>{`.VprModal-disc-tab { border-radius: 10px; border: 2px solid var(--modal-vpr-discrepancies-tab-border-color, rgba(33, 147, 176, 0.5)); }
.VprModal-disc-tab:hover { background: var(--modal-vpr-discrepancies-tab-hover-background, rgba(255,255,255,0.98)) !important; border-color: var(--modal-vpr-discrepancies-tab-hover-border-color, rgba(33, 147, 176, 0.9)) !important; color: var(--modal-vpr-discrepancies-tab-hover-text-color, #2193b0) !important; border-radius: 10px; box-shadow: 0 0 0 1px rgba(33, 147, 176, 0.2); }
.VprModal-disc-table tbody tr:nth-child(odd) { background: var(--modal-vpr-discrepancies-row-bg-odd, #343b46); color: var(--modal-vpr-discrepancies-row-text, #e8eef2); }
.VprModal-disc-table tbody tr:nth-child(even) { background: var(--modal-vpr-discrepancies-row-bg-even, #3d4654); color: var(--modal-vpr-discrepancies-row-text, #e8eef2); }
.VprModal-disc-table tbody td { color: inherit; }`}</style>
              <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, minWidth: 0, width: mobile ? '100%' : undefined, maxWidth: '100%', height: mobile ? undefined : '55vh', minHeight: 200, maxHeight: mobile ? 'none' : '55vh' }}>
              <div
                ref={discScrollContentRef}
                style={{
                  flex: 1,
                  minHeight: 0,
                  minWidth: 0,
                  width: mobile ? '100%' : undefined,
                  maxWidth: '100%',
                  overflowX: 'scroll',
                  overflowY: 'scroll',
                  overflow: 'auto',
                  background: 'var(--modal-vpr-discrepancies-results-background, rgba(255,255,255,0.7))',
                  borderRadius: mobile ? '0 10px 10px 10px' : '10px 10px 0 0',
                  border: '2px solid var(--modal-vpr-discrepancies-results-border-color, rgba(33, 147, 176, 0.35))',
                  borderBottomWidth: mobile ? 2 : 0,
                  padding: mobile ? '8px' : '12px',
                  boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.06)',
                  WebkitOverflowScrolling: 'touch',
                  userSelect: 'text',
                  WebkitUserSelect: 'text',
                  MozUserSelect: 'text',
                  msUserSelect: 'text',
                }}
                onScroll={() => {
                  if (discScrollSyncRef.current) return;
                  if (discHScrollRef.current && discScrollContentRef.current) {
                    discScrollSyncRef.current = true;
                    discHScrollRef.current.scrollLeft = discScrollContentRef.current.scrollLeft;
                    requestAnimationFrame(() => { discScrollSyncRef.current = false; });
                  }
                }}
              >
                {discResultTab === 'summary' ? (
                  <div style={{ padding: '20px 16px', userSelect: 'text', WebkitUserSelect: 'text' }}>
                    <p style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: 600, color: 'var(--modal-vpr-discrepancies-summary-date-color, var(--modal-vpr-discrepancies-text-color, #1a5f6e))' }}>
                      {discResult.filterDate ? `За дату: ${discResult.filterDate}` : 'За весь период'}
                    </p>
                    <ul style={{ margin: 0, paddingLeft: '24px', fontSize: '16px', lineHeight: 2, listStyle: 'disc', color: 'var(--modal-vpr-discrepancies-results-text-color, var(--modal-vpr-discrepancies-text-color, #1a5f6e))' }}>
                      <li><strong style={{ color: 'var(--modal-vpr-discrepancies-summary-label-color, #1a5f6e)' }}>Новая почта</strong><span style={{ color: 'var(--modal-vpr-discrepancies-summary-value-color, #2193b0)' }}> — {discResult.summary?.np ?? 0}</span></li>
                      <li><strong style={{ color: 'var(--modal-vpr-discrepancies-summary-label-color, #1a5f6e)' }}>Расхождения телефоны</strong><span style={{ color: 'var(--modal-vpr-discrepancies-summary-value-color, #2193b0)' }}> — {discResult.summary?.phoneDisc ?? 0}</span></li>
                      <li><strong style={{ color: 'var(--modal-vpr-discrepancies-summary-label-color, #1a5f6e)' }}>Наложка</strong><span style={{ color: 'var(--modal-vpr-discrepancies-summary-value-color, #2193b0)' }}> — {discResult.summary?.nalozhka ?? 0}</span></li>
                      <li><strong style={{ color: 'var(--modal-vpr-discrepancies-summary-label-color, #1a5f6e)' }}>RMP</strong><span style={{ color: 'var(--modal-vpr-discrepancies-summary-value-color, #2193b0)' }}> — {discResult.summary?.rmp ?? 0}</span></li>
                      <li><strong style={{ color: 'var(--modal-vpr-discrepancies-summary-label-color, #1a5f6e)' }}>Дроперы</strong><span style={{ color: 'var(--modal-vpr-discrepancies-summary-value-color, #2193b0)' }}> — {discResult.summary?.dropper ?? 0}</span></li>
                      <li><strong style={{ color: 'var(--modal-vpr-discrepancies-summary-label-color, #1a5f6e)' }}>Пустой ТТН в 1С (кол. O)</strong><span style={{ color: 'var(--modal-vpr-discrepancies-summary-value-color, #2193b0)' }}> — {discResult.summary?.emptyTtn ?? 0}</span></li>
                      <li><strong style={{ color: 'var(--modal-vpr-discrepancies-summary-label-color, #1a5f6e)' }}>Расхождения УКРПОЧТА</strong><span style={{ color: 'var(--modal-vpr-discrepancies-summary-value-color, #2193b0)' }}> — {discResult.summary?.ukrDiscrepancies ?? 0}</span></li>
                      <li><strong style={{ color: 'var(--modal-vpr-discrepancies-summary-label-color, #1a5f6e)' }}>Всего расхождений</strong><span style={{ color: 'var(--modal-vpr-discrepancies-summary-value-color, #2193b0)' }}> — {(discResult.summary?.np ?? 0) + (discResult.summary?.phoneDisc ?? 0) + (discResult.summary?.nalozhka ?? 0) + (discResult.summary?.rmp ?? 0) + (discResult.summary?.dropper ?? 0) + (discResult.summary?.emptyTtn ?? 0) + (discResult.summary?.ukrDiscrepancies ?? 0)}</span></li>
                    </ul>
                  </div>
                ) : discResultTab === 'rewrittenUkr' ? (
                  (discResult.rewrittenUkrHeaders?.length > 0 || discResult.rewrittenUkrRows?.length > 0) ? (
                    <table className="VprModal-disc-table" style={{ width: '100%', minWidth: mobile ? 400 : undefined, borderCollapse: 'collapse', fontSize: mobile ? '12px' : '13px', color: 'var(--modal-vpr-discrepancies-results-text-color, var(--modal-vpr-discrepancies-text-color, #1a5f6e))', userSelect: 'text' }}>
                      <thead>
                        <tr>
                          {(discResult.rewrittenUkrHeaders || []).map((h, i) => (
                            <th key={i} style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid var(--modal-vpr-discrepancies-results-border-color, rgba(33, 147, 176, 0.4))', fontWeight: 600, color: 'var(--modal-vpr-discrepancies-results-text-color, #1a5f6e)', userSelect: 'text', whiteSpace: 'nowrap' }}>{h || `Кол.${i + 1}`}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(discResult.rewrittenUkrRows || []).map((row, ri) => {
                          const isExpress = row.some((cell) =>
                            typeof cell === 'string' && cell.toLowerCase().includes('express_розетка')
                          );
                          return (
                            <tr
                              key={ri}
                              style={{
                                borderBottom: '1px solid var(--modal-vpr-discrepancies-results-border-color, rgba(33, 147, 176, 0.2))',
                                background: isExpress ? 'rgba(255, 165, 0, 0.24)' : undefined,
                              }}
                            >
                              {(discResult.rewrittenUkrHeaders || []).map((_, ci) => (
                                <td
                                  key={ci}
                                  style={{
                                    padding: '8px 12px',
                                    userSelect: 'text',
                                    color: isExpress && ci === 0 ? '#ff7f00' : undefined,
                                    fontWeight: isExpress && ci === 0 ? 700 : undefined,
                                  }}
                                >
                                  {row[ci] ?? ''}
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : <p style={{ margin: 0, padding: '24px', opacity: 0.8 }}>Нет переписанного реестра УКРПОЧТА.</p>
                ) : discResultTab === 'np' ? (
                  <table className="VprModal-disc-table" style={{ width: '100%', minWidth: mobile ? 400 : undefined, borderCollapse: 'collapse', fontSize: mobile ? '12px' : '13px', color: 'var(--modal-vpr-discrepancies-results-text-color, var(--modal-vpr-discrepancies-text-color, #1a5f6e))', userSelect: 'text' }}>
                    <thead>
                      <tr>
                        {(discResult.npHeaders || []).map((h, i) => (
                          <th key={i} style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid var(--modal-vpr-discrepancies-results-border-color, rgba(33, 147, 176, 0.4))', fontWeight: 600, color: 'var(--modal-vpr-discrepancies-results-text-color, #1a5f6e)', userSelect: 'text', whiteSpace: 'nowrap' }}>{h || `Колонка ${i + 1}`}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filterDiscRows(discResult.npRows || [], discResultFilterFio, discResultFilterInvoice, discResultFilterPhone).map((row, ri) => (
                        <tr key={ri} style={{ borderBottom: '1px solid var(--modal-vpr-discrepancies-results-border-color, rgba(33, 147, 176, 0.2))' }}>
                          {(discResult.npHeaders || []).map((_, ci) => (
                            <td key={ci} style={{ padding: '8px 12px', userSelect: 'text', whiteSpace: 'nowrap' }}>{row[ci] ?? ''}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : discResultTab === 'phoneDisc' ? (
                  <table className="VprModal-disc-table" style={{ width: '100%', minWidth: mobile ? 400 : undefined, borderCollapse: 'collapse', fontSize: mobile ? '12px' : '13px', color: 'var(--modal-vpr-discrepancies-results-text-color, var(--modal-vpr-discrepancies-text-color, #1a5f6e))', userSelect: 'text' }}>
                    <thead>
                      <tr>
                        {(discResult.phoneDiscHeaders || []).map((h, i) => (
                          <th key={i} style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid var(--modal-vpr-discrepancies-results-border-color, rgba(33, 147, 176, 0.4))', fontWeight: 600, color: 'var(--modal-vpr-discrepancies-results-text-color, #1a5f6e)', userSelect: 'text', whiteSpace: 'nowrap' }}>{h || `Колонка ${i + 1}`}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filterDiscRows(discResult.phoneDiscRows || [], discResultFilterFio, discResultFilterInvoice, discResultFilterPhone).map((row, ri) => (
                        <tr key={ri} style={{ borderBottom: '1px solid var(--modal-vpr-discrepancies-results-border-color, rgba(33, 147, 176, 0.2))' }}>
                          {(discResult.phoneDiscHeaders || []).map((_, ci) => (
                            <td key={ci} style={{ padding: '8px 12px', userSelect: 'text', whiteSpace: 'nowrap' }}>{row[ci] ?? ''}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : discResultTab === 'nalozhka' ? (
                  (discResult.nalozhkaRows?.length > 0) ? (
                    discNalozhkaRowsFiltered.length > 0 ? (
                      <table className="VprModal-disc-table" style={{ width: '100%', minWidth: mobile ? 400 : undefined, borderCollapse: 'collapse', fontSize: mobile ? '12px' : '13px', color: 'var(--modal-vpr-discrepancies-results-text-color, var(--modal-vpr-discrepancies-text-color, #1a5f6e))', userSelect: 'text' }}>
                        <thead>
                          <tr>
                            {(discResult.nalozhkaHeaders || []).map((h, i) => (
                              <th key={i} style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid var(--modal-vpr-discrepancies-results-border-color, rgba(33, 147, 176, 0.4))', fontWeight: 600, color: 'var(--modal-vpr-discrepancies-results-text-color, #1a5f6e)', userSelect: 'text', whiteSpace: 'nowrap' }}>{h || `Кол.${i + 1}`}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {discNalozhkaRowsFiltered.map((row, ri) => (
                            <tr key={ri} style={{ borderBottom: '1px solid var(--modal-vpr-discrepancies-results-border-color, rgba(33, 147, 176, 0.2))' }}>
                              {(discResult.nalozhkaHeaders || []).map((_, ci) => (
                                <td key={ci} style={{ padding: '8px 12px', userSelect: 'text', whiteSpace: 'nowrap' }}>{row[ci] ?? ''}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p style={{ margin: 0, padding: '24px', opacity: 0.8 }}>
                        Нет строк по фильтру{discResultFilterNalozhkaDiff.trim() ? ` (разница «${discResultFilterNalozhkaDiff.trim()}» грн не найдена в списке)` : ''}.
                      </p>
                    )
                  ) : <p style={{ margin: 0, padding: '24px', opacity: 0.8 }}>Нет строк наложки: нет пары 1С/НП по ТТН (O/A), или пустые P/W, или |P−W| меньше 1 грн.</p>
                ) : discResultTab === 'rmp' ? (
                  <table className="VprModal-disc-table" style={{ width: '100%', minWidth: mobile ? 400 : undefined, borderCollapse: 'collapse', fontSize: mobile ? '12px' : '13px', color: 'var(--modal-vpr-discrepancies-results-text-color, var(--modal-vpr-discrepancies-text-color, #1a5f6e))', userSelect: 'text' }}>
                    <thead>
                      <tr>
                        {(discResult.rmpHeaders || []).map((h, i) => (
                          <th key={i} style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid var(--modal-vpr-discrepancies-results-border-color, rgba(33, 147, 176, 0.4))', fontWeight: 600, color: 'var(--modal-vpr-discrepancies-results-text-color, #1a5f6e)', userSelect: 'text', whiteSpace: 'nowrap' }}>{h || `Колонка ${i + 1}`}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filterDiscRows(discResult.rmpRows || [], discResultFilterFio, discResultFilterInvoice, discResultFilterPhone).map((row, ri) => (
                        <tr key={ri} style={{ borderBottom: '1px solid var(--modal-vpr-discrepancies-results-border-color, rgba(33, 147, 176, 0.2))' }}>
                          {(discResult.rmpHeaders || []).map((_, ci) => (
                            <td key={ci} style={{ padding: '8px 12px', userSelect: 'text', whiteSpace: 'nowrap' }}>{row[ci] ?? ''}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : discResultTab === 'dropper' ? (
                  <table className="VprModal-disc-table" style={{ width: '100%', minWidth: mobile ? 400 : undefined, borderCollapse: 'collapse', fontSize: mobile ? '12px' : '13px', color: 'var(--modal-vpr-discrepancies-results-text-color, var(--modal-vpr-discrepancies-text-color, #1a5f6e))', userSelect: 'text' }}>
                    <thead>
                      <tr>
                        {(discResult.dropperHeaders || []).map((h, i) => (
                          <th key={i} style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid var(--modal-vpr-discrepancies-results-border-color, rgba(33, 147, 176, 0.4))', fontWeight: 600, color: 'var(--modal-vpr-discrepancies-results-text-color, #1a5f6e)', userSelect: 'text', whiteSpace: 'nowrap' }}>{h || `Колонка ${i + 1}`}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filterDiscRows(discResult.dropperRows || [], discResultFilterFio, discResultFilterInvoice, discResultFilterPhone).map((row, ri) => (
                        <tr key={ri} style={{ borderBottom: '1px solid var(--modal-vpr-discrepancies-results-border-color, rgba(33, 147, 176, 0.2))' }}>
                          {(discResult.dropperHeaders || []).map((_, ci) => (
                            <td key={ci} style={{ padding: '8px 12px', userSelect: 'text', whiteSpace: 'nowrap' }}>{row[ci] ?? ''}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : discResultTab === 'emptyTtn' ? (
                  (discResult.emptyTtnHeaders?.length > 0 || discResult.emptyTtnRows?.length > 0) ? (
                    <table className="VprModal-disc-table" style={{ width: '100%', minWidth: mobile ? 400 : undefined, borderCollapse: 'collapse', fontSize: mobile ? '12px' : '13px', color: 'var(--modal-vpr-discrepancies-results-text-color, var(--modal-vpr-discrepancies-text-color, #1a5f6e))', userSelect: 'text' }}>
                      <thead>
                        <tr>
                          {(discResult.emptyTtnHeaders || []).map((h, i) => (
                            <th key={i} style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid var(--modal-vpr-discrepancies-results-border-color, rgba(33, 147, 176, 0.4))', fontWeight: 600, color: 'var(--modal-vpr-discrepancies-results-text-color, #1a5f6e)', userSelect: 'text', whiteSpace: 'nowrap' }}>{h || `Кол.${i + 1}`}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filterDiscRows(discResult.emptyTtnRows || [], discResultFilterFio, discResultFilterInvoice, discResultFilterPhone).map((row, ri) => (
                          <tr key={ri} style={{ borderBottom: '1px solid var(--modal-vpr-discrepancies-results-border-color, rgba(33, 147, 176, 0.2))' }}>
                            {(discResult.emptyTtnHeaders || []).map((_, ci) => (
                              <td key={ci} style={{ padding: '8px 12px', userSelect: 'text', whiteSpace: 'nowrap' }}>{row[ci] ?? ''}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : <p style={{ margin: 0, padding: '24px', opacity: 0.8 }}>Нет записей 1С с пустым ТТН (кол. O).</p>
                ) : discResultTab === 'zagruzka' ? (
                  (discResult.zagruzkaHeaders?.length > 0 || discResult.zagruzkaRows?.length > 0) ? (
                    <table className="VprModal-disc-table" style={{ width: '100%', minWidth: mobile ? 400 : undefined, borderCollapse: 'collapse', fontSize: mobile ? '12px' : '13px', color: 'var(--modal-vpr-discrepancies-results-text-color, var(--modal-vpr-discrepancies-text-color, #1a5f6e))', userSelect: 'text' }}>
                      <thead>
                        <tr>
                          {(discResult.zagruzkaHeaders || []).map((h, i) => (
                            <th key={i} style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid var(--modal-vpr-discrepancies-results-border-color, rgba(33, 147, 176, 0.4))', fontWeight: 600, color: 'var(--modal-vpr-discrepancies-results-text-color, #1a5f6e)', userSelect: 'text', whiteSpace: 'nowrap' }}>{h || `Кол.${i + 1}`}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filterDiscRows(discZagruzkaRowsForCalendar, discResultFilterFio, discResultFilterInvoice, discResultFilterPhone).map((row, ri) => (
                          <tr key={ri} style={{ borderBottom: '1px solid var(--modal-vpr-discrepancies-results-border-color, rgba(33, 147, 176, 0.2))' }}>
                            {(discResult.zagruzkaHeaders || []).map((_, ci) => (
                              <td key={ci} style={{ padding: '8px 12px', userSelect: 'text', whiteSpace: 'nowrap' }}>{row[ci] ?? ''}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : <p style={{ margin: 0, padding: '24px', opacity: 0.8 }}>Нет данных листа «Загрузка» (столбцы H, I, J, K, O, N реестра 1С).</p>
                ) : null}
                {discResultTab === 'emptyTtn' && !filterDiscRows(discResult.emptyTtnRows || [], discResultFilterFio, discResultFilterInvoice, discResultFilterPhone).length && (discResult.emptyTtnRows?.length > 0) && <p style={{ margin: 0, padding: '24px', opacity: 0.8 }}>Нет записей по фильтру.</p>}
                {discResultTab === 'np' && !filterDiscRows(discResult.npRows || [], discResultFilterFio, discResultFilterInvoice, discResultFilterPhone).length && <p style={{ margin: 0, padding: '24px', opacity: 0.8 }}>Нет расхождений НП{discResultFilterFio || discResultFilterInvoice || discResultFilterPhone ? ' по фильтру' : ''}.</p>}
                {discResultTab === 'phoneDisc' && !filterDiscRows(discResult.phoneDiscRows || [], discResultFilterFio, discResultFilterInvoice, discResultFilterPhone).length && <p style={{ margin: 0, padding: '24px', opacity: 0.8 }}>Нет расхождений по телефонам (1С N vs НП Z / Укрпочта D){discResultFilterFio || discResultFilterInvoice || discResultFilterPhone ? ' по фильтру' : ''}.</p>}
                {discResultTab === 'rmp' && !filterDiscRows(discResult.rmpRows || [], discResultFilterFio, discResultFilterInvoice, discResultFilterPhone).length && <p style={{ margin: 0, padding: '24px', opacity: 0.8 }}>Нет расхождений RMP за выбранную дату{discResultFilterFio || discResultFilterInvoice || discResultFilterPhone ? ' по фильтру' : ''}.</p>}
                {discResultTab === 'dropper' && !filterDiscRows(discResult.dropperRows || [], discResultFilterFio, discResultFilterInvoice, discResultFilterPhone).length && <p style={{ margin: 0, padding: '24px', opacity: 0.8 }}>Нет расхождений по дроперам{discResultFilterFio || discResultFilterInvoice || discResultFilterPhone ? ' по фильтру' : ''}.</p>}
                {discResultTab === 'zagruzka' && !filterDiscRows(discZagruzkaRowsForCalendar, discResultFilterFio, discResultFilterInvoice, discResultFilterPhone).length && discZagruzkaRowsForCalendar.length > 0 && <p style={{ margin: 0, padding: '24px', opacity: 0.8 }}>Нет строк по фильтру.</p>}
              </div>
              {!mobile && discContentScrollWidth > 0 && (
                <div
                  ref={discHScrollRef}
                  style={{ flexShrink: 0, height: 14, overflowX: 'auto', overflowY: 'hidden', background: 'var(--modal-vpr-discrepancies-results-background, rgba(255,255,255,0.8))', border: '2px solid var(--modal-vpr-discrepancies-results-border-color, rgba(33, 147, 176, 0.35))', borderTop: 'none', borderRadius: '0 0 10px 10px' }}
                  onScroll={() => {
                    if (discScrollSyncRef.current) return;
                    if (discScrollContentRef.current && discHScrollRef.current) {
                      discScrollSyncRef.current = true;
                      discScrollContentRef.current.scrollLeft = discHScrollRef.current.scrollLeft;
                      requestAnimationFrame(() => { discScrollSyncRef.current = false; });
                    }
                  }}
                >
                  <div style={{ height: 1, width: Math.max(discContentScrollWidth, 1), minWidth: '100%' }} />
                </div>
              )}
            </div>
            </div>
          ) : (
            <>
            <div style={{ fontSize: mobile ? '15px' : '16px', fontWeight: 600, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--modal-vpr-header-text-color, #2193b0)' }}>
              <FiAlertCircle /> Расхождения посылок
            </div>
            <input ref={discInputUkrRef} type="file" accept=".csv,.xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; setDiscFileUkr(f || null); setDiscFileUkrName(f?.name || ''); setDiscError(''); e.target.value = ''; }} />
            <input ref={discInputUkrExpressRef} type="file" accept=".csv,.xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; setDiscFileUkrExpress(f || null); setDiscFileUkrExpressName(f?.name || ''); setDiscError(''); e.target.value = ''; }} />
            <input ref={discInputNpRef} type="file" accept=".xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; setDiscFileNp(f || null); setDiscFileNpName(f?.name || ''); setDiscError(''); e.target.value = ''; }} />
            <input ref={discInputOneCRef} type="file" accept=".xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; setDiscFileOneC(f || null); setDiscFileOneCName(f?.name || ''); setDiscError(''); e.target.value = ''; }} />
            <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : 'repeat(auto-fill, minmax(260px, 1fr))', gap: '16px', marginBottom: '16px', alignItems: 'stretch' }}>
              <div style={{ display: 'flex', flexDirection: 'column', minHeight: mobile ? undefined : 100 }}>
                <div style={{ marginTop: 'auto' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '4px', opacity: 0.9, color: vprFileLabelColor, textAlign: 'center' }}>1. реестр УКРПОЧТА.csv</label>
                <button type="button" onClick={() => discInputUkrRef.current?.click()} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: mobile ? '14px 14px' : '10px 14px', borderRadius: '8px', border: '2px solid var(--modal-vpr-button-border-color, rgba(33, 147, 176, 0.5))', background: 'var(--modal-vpr-button-background, rgba(255,255,255,0.8))', color: 'var(--modal-vpr-button-text-color, #2193b0)', fontWeight: 600, cursor: 'pointer', fontSize: '15px', width: '100%' }}>
                  <FiUpload /> {discFileUkrName || 'Выбрать…'}
                </button>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', minHeight: mobile ? undefined : 100 }}>
                <div style={{ marginTop: 'auto' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '4px', opacity: 0.9, color: vprFileLabelColor, textAlign: 'center' }}>2. express_Розетка.csv (опционально)</label>
                <button type="button" onClick={() => discInputUkrExpressRef.current?.click()} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: mobile ? '14px 14px' : '10px 14px', borderRadius: '8px', border: '2px solid var(--modal-vpr-button-border-color, rgba(33, 147, 176, 0.5))', background: 'var(--modal-vpr-button-background, rgba(255,255,255,0.8))', color: 'var(--modal-vpr-button-text-color, #2193b0)', fontWeight: 600, cursor: 'pointer', fontSize: '15px', width: '100%' }}>
                  <FiUpload /> {discFileUkrExpressName || 'Выбрать…'}
                </button>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', minHeight: mobile ? undefined : 100 }}>
                <div style={{ marginTop: 'auto' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '4px', opacity: 0.9, color: vprFileLabelColor, textAlign: 'center' }}>3. реестр НП.xlsx</label>
                <button type="button" onClick={() => discInputNpRef.current?.click()} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: mobile ? '14px 14px' : '10px 14px', borderRadius: '8px', border: '2px solid var(--modal-vpr-button-border-color, rgba(33, 147, 176, 0.5))', background: 'var(--modal-vpr-button-background, rgba(255,255,255,0.8))', color: 'var(--modal-vpr-button-text-color, #2193b0)', fontWeight: 600, cursor: 'pointer', fontSize: '15px', width: '100%' }}>
                  <FiUpload /> {discFileNpName || 'Выбрать…'}
                </button>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', minHeight: mobile ? undefined : 100 }}>
                <div style={{ marginTop: 'auto' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '4px', opacity: 0.9, color: vprFileLabelColor, textAlign: 'left' }}>4. загрузка 1С .xlsx</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: mobile ? 'wrap' : 'nowrap', ...(mobile ? { flexDirection: 'column', alignItems: 'stretch' } : {}) }}>
                  <button type="button" onClick={() => discInputOneCRef.current?.click()} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: mobile ? '14px 14px' : '10px 14px', borderRadius: '8px', border: '2px solid var(--modal-vpr-button-border-color, rgba(33, 147, 176, 0.5))', background: 'var(--modal-vpr-button-background, rgba(255,255,255,0.8))', color: 'var(--modal-vpr-button-text-color, #2193b0)', fontWeight: 600, cursor: 'pointer', fontSize: '15px', width: mobile ? '100%' : undefined, flex: mobile ? 'none' : '0 0 auto', minWidth: mobile ? undefined : 140 }}>
                    <FiUpload /> {discFileOneCName || 'Выбрать…'}
                  </button>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, opacity: 0.9, color: vprDateLabelColor, flex: mobile ? 'none' : '0 0 auto', ...(mobile ? { width: '100%', flexDirection: 'column', alignItems: 'stretch' } : {}) }}>
                    <span>Дата:</span>
                    <input
                      type="date"
                      value={discFilterDate}
                      onChange={(e) => { setDiscFilterDate(e.target.value || ''); setDiscError(''); }}
                      style={{ padding: '10px 12px', borderRadius: '8px', border: '2px solid var(--modal-vpr-date-field-border-color, rgba(33, 147, 176, 0.5))', background: 'var(--modal-vpr-date-field-background, rgba(255,255,255,0.9))', color: 'var(--modal-vpr-date-field-color, #1a5f6e)', fontSize: '16px', minWidth: mobile ? '100%' : '140px', boxSizing: 'border-box' }}
                      title="Фильтр по дате: сравнение с датой в столбце B реестра 1С (дата документа), не со столбцом H"
                    />
                  </label>
                </div>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', ...(mobile ? { flexDirection: 'column' } : {}) }}>
              <button type="button" onClick={handleRunDiscrepancies} disabled={discLoading || !discFileUkr || !discFileNp || !discFileOneC} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: mobile ? '14px 20px' : '12px 20px', borderRadius: '10px', border: `2px solid var(--modal-vpr-find-discrepancies-button-border-color, #2193b0)`, background: (discLoading || !discFileUkr || !discFileNp || !discFileOneC) ? 'rgba(33, 147, 176, 0.2)' : 'var(--modal-vpr-find-discrepancies-button-background, rgba(33, 147, 176, 0.25))', color: 'var(--modal-vpr-find-discrepancies-button-text-color, #2193b0)', fontWeight: 600, cursor: discLoading ? 'wait' : 'pointer', fontSize: '16px', width: mobile ? '100%' : undefined }}>
                <FiAlertCircle /> {discLoading ? 'Выполняется…' : 'Найти расхождения'}
              </button>
              {isAdmin && (
                <button type="button" onClick={openDiscTelegramSettings} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: mobile ? '14px 16px' : '10px 16px', borderRadius: '10px', border: '2px solid var(--modal-vpr-button-border-color, rgba(33, 147, 176, 0.5))', background: 'var(--modal-vpr-button-background, rgba(255,255,255,0.8))', color: 'var(--modal-vpr-button-text-color, #2193b0)', fontWeight: 600, cursor: 'pointer', fontSize: '14px', width: mobile ? '100%' : undefined }}>
                  <FiSettings /> Настройка Telegram
                </button>
              )}
              {discError && <span style={{ color: '#c53030', fontSize: '15px', ...(mobile ? { display: 'block', textAlign: 'center' } : {}) }}>{discError}</span>}
            </div>
            </>
          )}
          </div>
        </>
        )}

        {vprMainTab === 'manual' && (
        <div style={{
          padding: mobile ? '0 0 20px' : '20px 0',
          flex: '1 1 auto',
          minHeight: 0,
          ...(mobile ? { minWidth: 0, width: '100%' } : {}),
          display: 'flex',
          flexDirection: 'column',
          borderBottom: mobile ? 'none' : '1px solid rgba(33, 147, 176, 0.2)',
          color: 'var(--modal-vpr-text-color, var(--modal-calendar-text-color, #1a5f6e))',
        }}>
          {manualResult ? (
            /* Экран результатов ручного управления — как у расхождений */
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, ...(mobile ? { minWidth: 0, width: '100%' } : {}) }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '16px', flexShrink: 0, ...(mobile ? { flexDirection: 'column', alignItems: 'stretch' } : {}) }}>
                <button type="button" onClick={dismissManualRunResult} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: mobile ? '12px 14px' : '8px 14px', borderRadius: '8px', border: '2px solid var(--modal-vpr-discrepancies-button-border-color, rgba(33, 147, 176, 0.5))', background: 'var(--modal-vpr-discrepancies-button-background, rgba(255,255,255,0.9))', color: 'var(--modal-vpr-discrepancies-button-text-color, #2193b0)', fontWeight: 600, cursor: 'pointer', fontSize: mobile ? '15px' : '14px', ...(mobile ? { justifyContent: 'center' } : {}) }}>
                  <FiArrowLeft /> Назад
                </button>
                <h3 style={{ margin: 0, fontSize: mobile ? '1.1rem' : '1.25rem', fontWeight: 600, color: 'var(--modal-vpr-discrepancies-header-color, var(--modal-vpr-header-text-color, #2193b0))', ...(mobile ? { textAlign: 'center' } : {}) }}>
                  Результат
                  {manualDeepseekSummary?.aiOk && (
                    <span style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#4f46e5', marginTop: '4px' }}>
                      + вкладки DeepSeek (сравнение с движком)
                    </span>
                  )}
                </h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', ...(mobile ? { flexDirection: 'column', width: '100%' } : {}) }}>
                  <button type="button" onClick={() => { if (!manualResult?.blob) return; const a = document.createElement('a'); a.href = URL.createObjectURL(manualResult.blob); a.download = manualResult.fileName; a.click(); URL.revokeObjectURL(a.href); }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: mobile ? '12px 18px' : '10px 18px', borderRadius: '10px', border: '2px solid var(--modal-vpr-discrepancies-download-button-border-color, #2193b0)', background: 'var(--modal-vpr-discrepancies-download-button-background, rgba(33, 147, 176, 0.2))', color: 'var(--modal-vpr-discrepancies-download-button-text-color, #2193b0)', fontWeight: 600, cursor: 'pointer', fontSize: '15px', ...(mobile ? { width: '100%' } : {}) }}>
                    <FiDownload /> Скачать файл
                  </button>
                  <button type="button" onClick={async () => {
                    if (!manualResult?.blob) return;
                    const form = new FormData();
                    form.append('file', new File([manualResult.blob], manualResult.fileName || 'vpr_manual.xlsx', { type: manualResult.blob.type }));
                    try {
                      await api.post('/api/vpr-send-to-telegram', form, { headers: { 'Content-Type': undefined } });
                      setManualError('');
                      alert('Файл отправлен в Telegram.');
                    } catch (err) {
                      setManualError(err?.response?.data?.error || err?.message || 'Ошибка отправки.');
                    }
                  }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: mobile ? '12px 18px' : '10px 18px', borderRadius: '10px', border: '2px solid var(--modal-vpr-discrepancies-telegram-button-border-color, #2d7d4a)', background: 'var(--modal-vpr-discrepancies-telegram-button-background, rgba(45, 125, 74, 0.25))', color: 'var(--modal-vpr-discrepancies-telegram-button-text-color, #1a5f2e)', fontWeight: 600, cursor: 'pointer', fontSize: '15px', ...(mobile ? { width: '100%' } : {}) }}>
                    <FiSend /> Отправить в Telegram
                  </button>
                  <button type="button" onClick={() => {
                    if (!manualResult?.blob) return;
                    const file = new File([manualResult.blob], manualResult.fileName || 'vpr_manual.xlsx', { type: manualResult.blob.type });
                    window.dispatchEvent(new CustomEvent('vpr-open-create-task', { detail: { file } }));
                  }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: mobile ? '12px 18px' : '10px 18px', borderRadius: '10px', border: '2px solid var(--modal-vpr-discrepancies-assign-task-button-border-color, #7c3aed)', background: 'var(--modal-vpr-discrepancies-assign-task-button-background, rgba(124, 58, 237, 0.2))', color: 'var(--modal-vpr-discrepancies-assign-task-button-text-color, #5b21b6)', fontWeight: 600, cursor: 'pointer', fontSize: '15px', ...(mobile ? { width: '100%' } : {}) }}>
                    <FiCheckSquare /> Назначить задачу
                  </button>
                </div>
              </div>
              {manualDeepseekSummary && (
                <div style={{ marginBottom: '12px', padding: '10px 14px', borderRadius: '8px', border: '2px solid rgba(99,102,241,0.5)', background: 'rgba(238,242,255,0.9)', fontSize: '13px', lineHeight: 1.5, color: '#312e81', flexShrink: 0 }}>
                  <strong>DeepSeek vs движок:</strong> не звонить — движок {manualDeepseekSummary.noCall.engine}, ИИ {manualDeepseekSummary.noCall.ai}, совпали {manualDeepseekSummary.noCall.both}, только движок {manualDeepseekSummary.noCall.onlyEngine}, только ИИ {manualDeepseekSummary.noCall.onlyAi}.
                  {' '}Звонить — движок {manualDeepseekSummary.callOnly.engine}, ИИ {manualDeepseekSummary.callOnly.ai}, совпали {manualDeepseekSummary.callOnly.both}.
                  {manualDeepseekSummary.notes ? ` Заметки ИИ: ${manualDeepseekSummary.notes}` : ''}
                  {' '}Откройте вкладки <strong>«Сравнение ИИ/движок»</strong>, <strong>«ИИ — …»</strong> ниже.
                </div>
              )}
              {manualDataSlotIndices.length >= 2 && (
                <VprManualDeepseekPanel
                  compact
                  loadedIndices={manualDataSlotIndices}
                  manualFiles={manualFiles}
                  manualCrossFileMatch={manualCrossFileMatch}
                  manualFileConfigs={manualFileConfigs}
                  manualRenames={manualRenames}
                  manualMainFileIndex={manualMainFileIndex}
                  manualResult={manualResult}
                  deepseekResult={manualDeepseekResult}
                  onDeepseekResult={setManualDeepseekResult}
                  onAfterAnalysis={() => {
                    const base = manualResult?.sheets?.length || 0;
                    if (base) setManualResultTab(base);
                  }}
                  onError={setManualError}
                  onClearError={() => setManualError('')}
                />
              )}
              {!manualDeepseekResult && manualDataSlotIndices.length >= 2 && (
                <p style={{ margin: '0 0 12px', padding: '8px 12px', borderRadius: '8px', background: '#fef3c7', border: '1px solid #fcd34d', fontSize: '12px', color: '#92400e', flexShrink: 0 }}>
                  Данных ИИ пока нет. Нажмите «Назад», введите ключ DeepSeek и «Найти совпадения» — или «Обновить анализ ИИ» здесь (нужен сохранённый ключ).
                </p>
              )}
              <style>{`.VprModal-manual-tab { border-radius: 10px; border: 2px solid var(--modal-vpr-discrepancies-tab-border-color, rgba(33, 147, 176, 0.5)); }
.VprModal-manual-tab:hover { background: var(--modal-vpr-discrepancies-tab-hover-background, rgba(255,255,255,0.98)) !important; border-color: var(--modal-vpr-discrepancies-tab-hover-border-color, rgba(33, 147, 176, 0.9)) !important; color: var(--modal-vpr-discrepancies-tab-hover-text-color, #2193b0) !important; border-radius: 10px; box-shadow: 0 0 0 1px rgba(33, 147, 176, 0.2); }`}</style>
              {(manualAllResultSheets.length > 0) ? (
                <>
                  <div style={{ display: 'flex', gap: '4px', marginBottom: '12px', flexShrink: 0, flexWrap: 'wrap' }}>
                    {manualAllResultSheets.map((sh, si) => (
                      <button key={si} type="button" className="VprModal-manual-tab" onClick={() => setManualResultTab(si)} title={`${sh.name || 'Лист'}: ${getManualSheetRowCount(sh)} строк`} style={{ flex: '1 1 auto', minWidth: 100, padding: mobile ? '12px 10px' : '10px 16px', background: manualResultTab === si ? (sh._deepseek ? 'rgba(238,242,255,0.98)' : 'var(--modal-vpr-discrepancies-tab-active-background, rgba(255,255,255,0.95))') : (sh._deepseek ? 'rgba(238,242,255,0.65)' : 'var(--modal-vpr-discrepancies-tab-background, rgba(255,255,255,0.6))'), color: sh._deepseek ? '#4338ca' : 'var(--modal-vpr-discrepancies-tab-text-color, #1a5f6e)', fontWeight: 600, cursor: 'pointer', fontSize: mobile ? '13px' : '14px', border: sh._deepseek ? '2px solid rgba(99,102,241,0.55)' : undefined }}>
                        {formatManualSheetTabLabel(sh.name, getManualSheetRowCount(sh))}
                      </button>
                    ))}
                  </div>
                  {manualAllResultSheets[manualResultTab] && (() => {
                    const sh = manualAllResultSheets[manualResultTab];
                    const fc = detectManualResultFilterColumns(sh.headers || []);
                    const colHint = [
                      fc.nameIdx >= 0 ? `ФИО: ${sh.headers[fc.nameIdx]}` : null,
                      fc.phoneIdx >= 0 ? `тел.: ${sh.headers[fc.phoneIdx]}` : null,
                      fc.waybillIdx >= 0 ? `накладн.: ${sh.headers[fc.waybillIdx]}` : null,
                    ].filter(Boolean).join(' · ');
                    return (
                      <div style={{ marginBottom: '10px', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#f8fafc', flexShrink: 0 }}>
                        <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '8px', color: '#0f172a' }}>Поиск в таблице (превью)</div>
                        {colHint && <p style={{ margin: '0 0 8px', fontSize: '11px', color: '#64748b' }}>Столбцы: {colHint}</p>}
                        <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : 'repeat(3, 1fr)', gap: '8px', alignItems: 'end' }}>
                          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', fontWeight: 600 }}>
                            ФИО / имя
                            <input type="text" value={manualResultTextFilters.name} onChange={(e) => setManualResultTextFilters((f) => ({ ...f, name: e.target.value }))} placeholder="часть имени" style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }} />
                          </label>
                          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', fontWeight: 600 }}>
                            Телефон
                            <input type="text" value={manualResultTextFilters.phone} onChange={(e) => setManualResultTextFilters((f) => ({ ...f, phone: e.target.value }))} placeholder="цифры номера" style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }} />
                          </label>
                          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', fontWeight: 600 }}>
                            Накладная / номер
                            <input type="text" value={manualResultTextFilters.waybill} onChange={(e) => setManualResultTextFilters((f) => ({ ...f, waybill: e.target.value }))} placeholder="ТТН, внутр. номер" style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }} />
                          </label>
                        </div>
                        <button type="button" onClick={() => setManualResultTextFilters({ name: '', phone: '', waybill: '' })} style={{ marginTop: '8px', padding: '4px 12px', fontSize: '12px', borderRadius: '6px', border: '1px solid #94a3b8', background: '#fff', cursor: 'pointer' }}>Сбросить фильтры</button>
                      </div>
                    );
                  })()}
                  <div style={{ marginBottom: '10px', flexShrink: 0 }}>
                    <button
                      type="button"
                      onClick={() => setManualPreviewRulesOpen((o) => !o)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 14px',
                        borderRadius: '8px',
                        border: manualPreviewRulesOpen ? '2px solid #0ea5e9' : '2px solid #cbd5e1',
                        background: manualPreviewRulesOpen ? '#e0f2fe' : '#f8fafc',
                        color: '#0f172a',
                        fontWeight: 700,
                        fontSize: '14px',
                        cursor: 'pointer',
                      }}
                    >
                      Правила отображения в превью
                      {manualPreviewDisplayRules.length > 0 && (
                        <span style={{ fontSize: '12px', opacity: 0.85 }}>({manualPreviewDisplayRules.length})</span>
                      )}
                    </button>
                    {manualPreviewRulesOpen && (
                      <div style={{ marginTop: '10px', padding: '12px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', background: '#ffffff', color: '#0f172a', boxShadow: '0 1px 4px rgba(15,23,42,0.06)' }}>
                        <p style={{ margin: '0 0 8px', fontSize: '12px', lineHeight: 1.55, color: '#475569' }}>
                          Действуют только на таблицу превью во вкладках ниже. Кнопки <strong>«Скачать файл»</strong> и <strong>«Скачать этот лист»</strong> отдают полные данные без фильтра. Правила с одинаковой вкладкой объединяются через <strong>И</strong>.
                        </p>
                        <p style={{ margin: '0 0 12px', fontSize: '12px', lineHeight: 1.5, color: '#334155' }}>
                          <strong>Пример:</strong> скрыть мелкие расхождения (&lt; 1 грн) — столбец с разницей, сравнение <strong>≥</strong>, порог <strong>1</strong>, включить <strong>«По модулю |x|»</strong> (остаются строки с |разница| ≥ 1).
                        </p>
                        {manualPreviewDisplayRules.length > 0 && (
                          <ul style={{ margin: '0 0 12px', paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {manualPreviewDisplayRules.map((r) => (
                              <li key={r.id} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', fontSize: '13px', padding: '8px 10px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                <span style={{ fontWeight: 700 }}>
                                  {r.sheetTab === 'all' ? 'Все вкладки' : `Вкладка: ${(manualAllResultSheets[Number(r.sheetTab)] || {}).name ?? r.sheetTab}`}
                                </span>
                                <span style={{ color: '#64748b' }}>·</span>
                                <span>{r.columnMode === 'header_contains' ? `заголовок содержит «${(r.headerContains || '').slice(0, 40)}»` : `столбец №${(r.columnIndex ?? 0) + 1}`}</span>
                                <span style={{ color: '#64748b' }}>·</span>
                                <span>{(MANUAL_PREVIEW_COMPARE_OPS.find((o) => o.value === r.compareOp) || {}).label || r.compareOp} {r.threshold}{r.useAbs ? ' (|x|)' : ''}</span>
                                <button
                                  type="button"
                                  onClick={() => setManualPreviewDisplayRules((prev) => prev.filter((x) => x.id !== r.id))}
                                  style={{ marginLeft: 'auto', padding: '4px 10px', fontSize: '12px', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: '6px', background: '#fef2f2', cursor: 'pointer', fontWeight: 600 }}
                                >
                                  Удалить
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', paddingTop: '8px', borderTop: '1px dashed #e2e8f0' }}>
                          <div style={{ fontSize: '13px', fontWeight: 700 }}>Добавить правило</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                            <span style={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>Вкладка</span>
                            <select
                              value={manualPreviewRuleForm.sheetTab === 'all' ? 'all' : String(manualPreviewRuleForm.sheetTab)}
                              onChange={(e) => {
                                const v = e.target.value;
                                setManualPreviewRuleForm((f) => ({ ...f, sheetTab: v === 'all' ? 'all' : parseInt(v, 10) }));
                              }}
                              style={{ padding: '6px 10px', borderRadius: '6px', border: '2px solid #cbd5e1', fontSize: '13px' }}
                            >
                              <option value="all">Все вкладки</option>
                              {(manualAllResultSheets || []).map((sh, si) => (
                                <option key={si} value={String(si)}>{formatManualSheetTabLabel(sh.name || `Лист ${si + 1}`, getManualSheetRowCount(sh))}</option>
                              ))}
                            </select>
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer' }}>
                              <input type="radio" checked={manualPreviewRuleForm.columnMode === 'index'} onChange={() => setManualPreviewRuleForm((f) => ({ ...f, columnMode: 'index' }))} />
                              Столбец по номеру
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer' }}>
                              <input type="radio" checked={manualPreviewRuleForm.columnMode === 'header_contains'} onChange={() => setManualPreviewRuleForm((f) => ({ ...f, columnMode: 'header_contains' }))} />
                              Подстрока в заголовке
                            </label>
                          </div>
                          {manualPreviewRuleForm.columnMode === 'index' ? (
                            <select
                              value={manualPreviewRuleForm.columnIndex}
                              onChange={(e) => setManualPreviewRuleForm((f) => ({ ...f, columnIndex: parseInt(e.target.value, 10) }))}
                              style={{ padding: '6px 10px', borderRadius: '6px', border: '2px solid #cbd5e1', fontSize: '13px', maxWidth: '100%' }}
                            >
                              {((manualAllResultSheets[manualResultTab] || {}).headers || []).map((h, ci) => (
                                <option key={ci} value={ci}>{ci + 1}. {String(h).slice(0, 42) || `Кол.${ci + 1}`}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type="text"
                              placeholder="Часть названия столбца (без учёта регистра)"
                              value={manualPreviewRuleForm.headerContains}
                              onChange={(e) => setManualPreviewRuleForm((f) => ({ ...f, headerContains: e.target.value }))}
                              style={{ padding: '8px 10px', borderRadius: '6px', border: '2px solid #cbd5e1', fontSize: '13px' }}
                            />
                          )}
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                            <span style={{ fontSize: '12px', fontWeight: 600 }}>Число в ячейке</span>
                            <select
                              value={manualPreviewRuleForm.compareOp}
                              onChange={(e) => setManualPreviewRuleForm((f) => ({ ...f, compareOp: e.target.value }))}
                              style={{ padding: '6px 10px', borderRadius: '6px', border: '2px solid #cbd5e1', fontSize: '13px' }}
                            >
                              {MANUAL_PREVIEW_COMPARE_OPS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                            <input
                              type="text"
                              placeholder="Порог (например 1 или 1,5)"
                              value={manualPreviewRuleForm.threshold}
                              onChange={(e) => setManualPreviewRuleForm((f) => ({ ...f, threshold: e.target.value }))}
                              style={{ padding: '6px 10px', borderRadius: '6px', border: '2px solid #cbd5e1', fontSize: '13px', width: 120 }}
                            />
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer' }}>
                              <input type="checkbox" checked={manualPreviewRuleForm.useAbs} onChange={(e) => setManualPreviewRuleForm((f) => ({ ...f, useAbs: e.target.checked }))} />
                              По модулю |x|
                            </label>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              if (manualPreviewRuleForm.columnMode === 'header_contains' && !(manualPreviewRuleForm.headerContains || '').trim()) return;
                              setManualPreviewDisplayRules((prev) => [...prev, {
                                id: vprManualGenId(),
                                sheetTab: manualPreviewRuleForm.sheetTab,
                                columnMode: manualPreviewRuleForm.columnMode,
                                columnIndex: manualPreviewRuleForm.columnIndex,
                                headerContains: (manualPreviewRuleForm.headerContains || '').trim(),
                                compareOp: manualPreviewRuleForm.compareOp,
                                threshold: manualPreviewRuleForm.threshold,
                                useAbs: manualPreviewRuleForm.useAbs,
                              }]);
                            }}
                            style={{ alignSelf: 'flex-start', padding: '8px 16px', borderRadius: '8px', border: '2px solid rgba(33, 147, 176, 0.45)', background: '#fff', fontWeight: 700, fontSize: '13px', cursor: 'pointer', color: '#0f172a' }}
                          >
                            Добавить правило
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, minWidth: 0, height: mobile ? undefined : '50vh', minHeight: 200, maxHeight: mobile ? 'none' : '50vh' }}>
                  <div
                    ref={manualScrollContentRef}
                    style={{ flex: 1, minHeight: 0, minWidth: 0, overflowX: 'scroll', overflowY: 'scroll', overflow: 'auto', background: 'var(--modal-vpr-discrepancies-results-background, rgba(255,255,255,0.7))', borderRadius: '10px 10px 0 0', border: '2px solid var(--modal-vpr-discrepancies-results-border-color, rgba(33, 147, 176, 0.35))', borderBottomWidth: 0, padding: '12px', WebkitOverflowScrolling: 'touch', userSelect: 'text', WebkitUserSelect: 'text' }}
                    onScroll={() => {
                      if (manualScrollSyncRef.current) return;
                      if (manualHScrollRef.current && manualScrollContentRef.current) {
                        manualScrollSyncRef.current = true;
                        manualHScrollRef.current.scrollLeft = manualScrollContentRef.current.scrollLeft;
                        requestAnimationFrame(() => { manualScrollSyncRef.current = false; });
                      }
                    }}
                  >
                    {manualAllResultSheets[manualResultTab] && (() => {
                      const sh = manualAllResultSheets[manualResultTab];
                      const pr = manualPreviewFiltered;
                      const displayRows = pr ? pr.rows : (sh.rows || []);
                      const srcTotal = pr ? pr.sourceTotal : (sh.rows || []).length;
                      const filtered = !!(pr && pr.filtered);
                      const maxPreview = 50;
                      const dateBreakdown = summarizeRowsByDateColumn(displayRows, sh.headers || []);
                      const sliceRows = sampleRowsForMergedPreview(displayRows, sh.headers || [], maxPreview);
                      return (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexShrink: 0 }}>
                          <span style={{ fontWeight: 600, color: 'var(--modal-vpr-discrepancies-header-color, #2193b0)' }}>
                            {sh.name}
                            <span style={{ marginLeft: '8px', fontWeight: 700, fontSize: '13px', padding: '2px 10px', borderRadius: '8px', background: 'rgba(33, 147, 176, 0.15)', color: '#0f172a' }}>
                              {filtered ? `${displayRows.length} из ${srcTotal} строк` : `${srcTotal} строк`}
                            </span>
                          </span>
                          <button type="button" onClick={() => {
                            if (!sh) return;
                            const wb = XLSX.utils.book_new();
                            const ws = XLSX.utils.aoa_to_sheet([sh.headers, ...sh.rows]);
                            XLSX.utils.book_append_sheet(wb, ws, sh.name);
                            const wbout = XLSX.write(wb, { type: 'binary', bookType: 'xlsx' });
                            const buf = new ArrayBuffer(wbout.length);
                            const view = new Uint8Array(buf);
                            for (let i = 0; i < wbout.length; i++) view[i] = wbout.charCodeAt(i) & 0xFF;
                            const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })); a.download = (sh.name || 'лист') + '.xlsx'; a.click(); URL.revokeObjectURL(a.href);
                          }} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '8px', border: '2px solid var(--modal-vpr-discrepancies-download-button-border-color, #2193b0)', background: 'var(--modal-vpr-discrepancies-download-button-background, rgba(33, 147, 176, 0.2))', color: 'var(--modal-vpr-discrepancies-download-button-text-color, #2193b0)', fontWeight: 600, cursor: 'pointer', fontSize: '13px' }}>
                            <FiDownload /> Скачать этот файл
                          </button>
                        </div>
                        {filtered && (
                          <p style={{ margin: '0 0 8px', fontSize: '12px', color: '#0f172a', padding: '8px 10px', borderRadius: '8px', background: '#e0f2fe', border: '1px solid #7dd3fc' }}>
                            Фильтр превью: отображается <strong>{displayRows.length}</strong> из <strong>{srcTotal}</strong> строк на этой вкладке. В скачанном Excel — все строки.
                          </p>
                        )}
                        {!filtered && dateBreakdown.length > 1 && (
                          <p style={{ margin: '0 0 8px', fontSize: '12px', color: '#0f172a', padding: '8px 10px', borderRadius: '8px', background: '#ecfdf5', border: '1px solid #86efac' }}>
                            В файле несколько дат: <strong>{dateBreakdown.map((d) => `${d.date} (${d.count})`).join(', ')}</strong>.
                            {' '}В таблице ниже — по несколько строк с каждой даты. Полный список — в скачанном файле или прокрутите вниз.
                          </p>
                        )}
                        <div style={{ overflowX: 'auto', overflowY: 'auto', overflow: 'auto', userSelect: 'text', WebkitUserSelect: 'text' }}>
                          <table className="VprModal-disc-table" style={{ width: '100%', minWidth: 400, borderCollapse: 'collapse', fontSize: '13px', color: 'var(--modal-vpr-discrepancies-results-text-color, #1a5f6e)', userSelect: 'text' }}>
                            <thead>
                              <tr>
                                {(sh.headers || []).map((h, i) => (
                                  <th key={i} style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid var(--modal-vpr-discrepancies-results-border-color, rgba(33, 147, 176, 0.4))', fontWeight: 600, userSelect: 'text', whiteSpace: 'nowrap' }}>{h || `Кол.${i + 1}`}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {sliceRows.length === 0 ? (
                                <tr>
                                  <td colSpan={Math.max(1, (sh.headers || []).length)} style={{ padding: '16px 12px', opacity: 0.85, userSelect: 'text' }}>
                                    {filtered ? 'Нет строк, подходящих под правила превью на этой вкладке.' : 'Нет строк данных.'}
                                  </td>
                                </tr>
                              ) : (
                                sliceRows.map((row, ri) => (
                                  <tr key={ri} style={{ borderBottom: '1px solid var(--modal-vpr-discrepancies-results-border-color, rgba(33, 147, 176, 0.2))' }}>
                                    {(sh.headers || []).map((h, ci) => {
                                      const hTrim = String(h || '').trim();
                                      const isFilesCol = hTrim.startsWith('Есть в файлах');
                                      const cellVal = row[ci] ?? '';
                                      return (
                                      <td
                                        key={ci}
                                        title={String(cellVal)}
                                        style={{
                                          padding: '8px 12px',
                                          userSelect: 'text',
                                          WebkitUserSelect: 'text',
                                          ...(isFilesCol ? { whiteSpace: 'nowrap', minWidth: 140 } : { whiteSpace: 'nowrap' }),
                                        }}
                                      >
                                        {cellVal}
                                      </td>
                                      );
                                    })}
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                          {displayRows.length > maxPreview && (
                            <p style={{ margin: '8px 12px', fontSize: '12px', opacity: 0.8 }}>
                              Показано первых {maxPreview} из {displayRows.length} строк{filtered ? ' (после фильтра)' : ''}. Полные данные — в скачанном файле.
                            </p>
                          )}
                        </div>
                      </>
                      );
                    })()}
                  </div>
                  {!mobile && manualContentScrollWidth > 0 && (
                    <div
                      ref={manualHScrollRef}
                      style={{ flexShrink: 0, height: 14, overflowX: 'auto', overflowY: 'hidden', background: 'var(--modal-vpr-discrepancies-results-background, rgba(255,255,255,0.8))', border: '2px solid var(--modal-vpr-discrepancies-results-border-color, rgba(33, 147, 176, 0.35))', borderTop: 'none', borderRadius: '0 0 10px 10px' }}
                      onScroll={() => {
                        if (manualScrollSyncRef.current) return;
                        if (manualScrollContentRef.current && manualHScrollRef.current) {
                          manualScrollSyncRef.current = true;
                          manualScrollContentRef.current.scrollLeft = manualHScrollRef.current.scrollLeft;
                          requestAnimationFrame(() => { manualScrollSyncRef.current = false; });
                        }
                      }}
                    >
                      <div style={{ height: 1, width: Math.max(manualContentScrollWidth, 1), minWidth: '100%' }} />
                    </div>
                  )}
                </div>
                </>
              ) : (
                <p style={{ margin: 0, padding: '24px', fontSize: '14px', opacity: 0.9 }}>Файл сформирован. Скачайте по кнопке выше.</p>
              )}
            </div>
          ) : (
        <>
          <div style={{ fontSize: mobile ? '15px' : '16px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--modal-vpr-header-text-color, #2193b0)', marginBottom: '8px' }}>
            <FiSettings /> Ручное управление
          </div>
          <p style={{ margin: 0, fontSize: '14px', opacity: 0.9, marginBottom: '12px' }}>
            Пошаговые инструкции — разверните нужную, прочитайте и сверните обратно.
          </p>
          <VprManualGuidePanel
            title="Инструкция: 2–4 одностраничных файла (без вкладок Excel)"
            open={manualGuideSingleOpen}
            onToggle={() => setManualGuideSingleOpen((v) => !v)}
          >
            <p style={{ margin: '0 0 10px' }}>
              Каждый файл — <strong>один лист</strong> (Sheet1). Сравнение идёт по <strong>телефону</strong> (и при необходимости по ФИО) между всеми загруженными слотами.
            </p>
            <ol style={{ margin: 0, paddingLeft: '20px' }}>
              <li style={{ marginBottom: '6px' }}>
                Загрузите файлы в <strong>слоты 1–4</strong> (сколько нужно: 2, 3 или 4). В карточке у каждого — одна вкладка Excel.
              </li>
              <li style={{ marginBottom: '6px' }}>
                <strong>Не используйте</strong> «Разнести вкладки» — это только для книг с 2+ листами. «Разбить по дате» — только если в <em>одном</em> файле разные дни в строках одного листа.
              </li>
              <li style={{ marginBottom: '6px' }}>
                Когда загружено <strong>2 и больше</strong> файлов, в зелёном блоке включите <strong>«Совпадения по телефону / ФИО во всех файлах»</strong>.
              </li>
              <li style={{ marginBottom: '6px' }}>
                <strong>Ключ сравнения:</strong> «Только телефон», «Телефон + ФИО» или <strong>«Телефон + ФИО + НД»</strong> (НД в одном файле → тот же клиент во всех вкладках → перезвон).
              </li>
              <li style={{ marginBottom: '6px' }}>
                Для <strong>каждого</strong> файла в зелёном блоке укажите <strong>столбец телефона</strong> (и ФИО, если выбран режим с ФИО). Галочка <strong>«Участвует в совпадениях»</strong> — снимите с лишних слотов, если файл только для справки.
              </li>
              <li style={{ marginBottom: '6px' }}>
                <strong>«Кому не звонить», если номер есть в:</strong> при <strong>2 файлах</strong> «Авто» = номер должен быть <strong>в обоих</strong>; при <strong>3–4 файлах</strong> «Авто» = минимум в <strong>2 выгрузках</strong> (или выберите «Во всех» / «Минимум в N» вручную).
              </li>
              <li style={{ marginBottom: '6px' }}>
                В карточках файлов действие можно оставить «Расхождения» или выбрать <strong>«Совпадения по телефону (все файлы)»</strong> — главное, что галочка в зелёном блоке включена.
              </li>
              <li style={{ marginBottom: '6px' }}>
                Нажмите <strong>«Запустить»</strong> внизу экрана.
              </li>
              <li style={{ marginBottom: '6px' }}>
                Опционально: в блоке <strong>«ИИ-ассистент DeepSeek»</strong> введите API-ключ и нажмите <strong>«Найти совпадения (ИИ + движок)»</strong> — сравнение с результатом «Запустить» на вкладке «Сравнение».
              </li>
              <li>
                В результате: <strong>«Кому не звонить»</strong> — номер уже есть в нескольких выгрузках; <strong>«Кому звонить»</strong> — только в одной; <strong>«Совпадения подробно»</strong> — по каждому номеру, в каких файлах найден.
              </li>
            </ol>
          </VprManualGuidePanel>
          <VprManualGuidePanel
            title="Инструкция: неделя в одном листе + файл с вкладками Excel"
            open={manualGuideTabsOpen}
            onToggle={() => setManualGuideTabsOpen((v) => !v)}
          >
            <ol style={{ margin: 0, paddingLeft: '20px' }}>
              <li style={{ marginBottom: '6px' }}>
                <strong>Слот 1</strong> — загрузите «список посылок за неделю» (один лист, в строках разные даты). В зелёном блоке для этого файла: столбцы <strong>телефона</strong>, <strong>ФИО</strong>, <strong>даты</strong> → включите <strong>«Разбить по дате»</strong> (каждый день станет отдельным источником сравнения).
              </li>
              <li style={{ marginBottom: '6px' }}>
                <strong>По очереди:</strong> первый .xlsx в слот 1 → «Разнести» (слот 1 = книга-мастер, вкладки в 2–4) → второй файл в свободный слот → снова «Разнести» → «Запустить». Для 2×3 вкладки — <strong>8 слотов</strong>.
              </li>
              <li style={{ marginBottom: '6px' }}>
                Для всех участвующих слотов укажите столбцы <strong>телефона</strong> и при необходимости <strong>ФИО</strong>. Включите <strong>«Совпадения по телефону / ФИО»</strong>.
              </li>
              <li style={{ marginBottom: '6px' }}>
                Нажмите <strong>«Запустить»</strong>.
              </li>
              <li>
                В отчёте откройте <strong>«Совпадения подробно»</strong> — для каждого телефона/ФИО видно, в скольких местах найден человек (например: 13.05 в списке посылок и 14.05 на вкладке …).
              </li>
            </ol>
          </VprManualGuidePanel>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
            <button type="button" onClick={() => setManualUiSubTab('builder')} style={{ padding: '8px 14px', borderRadius: '8px', border: `2px solid ${manualUiSubTab === 'builder' ? 'var(--modal-vpr-header-text-color, #2193b0)' : 'rgba(33,147,176,0.4)'}`, background: manualUiSubTab === 'builder' ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.5)', fontWeight: 600, cursor: 'pointer', fontSize: '14px', color: 'var(--modal-vpr-text-color, #1a5f6e)' }}>
              Конструктор и очередь
            </button>
            <button type="button" onClick={() => setManualUiSubTab('presets')} style={{ padding: '8px 14px', borderRadius: '8px', border: `2px solid ${manualUiSubTab === 'presets' ? 'var(--modal-vpr-header-text-color, #2193b0)' : 'rgba(33,147,176,0.4)'}`, background: manualUiSubTab === 'presets' ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.5)', fontWeight: 600, cursor: 'pointer', fontSize: '14px', color: 'var(--modal-vpr-text-color, #1a5f6e)' }}>
              Пресеты
            </button>
          </div>
          {manualUiSubTab === 'builder' && (
            <div style={{ marginBottom: '16px', padding: '16px', borderRadius: '14px', border: '1px solid rgba(33, 147, 176, 0.45)', background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.96) 100%)', boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)', color: 'var(--modal-vpr-text-color, #0f172a)' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px', marginBottom: '14px', padding: '10px 12px', borderRadius: '8px', background: 'rgba(240, 249, 255, 0.9)', border: '1px solid rgba(33, 147, 176, 0.35)' }}>
                <label style={{ fontSize: '14px', fontWeight: 600, color: '#334155' }}>
                  Количество слотов для файлов:
                  <select
                    value={String(manualSlotCount)}
                    onChange={(e) => applyManualSlotCount(Number(e.target.value))}
                    style={{ marginLeft: '8px', padding: '6px 10px', borderRadius: '6px', fontSize: '14px' }}
                  >
                    {MANUAL_SLOT_COUNT_OPTIONS.map((n) => (
                      <option key={n} value={String(n)}>{n}</option>
                    ))}
                  </select>
                </label>
                <span style={{ fontSize: '12px', color: '#64748b', lineHeight: 1.45, maxWidth: 520 }}>
                  Книги с вкладками разносятся <strong>по одной</strong>. После первого файла загрузите второй в пустой слот.
                </span>
              </div>
              <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '8px', color: vprFileLabelColor }}>Главный / ведущий файл (расхождения и режим «Ведущий файл + тел + ФИО»)</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '14px' }}>
                {manualSlotList.map((i) => (
                  <label key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: manualFiles[i] ? 'pointer' : 'not-allowed', opacity: manualFiles[i] ? 1 : 0.45, fontSize: '14px' }}>
                    <input type="radio" name="vpr-manual-main" checked={manualMainFileIndex === i} disabled={!manualFiles[i]} onChange={() => setManualMainFileIndex(i)} />
                    Файл {i + 1}
                  </label>
                ))}
              </div>
              {(() => {
                const loadedSlotIndices = manualDataSlotIndices;
                const loadedCount = loadedSlotIndices.length;
                const hasMultiSheetBook = loadedSlotIndices.some(
                  (i) => getManualEntrySheetCount(manualFiles[i]) >= 2,
                );
                if (!hasMultiSheetBook && loadedCount < 2) return null;
                return (
                <div style={{ marginBottom: '14px', padding: '12px 14px', borderRadius: '10px', border: '2px solid rgba(22, 163, 74, 0.45)', background: 'rgba(240, 253, 244, 0.85)' }}>
                  {hasMultiSheetBook && (
                  <div style={{ marginBottom: loadedCount >= 2 ? '12px' : 0, padding: '10px 12px', borderRadius: '8px', border: '1px solid rgba(22, 163, 74, 0.5)', background: 'rgba(255,255,255,0.92)' }}>
                        <div style={{ fontSize: '13px', fontWeight: 700, color: '#166534', marginBottom: '8px' }}>Разнести вкладки Excel по слотам</div>
                        <p style={{ margin: '0 0 8px', fontSize: '12px', color: '#475569', lineHeight: 1.45 }}>
                          <strong>Шаг 1:</strong> загрузите первый .xlsx → выберите в списке → «Разнести этот файл» (состояние сохранится).
                          <strong> Шаг 2:</strong> загрузите второй файл в <strong>свободный слот</strong> → выберите его → снова «Разнести».
                          <strong> Шаг 3:</strong> «Запустить». Слот с исходным файлом — только книга-мастер, вкладки не дублируются.
                        </p>
                        {manualDistributedRanges.length > 0 && (
                          <p style={{ margin: '0 0 8px', padding: '8px 10px', borderRadius: '6px', background: '#ecfdf5', border: '1px solid #86efac', fontSize: '12px', color: '#166534', lineHeight: 1.45 }}>
                            Разнесено: {manualDistributedRanges.map((r) => (
                              `файл ${r.sourceSlot + 1}: мастер слот ${r.sourceSlot + 1}, вкладки в слотах ${r.startSlot + 1}–${r.startSlot + r.filled}`
                            )).join('; ')}.
                            {' '}Следующий файл: слот {getNextDistributeStartSlot(manualFiles, manualSlotCount, manualDistributedRanges) + 1}.
                          </p>
                        )}
                        {findManualDuplicateWorkbookSlots(manualFiles, manualSlotCount).length > 0 && (
                          <p style={{ margin: '0 0 8px', padding: '8px 10px', borderRadius: '6px', background: '#fef2f2', border: '1px solid #fecaca', fontSize: '12px', color: '#b91c1c', lineHeight: 1.45 }}>
                            Книга с вкладками загружена в несколько слотов — оставьте в одном (слот 2), остальные очистите, иначе появится дубликат вкладки в слоте 4.
                          </p>
                        )}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '8px' }}>
                          {(() => {
                            const seenDup = new Set();
                            return manualSlotList.map((i) => {
                            const entry = manualFiles[i];
                            if (!entry) return null;
                            const sheetN = getManualEntrySheetCount(entry);
                            const fname = fixVprDisplayFileName(entry.fileName || '').slice(0, 42) || `Файл ${i + 1}`;
                            const fileKey = String(entry.fileName || '').trim().toLowerCase();
                            const canDistributeSheets = sheetN >= 2;
                            const alreadySpread = manualDistributedRanges.some((r) => Number(r.sourceSlot) === i);
                            if (canDistributeSheets && fileKey && seenDup.has(fileKey)) {
                              return (
                                <div key={`dup-${i}`} style={{ fontSize: '12px', color: '#94a3b8', paddingLeft: '22px' }}>
                                  <strong>Файл {i + 1}:</strong> {fname} — та же книга уже в другом слоте
                                </div>
                              );
                            }
                            if (canDistributeSheets && fileKey) seenDup.add(fileKey);
                            return (
                              <label
                                key={i}
                                style={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: '8px',
                                  fontSize: '13px',
                                  cursor: canDistributeSheets ? 'pointer' : 'not-allowed',
                                  opacity: canDistributeSheets ? 1 : 0.65,
                                }}
                              >
                                <input
                                  type="radio"
                                  name="vpr-manual-distribute-source"
                                  disabled={!canDistributeSheets}
                                  checked={manualDistributeSourceSlot === i}
                                  onChange={() => {
                                    setManualDistributeSourceSlot(i);
                                    const autoStart = getNextDistributeStartSlot(
                                      manualFiles,
                                      manualSlotCount,
                                      manualDistributedRanges,
                                    );
                                    setManualDistributeStartSlot(
                                      manualSingleWorkbookMode ? Math.max(autoStart, 1) : autoStart,
                                    );
                                  }}
                                  style={{ marginTop: '3px' }}
                                />
                                <span>
                                  <strong>Файл {i + 1}:</strong> {fname}
                                  <span style={{ display: 'block', fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                                    {sheetN >= 2
                                      ? `${sheetN} вкладок${alreadySpread ? ' — уже разнесён, можно разнести заново' : ''}`
                                      : `1 вкладка — «Разбить по дате»`}
                                  </span>
                                </span>
                              </label>
                            );
                          });
                          })()}
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', marginBottom: '8px' }}>
                          <p style={{ margin: 0, fontSize: '12px', color: '#64748b', lineHeight: 1.45, flex: '1 1 100%' }}>
                            Вкладки пойдут в слоты начиная с <strong>{manualDistributeStartSlot + 1}</strong>
                            {manualDistributeSourceSlot != null ? ` (после книги-мастера в слоте ${manualDistributeSourceSlot + 1})` : ''}.
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            if (manualDistributeSourceSlot == null) {
                              setManualError('Выберите один файл с 2+ вкладками (переключатель в списке).');
                              return;
                            }
                            const res = distributeOneManualWorkbookAppend(
                              manualFiles,
                              manualDistributeSourceSlot,
                              Math.max(manualDistributeStartSlot, manualDistributeSourceSlot + 1),
                              manualSlotCount,
                              manualDistributedRanges,
                            );
                            if (!res.ok) {
                              setManualError(res.error);
                              return;
                            }
                            dismissManualRunResult();
                            setManualError(res.hint || '');
                            setManualFiles(res.files);
                            setManualSelectedCols(res.selectedCols);
                            setManualDistributeSourceSlot(null);
                            setManualSingleWorkbookMode(true);
                            setManualDistributeStartSlot(
                              res.nextStartSlot ?? res.range?.startSlot ?? manualDistributeStartSlot,
                            );
                            setManualDistributedRanges((prev) => {
                              const without = prev.filter((r) => Number(r.sourceSlot) !== res.sourceSlot);
                              const nextRanges = res.range ? [...without, res.range] : without;
                              saveManualSpreadState(nextRanges, manualSlotCount);
                              return nextRanges;
                            });
                            setManualCrossFileMatch((p) => ({
                              ...p,
                              enabled: true,
                              includeInFullMatchBySlot: p.includeInFullMatchBySlot.map((v, i) => (
                                i === res.sourceSlot ? false : v
                              )),
                              exportDateBySlot: p.exportDateBySlot.map((v, i) => (res.exportDateBySlot[i] ? res.exportDateBySlot[i] : v)),
                              phoneBySlot: p.phoneBySlot.map((v, i) => (res.phoneBySlot[i]?.col !== '' && res.phoneBySlot[i]?.col != null ? res.phoneBySlot[i] : v)),
                              fioBySlot: p.fioBySlot.map((v, i) => (
                                res.fioBySlot[i]?.col !== '' && res.fioBySlot[i]?.col != null ? res.fioBySlot[i] : v
                              )),
                              dateBySlot: p.dateBySlot.map((v, i) => (res.dateBySlot[i]?.col !== '' && res.dateBySlot[i]?.col != null ? res.dateBySlot[i] : v)),
                              ndBySlot: (p.ndBySlot || []).map((v, i) => (res.ndBySlot?.[i]?.col !== '' && res.ndBySlot?.[i]?.col != null ? res.ndBySlot[i] : v)),
                            }));
                          }}
                          style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '2px solid #16a34a', background: 'rgba(22, 163, 74, 0.15)', color: '#15803d', fontWeight: 700, cursor: 'pointer', fontSize: '13px' }}
                        >
                          Разнести этот файл по слотам
                        </button>
                  </div>
                  )}
                  {loadedCount >= 2 ? (
                  <>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 700, color: '#166534', marginBottom: '8px', marginTop: hasMultiSheetBook ? '12px' : 0 }}>
                    <input
                      type="checkbox"
                      checked={manualCrossFileMatch.enabled}
                      onChange={(e) => setManualCrossFileMatch((prev) => ({ ...prev, enabled: e.target.checked }))}
                    />
                    Совпадения по телефону / ФИО во всех файлах
                  </label>
                  <p style={{ margin: '0 0 10px', fontSize: '13px', lineHeight: 1.55, color: '#334155' }}>
                    {normalizeVprCrossMatchMode(manualCrossFileMatch.matchMode) === 'phone_nd' ? (
                      <>
                        <strong>«Перезвонить (НД)»</strong> — в одном файле строка с <strong>телефон + ФИО + «нд»</strong>; тот же телефон и ФИО ищется во всех других вкладках (там «нд» может не быть).
                        <strong> «Не звонить»</strong> — в другой выгрузке уже «дозвонились».
                      </>
                    ) : normalizeVprCrossMatchMode(manualCrossFileMatch.matchMode) === 'leading_phone_fio' ? (
                      <>
                        Из <strong>главного (ведущего) файла</strong> выше берутся все <strong>телефон + ФИО</strong> и ищутся в остальных выгрузках.
                        <strong> «Совпадения с ведущим»</strong> — тот же человек есть и в другом файле. <strong>«Только в ведущем»</strong> — в других не найден.
                      </>
                    ) : (
                      <>
                        <strong>«Кому не звонить»</strong> — номер есть минимум в 2 отмеченных выгрузках. <strong>«Кому звонить»</strong> — только в одной.
                      </>
                    )}
                    {' '}Для одного листа с датами в строках — <strong>«Разбить по дате»</strong>.
                  </p>
                  {manualCrossFileMatch.enabled && (
                    <>
                      <div style={{ marginBottom: '10px', display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>Ключ сравнения:</span>
                        <label style={{ fontSize: '13px', cursor: 'pointer' }}>
                          <input type="radio" name="vpr-cross-match-mode" checked={manualCrossFileMatch.matchMode === 'phone'} onChange={() => {
                            dismissManualRunResult();
                            setManualCrossFileMatch((prev) => ({
                              ...prev,
                              matchMode: 'phone',
                              fioBySlot: prev.fioBySlot.map(() => ({ col: '', part: '' })),
                              ndBySlot: (prev.ndBySlot || []).map(() => createEmptyManualCrossNdSlot()),
                            }));
                          }} />
                          {' '}Только телефон
                        </label>
                        <label style={{ fontSize: '13px', cursor: 'pointer' }}>
                          <input type="radio" name="vpr-cross-match-mode" checked={manualCrossFileMatch.matchMode === 'phone_fio'} onChange={() => {
                            dismissManualRunResult();
                            setManualCrossFileMatch((prev) => ({
                              ...prev,
                              matchMode: 'phone_fio',
                              ndBySlot: (prev.ndBySlot || []).map(() => createEmptyManualCrossNdSlot()),
                            }));
                          }} />
                          {' '}Телефон + ФИО
                        </label>
                        <label style={{ fontSize: '13px', cursor: 'pointer' }}>
                          <input type="radio" name="vpr-cross-match-mode" checked={manualCrossFileMatch.matchMode === 'phone_nd'} onChange={() => {
                            dismissManualRunResult();
                            setManualCrossFileMatch((prev) => {
                              const ndBySlot = [...(prev.ndBySlot || [])];
                              const fioBySlot = [...prev.fioBySlot];
                              manualDataSlotIndices.forEach((idx) => {
                                const hdrs = manualFiles[idx]?.headers || [];
                                const ndCol = detectManualNdCol(hdrs);
                                const fioCol = detectManualCrossCol(hdrs, 'fio');
                                ndBySlot[idx] = { col: ndCol >= 0 ? ndCol : '' };
                                fioBySlot[idx] = { col: fioCol >= 0 ? fioCol : '', part: '' };
                              });
                              return { ...prev, matchMode: 'phone_nd', ndBySlot, fioBySlot };
                            });
                          }} />
                          {' '}Телефон + ФИО + НД
                        </label>
                        <label style={{ fontSize: '13px', cursor: 'pointer' }}>
                          <input type="radio" name="vpr-cross-match-mode" checked={manualCrossFileMatch.matchMode === 'leading_phone_fio'} onChange={() => {
                            dismissManualRunResult();
                            setManualCrossFileMatch((prev) => {
                              const ndBySlot = [...(prev.ndBySlot || [])];
                              const fioBySlot = [...prev.fioBySlot];
                              manualDataSlotIndices.forEach((idx) => {
                                const hdrs = manualFiles[idx]?.headers || [];
                                const ndCol = detectManualNdCol(hdrs);
                                const fioCol = detectManualCrossCol(hdrs, 'fio');
                                ndBySlot[idx] = { col: ndCol >= 0 ? ndCol : '' };
                                fioBySlot[idx] = { col: fioCol >= 0 ? fioCol : '', part: '' };
                              });
                              return { ...prev, matchMode: 'leading_phone_fio', ndBySlot, fioBySlot };
                            });
                          }} />
                          {' '}Ведущий файл + тел + ФИО
                        </label>
                      </div>
                      {normalizeVprCrossMatchMode(manualCrossFileMatch.matchMode) === 'leading_phone_fio' && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', fontSize: '13px', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={!!manualCrossFileMatch.leadingOnlyNd}
                            onChange={(e) => setManualCrossFileMatch((prev) => ({ ...prev, leadingOnlyNd: e.target.checked }))}
                          />
                          В ведущем файле брать только строки с НД (нд, недозвон)
                        </label>
                      )}
                      {normalizeVprCrossMatchMode(manualCrossFileMatch.matchMode) !== 'phone_nd'
                        && normalizeVprCrossMatchMode(manualCrossFileMatch.matchMode) !== 'leading_phone_fio' && (
                      <div style={{ marginBottom: '10px' }}>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#334155', marginRight: '8px' }}>«Кому не звонить», если номер есть в:</span>
                        <select
                          value={manualCrossFileMatch.noCallRule || 'auto'}
                          onChange={(e) => setManualCrossFileMatch((prev) => ({ ...prev, noCallRule: e.target.value }))}
                          style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid rgba(33,147,176,0.5)', fontSize: '13px' }}
                        >
                          <option value="auto">Авто (2+ при 3+ файлах, все при 2 файлах)</option>
                          <option value="all">Во всех отмеченных файлах</option>
                          <option value="2">Минимум в 2 файлах</option>
                          <option value="3">Минимум в 3 файлах</option>
                          <option value="4">Минимум в 4 файлах</option>
                          {manualSlotCount > 4 && [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20]
                            .filter((n) => n <= manualSlotCount)
                            .map((n) => (
                              <option key={n} value={String(n)}>Минимум в {n} файлах</option>
                            ))}
                        </select>
                      </div>
                      )}
                      <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : 'repeat(2, 1fr)', gap: '10px' }}>
                        {manualDataSlotIndices.map((idx) => {
                          const modeNorm = normalizeVprCrossMatchMode(manualCrossFileMatch.matchMode);
                          const isNdMode = modeNorm === 'phone_nd';
                          const isLeadingMode = modeNorm === 'leading_phone_fio';
                          const showNdCol = isNdMode || (isLeadingMode && manualCrossFileMatch.leadingOnlyNd);
                          const phoneSlot = manualCrossFileMatch.phoneBySlot[idx] || { col: 0, part: '' };
                          const fioSlot = manualCrossFileMatch.fioBySlot[idx] || { col: '', part: '' };
                          const ndSlot = manualCrossFileMatch.ndBySlot?.[idx] || { col: '' };
                          const dateSlot = manualCrossFileMatch.dateBySlot[idx] || { col: '', part: '' };
                          const colOpts = buildManualCrossFileColSelectOptions(idx, manualFiles, manualDeletedCols, manualRenames, manualFileConfigs, manualAddedColumns);
                          const phoneVal = manualCrossColSelectValue(phoneSlot.col ?? 0, phoneSlot.part);
                          const fioVal = fioSlot.col === '' || fioSlot.col == null ? '' : manualCrossColSelectValue(fioSlot.col, fioSlot.part);
                          const dateVal = dateSlot.col === '' || dateSlot.col == null ? '' : String(dateSlot.col);
                          return (
                            <div key={idx} style={{ padding: '8px', borderRadius: '8px', background: 'rgba(255,255,255,0.9)', border: '1px solid rgba(33,147,176,0.35)' }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', fontSize: '13px', fontWeight: 700, color: '#166534', cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={manualCrossFileMatch.includeInFullMatchBySlot[idx] !== false}
                                  onChange={(e) => setManualCrossFileMatch((prev) => {
                                    const includeInFullMatchBySlot = [...prev.includeInFullMatchBySlot];
                                    includeInFullMatchBySlot[idx] = e.target.checked;
                                    return { ...prev, includeInFullMatchBySlot };
                                  })}
                                />
                                Участвует в «Совпадения все файлы»
                              </label>
                              <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '6px', color: vprFileLabelColor }}>
                                Файл {idx + 1}
                                {isLeadingMode && manualMainFileIndex === idx ? ' — ведущий' : ''}
                                {manualFiles[idx]?.fileName ? ` — ${fixVprDisplayFileName(manualFiles[idx].fileName).slice(0, 36)}` : ''}
                              </div>
                              {getManualWorkbookSheetCountForSlot(manualFiles, idx) > 1 && manualFiles[idx]?.excelSheetName && (
                                <div style={{ fontSize: '11px', marginBottom: '6px', opacity: 0.88, color: '#166534' }}>
                                  Вкладка: <strong>{manualFiles[idx].excelSheetName}</strong>
                                  {manualFiles[idx]?.fileName ? ` (книга ${fixVprDisplayFileName(manualFiles[idx].fileName).slice(0, 28)})` : ''}
                                </div>
                              )}
                              {getManualWorkbookSheetCountForSlot(manualFiles, idx) <= 1 && manualFiles[idx]?.fileName && (
                                <div style={{ fontSize: '11px', marginBottom: '6px', opacity: 0.88, color: '#166534' }}>
                                  Книга: <strong>{fixVprDisplayFileName(manualFiles[idx].fileName).slice(0, 40)}</strong>
                                  <span style={{ fontWeight: 400 }}> (одна вкладка — в отчёте только имя файла)</span>
                                </div>
                              )}
                              <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>Столбец телефона</div>
                              <select
                                value={phoneVal}
                                onChange={(e) => {
                                  const { col, part } = parseManualCrossColSelectValue(e.target.value);
                                  setManualCrossFileMatch((prev) => {
                                    const phoneBySlot = [...prev.phoneBySlot];
                                    phoneBySlot[idx] = { col, part };
                                    return { ...prev, phoneBySlot };
                                  });
                                }}
                                style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1px solid rgba(33,147,176,0.5)', fontSize: '13px', marginBottom: '6px' }}
                              >
                                {colOpts.map((o) => (
                                  <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                              </select>
                              <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>
                                Столбец ФИО {(isNdMode || isLeadingMode) ? '(обязательно для связки)' : '(необязательно)'}
                              </div>
                              <select
                                value={fioVal}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setManualCrossFileMatch((prev) => {
                                    const fioBySlot = [...prev.fioBySlot];
                                    if (v === '') fioBySlot[idx] = { col: '', part: '' };
                                    else {
                                      const { col, part } = parseManualCrossColSelectValue(v);
                                      fioBySlot[idx] = { col, part };
                                    }
                                    return { ...prev, fioBySlot };
                                  });
                                }}
                                style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1px solid rgba(33,147,176,0.5)', fontSize: '13px', marginBottom: '6px' }}
                              >
                                <option value="">— не использовать —</option>
                                {colOpts.map((o) => (
                                  <option key={`fio-${o.value}`} value={o.value}>{o.label}</option>
                                ))}
                              </select>
                              {showNdCol && (
                              <>
                              <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>Столбец НД / статус звонка</div>
                              <select
                                value={ndSlot.col === '' || ndSlot.col == null ? '' : String(ndSlot.col)}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setManualCrossFileMatch((prev) => {
                                    const ndBySlot = [...(prev.ndBySlot || [])];
                                    ndBySlot[idx] = v === '' ? { col: '' } : { col: parseInt(v, 10) };
                                    return { ...prev, ndBySlot };
                                  });
                                }}
                                style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1px solid rgba(22,163,74,0.55)', fontSize: '13px', marginBottom: '6px' }}
                              >
                                <option value="">— выберите столбец —</option>
                                {colOpts.map((o) => {
                                  const { col } = parseManualCrossColSelectValue(o.value);
                                  return (
                                    <option key={`nd-${o.value}`} value={String(col)}>{o.label}</option>
                                  );
                                })}
                              </select>
                              <p style={{ margin: '0 0 6px', fontSize: '11px', color: '#64748b', lineHeight: 1.4 }}>
                                Сначала ищется строка с «нд» (тел.+ФИО). Потом тот же тел.+ФИО — во всех файлах. «Дозвон» в другом файле — не звонить.
                              </p>
                              </>
                              )}
                              <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '4px', marginTop: '4px' }}>Столбец даты в файле</div>
                              <select
                                value={dateVal}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setManualCrossFileMatch((prev) => {
                                    const dateBySlot = [...prev.dateBySlot];
                                    if (v === '') dateBySlot[idx] = { col: '', part: '' };
                                    else dateBySlot[idx] = { col: parseInt(v, 10), part: '' };
                                    return { ...prev, dateBySlot };
                                  });
                                }}
                                style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1px solid rgba(33,147,176,0.5)', fontSize: '13px', marginBottom: '6px' }}
                              >
                                <option value="">— из столбца не брать —</option>
                                {colOpts.map((o) => (
                                  <option key={`date-${o.value}`} value={String(parseManualCrossColSelectValue(o.value).col)}>{o.label}</option>
                                ))}
                              </select>
                              <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>Дата выгрузки (если в таблице нет даты)</div>
                              <input
                                type="date"
                                value={manualCrossFileMatch.exportDateBySlot[idx] || ''}
                                onChange={(e) => setManualCrossFileMatch((prev) => {
                                  const exportDateBySlot = [...prev.exportDateBySlot];
                                  exportDateBySlot[idx] = e.target.value || '';
                                  return { ...prev, exportDateBySlot };
                                })}
                                style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1px solid rgba(33,147,176,0.5)', fontSize: '13px', boxSizing: 'border-box' }}
                              />
                              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginTop: '8px', fontSize: '12px', fontWeight: 600, color: '#166534', cursor: dateVal ? 'pointer' : 'not-allowed', opacity: dateVal ? 1 : 0.55 }}>
                                <input
                                  type="checkbox"
                                  disabled={!dateVal}
                                  checked={!!manualCrossFileMatch.splitByDateBySlot?.[idx]}
                                  onChange={(e) => setManualCrossFileMatch((prev) => {
                                    const splitByDateBySlot = [...prev.splitByDateBySlot];
                                    splitByDateBySlot[idx] = e.target.checked;
                                    return { ...prev, splitByDateBySlot };
                                  })}
                                  style={{ marginTop: '2px' }}
                                />
                                <span>
                                  Один лист — несколько дней: разбить по столбцу даты (каждая дата отдельно в отчёте)
                                </span>
                              </label>
                              {manualCrossFileMatch.splitByDateBySlot?.[idx] && dateVal && (() => {
                                const groups = previewManualUniqueDateGroups(manualFiles[idx], parseInt(dateVal, 10));
                                if (!groups.length) return null;
                                return (
                                  <p style={{ margin: '6px 0 0', fontSize: '11px', lineHeight: 1.45, color: '#475569' }}>
                                    Будет {groups.length} блок(ов): {groups.slice(0, 8).map((g) => `${g.date} (${g.count})`).join(', ')}
                                    {groups.length > 8 ? '…' : ''}
                                  </p>
                                );
                              })()}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                  </>
                  ) : hasMultiSheetBook ? (
                    <p style={{ margin: '10px 0 0', fontSize: '12px', lineHeight: 1.5, color: '#475569' }}>
                      Сначала разнесите вкладки первого файла. Затем загрузите второй файл в свободный слот — появится настройка <strong>«Совпадения по телефону / ФИО»</strong> для поиска дублей.
                    </p>
                  ) : null}
                </div>
                );
              })()}
              {manualDataSlotIndices.length >= 2 && (
                <VprManualDeepseekPanel
                  loadedIndices={manualDataSlotIndices}
                  manualFiles={manualFiles}
                  manualCrossFileMatch={manualCrossFileMatch}
                  manualFileConfigs={manualFileConfigs}
                  manualRenames={manualRenames}
                  manualMainFileIndex={manualMainFileIndex}
                  manualResult={manualResult}
                  deepseekResult={manualDeepseekResult}
                  onDeepseekResult={setManualDeepseekResult}
                  onAfterAnalysis={() => {
                    if (manualResult?.sheets?.length) setManualResultTab(manualResult.sheets.length);
                  }}
                  onError={setManualError}
                  onClearError={() => setManualError('')}
                />
              )}
              <div style={{ marginTop: '4px', marginBottom: '14px', paddingTop: '12px', borderTop: '1px dashed rgba(33,147,176,0.35)' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 600, color: vprFileLabelColor, marginBottom: '8px' }}>
                  <input type="checkbox" checked={manualLinkMode} onChange={(e) => { setManualLinkMode(e.target.checked); if (!e.target.checked) setManualChainDraft(null); }} />
                  Связывание столбцов между файлами (цветные маркеры)
                </label>
                {manualLinkMode && (
                  <p style={{ margin: '0 0 12px', fontSize: '13px', lineHeight: 1.6, color: '#334155' }}>
                    1) «Начать новую связку» — закрепляется цвет. 2) В каждом файле нажмите <strong style={{ color: '#0f172a' }}>цветной квадрат</strong> у столбца из файла <em>или</em> у строки <strong>добавленного столбца</strong> («+ Добавить столбец») — по порядку цепочки. Один выбор на файл в связке; повторный клик по тому же файлу заменит выбор. 3) «Завершить связку» (минимум 2 точки). Для добавленных столбцов в результат попадают те же значения, что в конце листа (например после привязки к извлечению ФИО/телефона). 4) «Запуск» / очередь строит правила.
                  </p>
                )}
                {manualLinkMode && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px', alignItems: 'center' }}>
                    <button
                      type="button"
                      disabled={!!manualChainDraft}
                      onClick={() => {
                        const color = MANUAL_LINK_COLORS[manualColumnChains.length % MANUAL_LINK_COLORS.length];
                        setManualChainDraft({ id: vprManualGenId(), color, linkAction: 'compare_eq', picks: [] });
                        setManualError('');
                      }}
                      style={{ padding: '8px 12px', borderRadius: '8px', border: '2px solid rgba(33,147,176,0.5)', background: 'rgba(255,255,255,0.95)', fontWeight: 600, cursor: manualChainDraft ? 'not-allowed' : 'pointer', fontSize: '13px', opacity: manualChainDraft ? 0.55 : 1 }}
                    >
                      Начать новую связку
                      <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 3, marginLeft: 8, verticalAlign: 'middle', background: MANUAL_LINK_COLORS[manualColumnChains.length % MANUAL_LINK_COLORS.length], border: '1px solid rgba(0,0,0,0.2)' }} />
                    </button>
                    <button
                      type="button"
                      disabled={!manualChainDraft || (manualChainDraft.picks || []).length < 2}
                      onClick={() => {
                        if (!manualChainDraft || manualChainDraft.picks.length < 2) {
                          setManualError('В связке должно быть минимум два столбца.');
                          return;
                        }
                        setManualColumnChains((ch) => [...ch, { id: manualChainDraft.id, color: manualChainDraft.color, linkAction: manualChainDraft.linkAction || 'compare_eq', picks: [...manualChainDraft.picks] }]);
                        setManualChainDraft(null);
                        setManualError('');
                      }}
                      style={{ padding: '8px 12px', borderRadius: '8px', border: '2px solid #16a34a', color: '#166534', background: 'rgba(220,252,231,0.9)', fontWeight: 600, cursor: (!manualChainDraft || manualChainDraft.picks.length < 2) ? 'not-allowed' : 'pointer', fontSize: '13px' }}
                    >
                      Завершить связку
                    </button>
                    <button type="button" disabled={!manualChainDraft} onClick={() => { setManualChainDraft(null); setManualError(''); }} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #94a3b8', background: 'rgba(255,255,255,0.9)', fontSize: '13px', cursor: manualChainDraft ? 'pointer' : 'not-allowed', opacity: manualChainDraft ? 1 : 0.5 }}>
                      Отменить черновик
                    </button>
                  </div>
                )}
                {manualLinkMode && manualChainDraft && (
                  <div
                    style={{
                      marginBottom: '12px',
                      padding: '14px 16px',
                      borderRadius: '12px',
                      border: `2px solid ${manualChainDraft.color}`,
                      background: '#ffffff',
                      boxShadow: `0 2px 12px rgba(15, 23, 42, 0.08), inset 0 0 0 1px rgba(255,255,255,1)`,
                      color: '#0f172a',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
                      <span style={{ width: 14, height: 14, borderRadius: 4, background: manualChainDraft.color, border: '1px solid rgba(0,0,0,0.12)', flexShrink: 0 }} title="Цвет этой связки" />
                      <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', letterSpacing: '0.01em' }}>Текущая связка (черновик)</div>
                    </div>
                    <div style={{ fontSize: '14px', marginBottom: '12px', lineHeight: 1.55, color: '#1e293b', fontWeight: 500 }}>
                      {(manualChainDraft.picks || []).length === 0 ? (
                        <span style={{ color: '#475569' }}>Точки цепочки ещё не выбраны — нажмите цветной квадрат у столбца из файла или у добавленного столбца (+) в карточке.</span>
                      ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '6px 4px' }}>
                          {(manualChainDraft.picks || []).map((p, pi) => {
                            const isAdded = pickIsAddedSlot(p);
                            const cap = isAdded
                              ? manualAddedPickCaption(manualAddedColumns, p.fileIdx, p.addedSlotIndex, 44)
                              : manualPickColumnCaption(manualFiles, manualRenames, p.fileIdx, p.colIdx, 44);
                            const letter = isAdded ? '+' : colIndexToLetter(p.colIdx);
                            const partSuf = isAdded ? '' : manualColPartPickSuffix(manualFileConfigs[p.fileIdx], p.colPart, manualAddedColumns[p.fileIdx]);
                            const fragKey = `${p.fileIdx}-${isAdded ? `add${p.addedSlotIndex}` : `${p.colIdx}-${p.colPart || ''}`}-${pi}`;
                            return (
                              <React.Fragment key={fragKey}>
                                {pi > 0 && <span style={{ color: '#94a3b8', fontWeight: 700, padding: '0 2px' }}>→</span>}
                                <span style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '6px', padding: '6px 10px', borderRadius: '8px', background: '#f1f5f9', border: '1px solid #e2e8f0' }}>
                                  <span style={{ fontWeight: 800, color: '#0f172a' }}>Файл {p.fileIdx + 1}</span>
                                  <span style={{ color: '#64748b' }}>·</span>
                                  <span style={{ fontFamily: 'ui-monospace, Consolas, monospace', fontWeight: 800, color: manualChainDraft.color }}>{letter}</span>
                                  {isAdded ? <span style={{ color: '#64748b', fontWeight: 600, fontSize: '12px' }}>добавл.</span> : null}
                                  {partSuf ? <span style={{ color: manualChainDraft.color, fontWeight: 700 }}>{partSuf}</span> : null}
                                  {cap ? <span style={{ color: '#334155', fontWeight: 600, maxWidth: '100%', wordBreak: 'break-word' }} title={cap}>{cap}</span> : null}
                                </span>
                              </React.Fragment>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <label style={{ fontSize: '13px', display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '8px', color: '#334155', fontWeight: 600 }}>
                      <span>Действие для этой связки</span>
                      <select
                        value={MANUAL_LINK_ACTION_OPTIONS.some((o) => o.value === (manualChainDraft.linkAction || 'compare_eq')) ? (manualChainDraft.linkAction || 'compare_eq') : 'compare_eq'}
                        onChange={(e) => setManualChainDraft((d) => (d ? { ...d, linkAction: e.target.value } : d))}
                        style={{
                          padding: '10px 12px',
                          borderRadius: '8px',
                          fontSize: '14px',
                          width: '100%',
                          maxWidth: '100%',
                          background: '#f8fafc',
                          color: '#0f172a',
                          border: '2px solid #cbd5e1',
                          fontWeight: 600,
                          cursor: 'pointer',
                          boxSizing: 'border-box',
                        }}
                      >
                        {MANUAL_LINK_ACTION_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                )}
                {manualLinkMode && manualColumnChains.length > 0 && (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '8px', color: '#0f172a' }}>Готовые связки</div>
                    {manualColumnChains.map((ch, ci) => (
                      <div key={ch.id} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px', marginBottom: '8px', padding: '10px 12px', borderRadius: '10px', border: `2px solid ${ch.color}`, background: '#ffffff', color: '#0f172a', boxShadow: '0 1px 4px rgba(15,23,42,0.06)' }}>
                        <span style={{ fontWeight: 800, fontSize: '13px', color: '#64748b', minWidth: '1.5em' }}>{ci + 1}.</span>
                        <span style={{ fontSize: '13px', flex: '1 1 220px', lineHeight: 1.5, color: '#1e293b', fontWeight: 500 }}>
                          {(ch.picks || []).map((p, pi) => {
                            const isAdded = pickIsAddedSlot(p);
                            const cap = isAdded
                              ? manualAddedPickCaption(manualAddedColumns, p.fileIdx, p.addedSlotIndex, 32)
                              : manualPickColumnCaption(manualFiles, manualRenames, p.fileIdx, p.colIdx, 32);
                            const partSuf = isAdded ? '' : manualColPartPickSuffix(manualFileConfigs[p.fileIdx], p.colPart, manualAddedColumns[p.fileIdx]);
                            const letter = isAdded ? '+' : colIndexToLetter(p.colIdx);
                            const spanKey = `${p.fileIdx}-${isAdded ? `add${p.addedSlotIndex}` : `${p.colIdx}-${p.colPart || ''}`}-${pi}`;
                            return (
                              <span key={spanKey}>
                                {pi > 0 && <span style={{ color: '#94a3b8', fontWeight: 700 }}> → </span>}
                                <strong style={{ color: '#0f172a' }}>Ф{p.fileIdx + 1}</strong>
                                <span style={{ fontFamily: 'ui-monospace, Consolas, monospace', fontWeight: 800, color: ch.color }}> [{letter}]</span>
                                {isAdded ? <span style={{ color: '#64748b', fontSize: '12px' }}> доб.</span> : null}
                                {partSuf ? <span style={{ color: ch.color, fontWeight: 700 }}>{partSuf}</span> : null}
                                {cap ? <span style={{ color: '#475569' }}>{` ${cap}`}</span> : null}
                              </span>
                            );
                          })}
                        </span>
                        <select value={MANUAL_LINK_ACTION_OPTIONS.some((o) => o.value === (ch.linkAction || 'compare_eq')) ? (ch.linkAction || 'compare_eq') : 'compare_eq'} onChange={(e) => setManualColumnChains((prev) => prev.map((x) => (x.id === ch.id ? { ...x, linkAction: e.target.value } : x)))} style={{ padding: '8px 10px', fontSize: '13px', borderRadius: '8px', maxWidth: 'min(100%, 340px)', background: '#f8fafc', color: '#0f172a', border: '2px solid #cbd5e1', fontWeight: 600, cursor: 'pointer' }}>
                          {MANUAL_LINK_ACTION_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                        <button type="button" onClick={() => setManualColumnChains((prev) => prev.filter((x) => x.id !== ch.id))} style={{ padding: '6px 12px', fontSize: '13px', color: '#b91c1c', cursor: 'pointer', fontWeight: 600, border: '1px solid #fecaca', borderRadius: '8px', background: '#fef2f2' }}>Удалить</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '8px', color: vprFileLabelColor }}>Очередь задач ({manualTaskSteps.length})</div>
              {manualTaskSteps.length === 0 ? (
                <p style={{ margin: '0 0 10px', fontSize: '13px', opacity: 0.88 }}>Очередь пуста — настройте столбцы и правила ниже, затем нажмите «Добавить в очередь». Либо запустите сразу без очереди.</p>
              ) : (
                <ul style={{ margin: '0 0 12px', paddingLeft: 0, listStyle: 'none', maxHeight: 200, overflowY: 'auto' }}>
                  {manualTaskSteps.map((st, qi) => (
                    <li key={st.id} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', marginBottom: '8px', padding: '8px 10px', borderRadius: '8px', background: 'rgba(255,255,255,0.9)', border: '1px solid rgba(33,147,176,0.35)' }}>
                      <span style={{ fontWeight: 600, flex: '1 1 140px' }}>{qi + 1}. {st.title}</span>
                      <span style={{ fontSize: '12px', opacity: 0.85 }}>связок: {(st.columnChains || []).length}, правил: {(st.rules || []).length}</span>
                      <button type="button" onClick={() => setManualTaskSteps((prev) => { const n = prev.slice(); const t = n[qi - 1]; n[qi - 1] = n[qi]; n[qi] = t; return n; })} disabled={qi === 0} style={{ padding: '4px 8px', fontSize: '12px', cursor: qi === 0 ? 'not-allowed' : 'pointer' }}>Вверх</button>
                      <button type="button" onClick={() => setManualTaskSteps((prev) => { const n = prev.slice(); if (qi >= n.length - 1) return n; const t = n[qi + 1]; n[qi + 1] = n[qi]; n[qi] = t; return n; })} disabled={qi >= manualTaskSteps.length - 1} style={{ padding: '4px 8px', fontSize: '12px', cursor: qi >= manualTaskSteps.length - 1 ? 'not-allowed' : 'pointer' }}>Вниз</button>
                      <button type="button" onClick={() => {
                        const s = manualTaskSteps[qi];
                        setManualSelectedCols(manualSlotList.map((i) => [...(s.selectedCols[i] || [])]));
                        setManualRules(JSON.parse(JSON.stringify(s.rules || [])));
                        const draftFileConfigs = JSON.parse(JSON.stringify(s.fileConfigs || manualFileConfigs));
                        setManualFileConfigs(draftFileConfigs);
                        setManualFiles((prev) => {
                          const next = prev.slice(0, manualSlotCount);
                          manualSlotList.forEach((i) => {
                            const sn = draftFileConfigs[i]?.excelSheetName;
                            if (next[i]?.sheetsByName && sn) {
                              next[i] = applyManualExcelSheetToEntry(next[i], sn);
                            }
                          });
                          return next;
                        });
                        setManualDeletedCols(JSON.parse(JSON.stringify(s.deletedCols || [[], [], [], []])));
                        setManualRenames(JSON.parse(JSON.stringify(s.renames || [{}, {}, {}, {}])));
                        setManualAddedColumns(JSON.parse(JSON.stringify(s.addedColumns || [[], [], [], []])));
                        setManualMainFileIndex(s.mainFileIndex != null ? s.mainFileIndex : 0);
                        setManualColumnChains(JSON.parse(JSON.stringify(s.columnChains || [])));
                        setManualChainDraft(null);
                      }} style={{ padding: '4px 10px', fontSize: '12px', cursor: 'pointer' }}>В черновик</button>
                      <button type="button" onClick={() => setManualTaskSteps((prev) => prev.filter((_, j) => j !== qi))} style={{ padding: '4px 10px', fontSize: '12px', color: '#c53030', cursor: 'pointer' }}>Удалить</button>
                    </li>
                  ))}
                </ul>
              )}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                <input type="text" placeholder="Название задачи в отчёте" value={manualNewTaskTitle} onChange={(e) => setManualNewTaskTitle(e.target.value)} style={{ flex: '1 1 200px', padding: '8px 10px', borderRadius: '8px', border: '2px solid rgba(33,147,176,0.45)', fontSize: '14px' }} />
                <button
                  type="button"
                  onClick={() => {
                    const title = (manualNewTaskTitle || '').trim() || `Задача ${manualTaskSteps.length + 1}`;
                    const snap = {
                      id: vprManualGenId(),
                      title,
                      selectedCols: manualSlotList.map((i) => [...(manualSelectedCols[i] || [])]),
                      rules: JSON.parse(JSON.stringify(manualRules)),
                      fileConfigs: manualSlotList.map((i) => {
                        const c = JSON.parse(JSON.stringify(manualFileConfigs[i] || {}));
                        if (manualFiles[i]?.excelSheetName) c.excelSheetName = manualFiles[i].excelSheetName;
                        return c;
                      }),
                      deletedCols: JSON.parse(JSON.stringify(manualDeletedCols)),
                      renames: JSON.parse(JSON.stringify(manualRenames)),
                      addedColumns: JSON.parse(JSON.stringify(manualAddedColumns)),
                      mainFileIndex: manualMainFileIndex,
                      columnChains: JSON.parse(JSON.stringify(manualColumnChains)),
                    };
                    setManualTaskSteps((q) => [...q, snap]);
                    setManualNewTaskTitle('');
                  }}
                  style={{ padding: '10px 14px', borderRadius: '8px', border: '2px solid var(--modal-vpr-button-border-color, rgba(33, 147, 176, 0.5))', background: 'var(--modal-vpr-button-background, rgba(255,255,255,0.9))', fontWeight: 600, cursor: 'pointer', fontSize: '14px', color: 'var(--modal-vpr-button-text-color, #2193b0)' }}
                >
                  Добавить в очередь
                </button>
                <button type="button" onClick={() => { setManualTaskSteps([]); setManualError(''); }} style={{ padding: '10px 14px', borderRadius: '8px', border: '1px solid #c53030', color: '#c53030', background: 'rgba(255,255,255,0.9)', fontWeight: 600, cursor: 'pointer', fontSize: '14px' }}>
                  Очистить очередь
                </button>
              </div>
            </div>
          )}
          {manualUiSubTab === 'presets' && (
            <div style={{ marginBottom: '16px', padding: '14px', borderRadius: '12px', border: '2px solid rgba(33, 147, 176, 0.35)', background: 'rgba(255,255,255,0.75)' }}>
              <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '10px', color: vprFileLabelColor }}>Сохранённые пресеты (очередь задач + дата)</div>
              <p style={{ margin: '0 0 10px', fontSize: '13px', opacity: 0.88 }}>Пресет хранит только список задач и фильтр даты. Файлы нужно снова загрузить в те же слоты (1–4), структура столбцов должна совпадать.</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px', alignItems: 'center' }}>
                <input type="text" placeholder="Имя пресета" value={manualPresetName} onChange={(e) => setManualPresetName(e.target.value)} style={{ flex: '1 1 180px', padding: '8px 10px', borderRadius: '8px', border: '2px solid rgba(33,147,176,0.45)', fontSize: '14px' }} />
                <button
                  type="button"
                  onClick={() => {
                    const name = (manualPresetName || '').trim();
                    if (!name) {
                      setManualError('Укажите имя пресета');
                      return;
                    }
                    setManualError('');
                    const entry = {
                      id: vprManualGenId(),
                      name,
                      taskSteps: JSON.parse(JSON.stringify(manualTaskSteps)),
                      filterDate: manualFilterDate || '',
                      savedAt: new Date().toISOString(),
                    };
                    persistManualPresets([...manualPresetsList.filter((p) => p.name !== name), entry]);
                    setManualPresetName('');
                  }}
                  style={{ padding: '10px 14px', borderRadius: '8px', border: '2px solid var(--modal-vpr-button-border-color, rgba(33, 147, 176, 0.5))', background: 'var(--modal-vpr-button-background, rgba(255,255,255,0.9))', fontWeight: 600, cursor: 'pointer', fontSize: '14px' }}
                >
                  Сохранить пресет
                </button>
              </div>
              {manualPresetsList.length === 0 ? (
                <p style={{ margin: 0, fontSize: '13px', opacity: 0.8 }}>Пока нет сохранённых пресетов.</p>
              ) : (
                <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none' }}>
                  {manualPresetsList.map((p) => (
                    <li key={p.id} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', marginBottom: '8px', padding: '8px', borderRadius: '8px', background: 'rgba(255,255,255,0.85)' }}>
                      <strong style={{ flex: '1 1 120px' }}>{p.name}</strong>
                      <span style={{ fontSize: '12px', opacity: 0.85 }}>шагов: {(p.taskSteps || []).length}</span>
                      <button type="button" onClick={() => { setManualTaskSteps(JSON.parse(JSON.stringify(p.taskSteps || []))); if (p.filterDate) setManualFilterDate(p.filterDate); setManualUiSubTab('builder'); setManualError(''); }} style={{ padding: '6px 12px', fontSize: '13px', cursor: 'pointer', fontWeight: 600 }}>Загрузить</button>
                      <button type="button" onClick={() => persistManualPresets(manualPresetsList.filter((x) => x.id !== p.id))} style={{ padding: '6px 12px', fontSize: '13px', color: '#c53030', cursor: 'pointer' }}>Удалить</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {manualSlotList.map((idx) => (
            <input key={idx} ref={getManualInputRef(idx)} type="file" accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" style={{ display: 'none' }} onChange={(e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              dismissManualRunResult();
              setManualError('');
              const reader = new FileReader();
              reader.onload = (ev) => {
                try {
                  const isExcel = /\.(xlsx|xls)$/i.test(f.name);
                  let headers = [];
                  let rows = [];
                  if (isExcel && ev.target?.result instanceof ArrayBuffer) {
                    const { sheetNames, sheetsByName } = parseManualExcelWorkbookAllSheets(ev.target.result);
                    const preferred = (manualFileConfigs[idx]?.excelSheetName || '').trim();
                    const entry = buildManualFileEntryFromExcel(f, sheetsByName, sheetNames, preferred || undefined);
                    setManualFiles((prev) => {
                      const next = prev.slice(0, manualSlotCount);
                      next[idx] = entry;
                      if (sheetNames.length >= 2) {
                        const autoStart = getNextDistributeStartSlot(
                          next,
                          manualSlotCount,
                          manualDistributedRanges,
                        );
                        setManualDistributeSourceSlot(idx);
                        setManualDistributeStartSlot(
                          manualSingleWorkbookMode ? Math.max(autoStart, 1) : autoStart,
                        );
                      }
                      return next;
                    });
                    setManualFileConfigs((prev) => {
                      const next = prev.slice();
                      const cur = next[idx] || createDefaultManualFileConfigEntry();
                      const include = defaultMergeSheetsIncludeForEntry(entry);
                      next[idx] = {
                        ...cur,
                        excelSheetName: entry.excelSheetName,
                        params: { ...(cur.params || createDefaultManualFileParams()), mergeSheetsInclude: include },
                      };
                      return next;
                    });
                    if (sheetNames.length >= 1) {
                      const summary = sheetNames.map((sn) => {
                        const n = (sheetsByName[sn]?.rows || []).length;
                        return `«${sn}» (${n})`;
                      }).join(', ');
                      setManualError(
                        sheetNames.length < 2
                          ? `Загружено в слот ${idx + 1}. Найдена 1 вкладка: ${summary}. Для сравнения дней в книге — 2–4 листа или «Разбить по дате».`
                          : `Загружено в слот ${idx + 1}: ${sheetNames.length} вкладок — ${summary}. Выбран для разнесения — нажмите «Разнести этот файл по слотам» (после первого файла загрузите второй в свободный слот).`
                      );
                    }
                    headers = entry.headers;
                    rows = entry.rows;
                  } else if (typeof ev.target?.result === 'string') {
                    const lines = parseCSV(ev.target.result);
                    headers = (lines[0] || []).map((c) => String(c ?? '').trim());
                    rows = (lines.length > 1 ? lines.slice(1) : []).map((row) => (Array.isArray(row) ? row : []).map((c) => (c != null ? String(c) : '')));
                  }
                  if (!isExcel || !(ev.target?.result instanceof ArrayBuffer)) {
                  setManualFiles((prev) => {
                      const next = prev.slice(0, manualSlotCount);
                    next[idx] = { file: f, fileName: f.name, headers, rows };
                    return next;
                  });
                  }
                  setManualSelectedCols((prev) => {
                    const next = prev.slice(0, manualSlotCount);
                    next[idx] = headers.map((_, i) => i);
                    return next;
                  });
                  setManualDeletedCols((prev) => { const n = prev.slice(); n[idx] = []; return n; });
                  setManualRenames((prev) => { const n = prev.slice(); n[idx] = {}; return n; });
                  setManualAddedColumns((prev) => { const n = prev.slice(); n[idx] = []; return n; });
                  setManualFileConfigs((prev) => {
                    const n = prev.slice();
                    n[idx] = createDefaultManualFileConfigEntry();
                    return n;
                  });
                  setManualCrossFileMatch((prev) => patchManualCrossFileMatchForSlot(prev, idx, headers, manualRenames[idx]));
                } catch (err) {
                  setManualError('Ошибка чтения файла: ' + (err?.message || ''));
                }
              };
              if (/\.(xlsx|xls)$/i.test(f.name)) reader.readAsArrayBuffer(f);
              else reader.readAsText(f, 'UTF-8');
              e.target.value = '';
            }} />
          ))}
          <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : 'repeat(auto-fill, minmax(260px, 1fr))', gap: '16px' }}>
            {manualSlotList.map((idx) => {
              const sharedWorkbook = getManualWorkbookSourceForSharedSlot(
                manualFiles,
                idx,
                manualDistributeSourceSlot,
                manualSingleWorkbookMode,
                manualDistributedRanges,
              );
              return (
              <div key={idx} style={{ position: 'relative', border: '1px solid rgba(33, 147, 176, 0.5)', borderRadius: '12px', padding: '14px', background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)', boxShadow: '0 2px 10px rgba(15, 23, 42, 0.05)', color: '#0f172a' }}>
                {manualFiles[idx] && (
                  <button
                    type="button"
                    title="Очистить слот"
                    onClick={() => clearManualSlot(idx)}
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      width: '28px',
                      height: '28px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      border: '2px solid #e53e3e',
                      background: '#fff5f5',
                      color: '#c53030',
                      cursor: 'pointer',
                      padding: 0,
                      zIndex: 2,
                    }}
                  >
                    <FiX size={16} />
                  </button>
                )}
                <label style={{ display: 'block', fontSize: '15px', fontWeight: 600, marginBottom: '6px', color: vprFileLabelColor, paddingRight: manualFiles[idx] ? '32px' : 0 }}>
                  {isManualWorkbookMasterSlot(manualFiles[idx])
                    ? `Книга-мастер (слот ${idx + 1})`
                    : (manualSingleWorkbookMode ? `Вкладка ${idx + 1}` : `Файл ${idx + 1}`)}
                </label>
                {isManualWorkbookMasterSlot(manualFiles[idx]) ? (
                  <p style={{ margin: '0 0 10px', fontSize: '12px', color: '#166534', lineHeight: 1.45, padding: '8px 10px', borderRadius: '8px', background: '#ecfdf5', border: '1px solid #86efac' }}>
                    Хранит книгу «{fixVprDisplayFileName(manualFiles[idx].fileName || '').slice(0, 40)}» ({getManualEntrySheetCount(manualFiles[idx])} вкладок).
                    В сравнении и «Запустить» не участвует — данные в соседних слотах.
                  </p>
                ) : sharedWorkbook ? (
                  <p style={{ margin: '0 0 10px', fontSize: '12px', color: '#475569', lineHeight: 1.4 }}>
                    Вкладка из книги в слоте {sharedWorkbook.slot + 1}
                  </p>
                ) : (
                <button type="button" onClick={() => getManualInputRef(idx).current?.click()} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '10px 12px', borderRadius: '8px', border: '2px solid var(--modal-vpr-button-border-color, rgba(33, 147, 176, 0.5))', background: 'var(--modal-vpr-button-background, rgba(255,255,255,0.8))', color: 'var(--modal-vpr-button-text-color, #2193b0)', fontWeight: 600, cursor: 'pointer', fontSize: '14px', width: '100%' }}>
                  <FiUpload /> {manualFiles[idx]?.fileName || 'Выбрать…'}
                </button>
                )}
                {(
                  (sharedWorkbook
                  ? (sharedWorkbook.entry.sheetNames || [])
                  : (manualFiles[idx]?.sheetNames || [])
                ).length >= 1
                  && (manualSingleWorkbookMode || (manualFiles[idx]?.sheetNames || []).length > 1) && (
                  <label style={{ display: 'block', marginTop: '10px', fontSize: '13px', fontWeight: 600, color: vprFileLabelColor }}>
                    Вкладка Excel
                    <select
                      value={manualFiles[idx]?.excelSheetName || manualFiles[idx]?.sheetNames?.[0] || ''}
                      title="Каждый слот — отдельный лист книги Excel"
                      onChange={(e) => {
                        const sheetName = e.target.value;
                        setManualFileConfigs((prev) => {
                          const next = prev.slice();
                          next[idx] = { ...(next[idx] || {}), excelSheetName: sheetName };
                          return next;
                        });
                        const master = sharedWorkbook;
                        const shared = !!sharedWorkbook;
                        const entry = shared && master
                          ? linkManualSlotToWorkbook(master.entry, sheetName, master.slot)
                          : applyManualExcelSheetToEntry(manualFiles[idx], sheetName);
                        const hdrs = entry?.headers || [];
                        dismissManualRunResult();
                        setManualCrossFileMatch((prev) => {
                          const exportDateBySlot = [...prev.exportDateBySlot];
                          if (!exportDateBySlot[idx]) exportDateBySlot[idx] = guessExportDateFromSheetName(sheetName);
                          return patchManualCrossFileMatchForSlot({ ...prev, exportDateBySlot }, idx, hdrs, manualRenames[idx]);
                        });
                        setManualFiles((prev) => {
                          const next = prev.slice(0, manualSlotCount);
                          if (shared && master) {
                            next[idx] = linkManualSlotToWorkbook(master.entry, sheetName, master.slot);
                            return next;
                          }
                          const cur = next[idx];
                          if (!cur) return prev;
                          next[idx] = applyManualExcelSheetToEntry(cur, sheetName);
                          return next;
                        });
                        setManualSelectedCols((prev) => {
                          const next = prev.slice(0, manualSlotCount);
                          next[idx] = hdrs.map((_, i) => i);
                          return next;
                        });
                        setManualDeletedCols((prev) => { const n = prev.slice(); n[idx] = []; return n; });
                        setManualRenames((prev) => { const n = prev.slice(); n[idx] = {}; return n; });
                        setManualAddedColumns((prev) => { const n = prev.slice(); n[idx] = []; return n; });
                        setManualError('');
                      }}
                      style={{ display: 'block', width: '100%', marginTop: '6px', padding: '8px 10px', borderRadius: '8px', border: '2px solid rgba(33, 147, 176, 0.45)', fontSize: '13px', background: '#fff', color: '#0f172a' }}
                    >
                      {(sharedWorkbook
                        ? sharedWorkbook.entry.sheetNames
                        : manualFiles[idx]?.sheetNames || []).map((sn) => {
                        const byName = sharedWorkbook
                          ? sharedWorkbook.entry.sheetsByName
                          : manualFiles[idx]?.sheetsByName;
                        const rowN = (byName?.[sn]?.rows || []).length;
                        return (
                          <option key={sn} value={sn}>
                            {sn} ({rowN} строк)
                          </option>
                        );
                      })}
                    </select>
                    {manualFiles[idx]?.excelSheetName && (
                      <span style={{ display: 'block', marginTop: '4px', fontSize: '11px', color: '#64748b' }}>
                        Строк с данными на этом листе: {(manualFiles[idx]?.rows || []).length}
                      </span>
                    )}
                  </label>
                ))}
                {!isManualWorkbookMasterSlot(manualFiles[idx]) && manualFiles[idx]?.headers?.length > 0 && (
                  <>
                  <div style={{ marginTop: '10px' }}>
                    <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '4px', color: vprFileLabelColor }}>Столбцы (участвуют в парсинге):</div>
                    <div style={{ maxHeight: 180, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {((manualSelectedCols[idx] || [])
                        .filter((colIdx) => (manualDeletedCols[idx] || []).indexOf(colIdx) < 0)
                        .map((colIdx, orderPos) => {
                          const headers = manualFiles[idx].headers || [];
                          const h = headers[colIdx];
                          const displayName = (manualRenames[idx] || {})[colIdx] ?? h ?? `Кол.${colIdx + 1}`;
                          const letter = colIndexToLetter(orderPos);
                          const partChoices = manualColPartChoices(colIdx, manualFileConfigs[idx], manualAddedColumns[idx]);
                          const linkMeta = findAnyManualLinkMetaForCol(manualColumnChains, manualChainDraft, idx, colIdx, partChoices);
                          return (
                        <div key={colIdx} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          flexWrap: 'wrap',
                          color: '#0f172a',
                          ...(linkMeta ? {
                            borderLeft: `4px solid ${linkMeta.color}`,
                            paddingLeft: '10px',
                            paddingTop: '4px',
                            paddingBottom: '4px',
                            borderRadius: '8px',
                            background: linkMeta.isDraft ? '#f8fafc' : '#f1f5f9',
                            boxShadow: `inset 0 0 0 1px ${linkMeta.isDraft ? linkMeta.color + '55' : 'rgba(148,163,184,0.45)'}`,
                          } : {}),
                        }}>
                          {manualLinkMode && manualChainDraft && (
                            partChoices ? (
                              <span style={{ display: 'inline-flex', gap: 4, flexShrink: 0 }} title="Два результата после действия с файлом: выберите ФИО или телефон (или часть 1 / 2)">
                                {partChoices.map((c) => (
                                  <button
                                    key={c.part}
                                    type="button"
                                    title={c.title}
                                    onClick={() => {
                                      setManualSelectedCols((prev) => {
                                        const next = prev.slice(0, manualSlotCount);
                                        if ((next[idx] || []).indexOf(colIdx) < 0) {
                                          next[idx] = [...(next[idx] || []), colIdx].sort((a, b) => a - b);
                                        }
                                        return next;
                                      });
                                      setManualChainDraft((d) => {
                                        if (!d) return d;
                                        const picks = [...(d.picks || [])];
                                        const existing = picks.findIndex((p) => p.fileIdx === idx);
                                        const entry = { fileIdx: idx, colIdx, colPart: c.part };
                                        if (existing >= 0) picks[existing] = entry;
                                        else picks.push(entry);
                                        return { ...d, picks };
                                      });
                                    }}
                                    style={{
                                      minWidth: 22,
                                      height: 22,
                                      flexShrink: 0,
                                      borderRadius: 4,
                                      border: '2px solid rgba(0,0,0,0.2)',
                                      background: manualChainDraft.color,
                                      cursor: 'pointer',
                                      padding: '0 4px',
                                      fontSize: '11px',
                                      fontWeight: 800,
                                      color: '#0f172a',
                                    }}
                                  >
                                    {c.short}
                                  </button>
                                ))}
                              </span>
                            ) : (
                              <button
                                type="button"
                                title="Добавить столбец в текущую связку (повторный клик по этому файлу заменит выбор)"
                                onClick={() => {
                                  setManualSelectedCols((prev) => {
                                    const next = prev.slice(0, manualSlotCount);
                                    if ((next[idx] || []).indexOf(colIdx) < 0) {
                                      next[idx] = [...(next[idx] || []), colIdx].sort((a, b) => a - b);
                                    }
                                    return next;
                                  });
                                  setManualChainDraft((d) => {
                                    if (!d) return d;
                                    const picks = [...(d.picks || [])];
                                    const existing = picks.findIndex((p) => p.fileIdx === idx);
                                    if (existing >= 0) picks[existing] = { fileIdx: idx, colIdx };
                                    else picks.push({ fileIdx: idx, colIdx });
                                    return { ...d, picks };
                                  });
                                }}
                                style={{
                                  width: 22,
                                  height: 22,
                                  flexShrink: 0,
                                  borderRadius: 4,
                                  border: '2px solid rgba(0,0,0,0.2)',
                                  background: manualChainDraft.color,
                                  cursor: 'pointer',
                                  padding: 0,
                                }}
                              />
                            )
                          )}
                          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '15px', flex: '1 1 auto', minWidth: 0 }}>
                            <input type="checkbox" checked onChange={(e) => {
                              if (!e.target.checked) setManualSelectedCols((prev) => {
                                const next = prev.slice(0, manualSlotCount);
                                next[idx] = (next[idx] || []).filter((i) => i !== colIdx);
                                return next;
                              });
                            }} />
                            <span title={displayName} style={{ color: '#0f172a' }}><strong style={{ color: '#b91c1c', minWidth: '1.8em' }}>{letter}</strong> — <span style={{ fontWeight: 600 }}>{(displayName || '').slice(0, 22)}{(displayName || '').length > 22 ? '…' : ''}</span></span>
                          </label>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <button type="button" onClick={() => {
                              setManualSelectedCols((prev) => {
                                const next = prev.slice(0, manualSlotCount);
                                const arr = [...(next[idx] || [])];
                                const i = arr.indexOf(colIdx);
                                if (i > 0) {
                                  const tmp = arr[i - 1];
                                  arr[i - 1] = arr[i];
                                  arr[i] = tmp;
                                }
                                next[idx] = arr;
                                return next;
                              });
                            }} title="Выше" style={{ padding: '2px 4px', borderRadius: '4px', border: '1px solid rgba(33,147,176,0.6)', background: 'rgba(255,255,255,0.9)', cursor: 'pointer' }}>
                              <FiChevronUp />
                            </button>
                            <button type="button" onClick={() => {
                              setManualSelectedCols((prev) => {
                                const next = prev.slice(0, manualSlotCount);
                                const arr = [...(next[idx] || [])];
                                const i = arr.indexOf(colIdx);
                                if (i >= 0 && i < arr.length - 1) {
                                  const tmp = arr[i + 1];
                                  arr[i + 1] = arr[i];
                                  arr[i] = tmp;
                                }
                                next[idx] = arr;
                                return next;
                              });
                            }} title="Ниже" style={{ padding: '2px 4px', borderRadius: '4px', border: '1px solid rgba(33,147,176,0.6)', background: 'rgba(255,255,255,0.9)', cursor: 'pointer' }}>
                              <FiChevronDown />
                            </button>
                            <button type="button" onClick={() => { const v = prompt('Новое имя столбца:', displayName || ''); if (v !== null) setManualRenames((prev) => { const n = prev.slice(); n[idx] = { ...(n[idx] || {}), [colIdx]: v.trim() }; return n; }); }} style={{ padding: '2px 8px', fontSize: '12px', borderRadius: '4px', border: '1px solid rgba(33, 147, 176, 0.6)', background: 'rgba(255,255,255,0.9)', cursor: 'pointer' }}>Имя</button>
                            <button type="button" onClick={() => { setManualDeletedCols((prev) => { const n = prev.slice(); n[idx] = [...(n[idx] || []), colIdx].sort((a,b)=>a-b); return n; }); setManualSelectedCols((prev) => { const next = prev.slice(0, manualSlotCount); next[idx] = (next[idx] || []).filter((i) => i !== colIdx); return next; }); }} style={{ padding: '2px 8px', fontSize: '12px', borderRadius: '4px', border: '1px solid #c53030', background: 'rgba(255,255,255,0.9)', color: '#c53030', cursor: 'pointer' }}>Удалить</button>
                          </div>
                        </div>
                          );
                        }))}
                      {(manualAddedColumns[idx] || []).map((ac, ai) => {
                        const pEx = manualFileConfigs[idx]?.params || {};
                        const fiSlot = parseManualAddedExtractIdx(pEx.extractFioAddedColumnIndex);
                        const phSlot = parseManualAddedExtractIdx(pEx.extractPhoneAddedColumnIndex);
                        const isFioSlot = (manualFileConfigs[idx]?.action === 'extract_phone' && fiSlot === ai);
                        const isPhoneSlot = (manualFileConfigs[idx]?.action === 'extract_phone' && phSlot === ai);
                        const linkMetaAdded = findManualLinkMeta(manualColumnChains, manualChainDraft, idx, undefined, undefined, ai);
                        return (
                        <div key={`add-${ai}`} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          flexWrap: 'wrap',
                          ...(linkMetaAdded ? {
                            borderLeft: `4px solid ${linkMetaAdded.color}`,
                            paddingLeft: '10px',
                            paddingTop: '4px',
                            paddingBottom: '4px',
                            borderRadius: '8px',
                            background: linkMetaAdded.isDraft ? '#f8fafc' : '#f1f5f9',
                            boxShadow: `inset 0 0 0 1px ${linkMetaAdded.isDraft ? linkMetaAdded.color + '55' : 'rgba(148,163,184,0.45)'}`,
                          } : {}),
                        }}>
                          <span style={{ fontSize: '15px' }}><strong style={{ color: '#c53030' }}>{ac.letter}</strong> — {(ac.name || '').slice(0, 18) || 'Новый столбец'}</span>
                          {isFioSlot && <span style={{ fontSize: '11px', fontWeight: 700, padding: '2px 6px', borderRadius: '6px', background: '#e0f2fe', color: '#0369a1', border: '1px solid #7dd3fc' }}>ФИО извлечение</span>}
                          {isPhoneSlot && <span style={{ fontSize: '11px', fontWeight: 700, padding: '2px 6px', borderRadius: '6px', background: '#ecfccb', color: '#3f6212', border: '1px solid #bef264' }}>Телефон извлечение</span>}
                          {manualLinkMode && manualChainDraft && (
                            <button
                              type="button"
                              title="Добавить этот добавленный столбец в текущую связку (значение = как в конце листа после запуска; при извлечении ФИО/тел. — см. подписи выше)"
                              onClick={() => {
                                setManualChainDraft((d) => {
                                  if (!d) return d;
                                  const picks = [...(d.picks || [])];
                                  const existing = picks.findIndex((p) => p.fileIdx === idx);
                                  const entry = { fileIdx: idx, addedSlotIndex: ai };
                                  if (existing >= 0) picks[existing] = entry;
                                  else picks.push(entry);
                                  return { ...d, picks };
                                });
                              }}
                              style={{
                                width: 22,
                                height: 22,
                                flexShrink: 0,
                                borderRadius: 4,
                                border: '2px solid rgba(0,0,0,0.2)',
                                background: manualChainDraft.color,
                                cursor: 'pointer',
                                padding: 0,
                              }}
                            />
                          )}
                          <button type="button" onClick={() => { const v = prompt('Имя столбца:', ac.name || ''); if (v !== null) setManualAddedColumns((prev) => { const n = prev.slice(); n[idx] = (n[idx] || []).slice(); n[idx][ai] = { ...ac, name: v.trim() }; return n; }); }} style={{ padding: '2px 8px', fontSize: '12px', borderRadius: '4px', border: '1px solid rgba(33, 147, 176, 0.6)', background: 'rgba(255,255,255,0.9)', cursor: 'pointer' }}>Имя</button>
                          <button type="button" onClick={() => setManualAddedColumns((prev) => { const n = prev.slice(); n[idx] = (n[idx] || []).filter((_, i) => i !== ai); return n; })} style={{ padding: '2px 8px', fontSize: '12px', borderRadius: '4px', border: '1px solid #c53030', color: '#c53030', background: 'rgba(255,255,255,0.9)', cursor: 'pointer' }}>Удалить</button>
                        </div>
                        );
                      })}
                    </div>
                    <button type="button" onClick={() => {
                      const headers = manualFiles[idx]?.headers || [];
                      const added = manualAddedColumns[idx] || [];
                      const nextLetter = colIndexToLetter(headers.length + added.length);
                      const name = prompt('Имя нового столбца:', '');
                      if (name !== null) setManualAddedColumns((prev) => { const n = prev.slice(); n[idx] = [...(n[idx] || []), { letter: nextLetter, name: (name || '').trim() }]; return n; });
                    }} style={{ marginTop: '6px', padding: '6px 12px', fontSize: '13px', borderRadius: '6px', border: '2px solid var(--modal-vpr-button-border-color, rgba(33, 147, 176, 0.5))', background: 'var(--modal-vpr-button-background, rgba(255,255,255,0.8))', color: 'var(--modal-vpr-button-text-color, #2193b0)', fontWeight: 600, cursor: 'pointer' }}>
                      + Добавить столбец
                    </button>
                    {((manualFiles[idx].headers || []).filter((_, colIdx) => (manualDeletedCols[idx] || []).indexOf(colIdx) < 0 && (manualSelectedCols[idx] || []).indexOf(colIdx) < 0).length) > 0 && (
                      <div style={{ marginTop: '12px', padding: '12px 14px', borderRadius: '10px', background: 'rgba(255, 255, 255, 0.95)', border: '2px solid rgba(33, 147, 176, 0.4)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.8)', maxHeight: 150, overflowY: 'auto' }}>
                        <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: 'var(--modal-vpr-header-text-color, #1a5f6e)' }}>Исключённые (добавить в парсинг):</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {(manualFiles[idx].headers || [])
                            .map((h, colIdx) => ({ h, colIdx }))
                            .filter(({ colIdx }) => (manualDeletedCols[idx] || []).indexOf(colIdx) < 0)
                            .filter(({ colIdx }) => (manualSelectedCols[idx] || []).indexOf(colIdx) < 0)
                            .map(({ h, colIdx }) => {
                              const displayName = (manualRenames[idx] || {})[colIdx] ?? h ?? `Кол.${colIdx + 1}`;
                              const partChoicesEx = manualColPartChoices(colIdx, manualFileConfigs[idx], manualAddedColumns[idx]);
                              const linkMetaEx = findAnyManualLinkMetaForCol(manualColumnChains, manualChainDraft, idx, colIdx, partChoicesEx);
                              return (
                              <div key={colIdx} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#0f172a', ...(linkMetaEx ? { borderLeft: `4px solid ${linkMetaEx.color}`, paddingLeft: '8px', paddingTop: '2px', paddingBottom: '2px', borderRadius: '8px', background: linkMetaEx.isDraft ? '#f8fafc' : '#f1f5f9', boxShadow: `inset 0 0 0 1px ${linkMetaEx.isDraft ? linkMetaEx.color + '55' : 'rgba(148,163,184,0.45)'}` } : {}) }}>
                                {manualLinkMode && manualChainDraft && (
                                  partChoicesEx ? (
                                    <span style={{ display: 'inline-flex', gap: 3 }}>
                                      {partChoicesEx.map((c) => (
                                        <button
                                          key={c.part}
                                          type="button"
                                          title={c.title}
                                          onClick={() => {
                                            setManualSelectedCols((prev) => {
                                              const next = prev.slice(0, manualSlotCount);
                                              if ((next[idx] || []).indexOf(colIdx) < 0) {
                                                next[idx] = [...(next[idx] || []), colIdx].sort((a, b) => a - b);
                                              }
                                              return next;
                                            });
                                            setManualChainDraft((d) => {
                                              if (!d) return d;
                                              const picks = [...(d.picks || [])];
                                              const existing = picks.findIndex((p) => p.fileIdx === idx);
                                              const entry = { fileIdx: idx, colIdx, colPart: c.part };
                                              if (existing >= 0) picks[existing] = entry;
                                              else picks.push(entry);
                                              return { ...d, picks };
                                            });
                                          }}
                                          style={{ minWidth: 20, height: 22, flexShrink: 0, borderRadius: 4, border: '2px solid rgba(0,0,0,0.2)', background: manualChainDraft.color, cursor: 'pointer', padding: '0 3px', fontSize: '10px', fontWeight: 800, color: '#0f172a' }}
                                        >
                                          {c.short}
                                        </button>
                                      ))}
                                    </span>
                                  ) : (
                                    <button
                                      type="button"
                                      title="Связать этот столбец (в связку)"
                                      onClick={() => {
                                        setManualSelectedCols((prev) => {
                                          const next = prev.slice(0, manualSlotCount);
                                          if ((next[idx] || []).indexOf(colIdx) < 0) {
                                            next[idx] = [...(next[idx] || []), colIdx].sort((a, b) => a - b);
                                          }
                                          return next;
                                        });
                                        setManualChainDraft((d) => {
                                          if (!d) return d;
                                          const picks = [...(d.picks || [])];
                                          const existing = picks.findIndex((p) => p.fileIdx === idx);
                                          if (existing >= 0) picks[existing] = { fileIdx: idx, colIdx };
                                          else picks.push({ fileIdx: idx, colIdx });
                                          return { ...d, picks };
                                        });
                                      }}
                                      style={{ width: 22, height: 22, flexShrink: 0, borderRadius: 4, border: '2px solid rgba(0,0,0,0.2)', background: manualChainDraft.color, cursor: 'pointer', padding: 0 }}
                                    />
                                  )
                                )}
                                <button type="button" onClick={() => setManualSelectedCols((prev) => {
                                  const next = prev.slice(0, manualSlotCount);
                                  next[idx] = [...(next[idx] || []), colIdx].sort((a, b) => a - b);
                                  return next;
                                })} style={{ padding: '6px 12px', borderRadius: '8px', border: '2px solid rgba(180, 80, 80, 0.6)', background: '#fff', fontSize: '13px', cursor: 'pointer', color: '#2d3748', fontWeight: 500, boxShadow: '0 1px 2px rgba(0,0,0,0.06)' }}>
                                  <strong style={{ color: '#c53030', marginRight: '4px' }}>{colIndexToLetter(colIdx)}</strong> — {(displayName || '').slice(0, 18)}{(displayName || '').length > 18 ? '…' : ''}
                                </button>
                              </div>
                              );
                            })}
                        </div>
                      </div>
                    )}
                  </div>
                  <div style={{ marginTop: '12px', paddingTop: '10px', borderTop: '1px solid rgba(33, 147, 176, 0.3)' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginBottom: '8px' }}>
                      <div style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>Действие</div>
                      <button
                        type="button"
                        onClick={() => setManualConditionsOpen((prev) => { const n = [...prev]; n[idx] = !n[idx]; return n; })}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '8px 14px',
                          borderRadius: '8px',
                          border: manualConditionsOpen[idx] ? '2px solid #0ea5e9' : '2px solid #cbd5e1',
                          background: manualConditionsOpen[idx] ? '#e0f2fe' : '#f8fafc',
                          color: '#0f172a',
                          fontWeight: 700,
                          fontSize: '14px',
                          cursor: 'pointer',
                        }}
                      >
                        <FiFilter size={16} />
                        Условия
                        {(manualFileConfigs[idx]?.params?.manipulationConditions || []).length > 0 && (
                          <span style={{ fontSize: '12px', opacity: 0.85 }}>({(manualFileConfigs[idx].params.manipulationConditions || []).length})</span>
                        )}
                      </button>
                    </div>
                    {manualConditionsOpen[idx] && (
                      <div style={{ marginBottom: '12px', padding: '12px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', background: '#ffffff', color: '#0f172a', boxShadow: '0 1px 4px rgba(15,23,42,0.06)' }}>
                        <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '6px' }}>Условия на строку</div>
                        <p style={{ margin: '0 0 10px', fontSize: '12px', lineHeight: 1.5, color: '#475569' }}>
                          Задайте проверки по <strong>любому столбцу файла</strong> (не только выбранным для парсинга). Все условия объединяются через <strong>И</strong>. Есть сравнения <strong>числа с порогом</strong> (больше / меньше / равно и т.д., запятая как десятичный разделитель). На сервере учитываются для <strong>«Замена текста»</strong>, <strong>«Преобразование телефонов»</strong> и <strong>«Заполнить из столбца, если пусто»</strong>; для разбиения столбца и извлечения телефона список только сохраняется в пресете.
                        </p>
                        {(manualFileConfigs[idx]?.params?.manipulationConditions || []).length === 0 ? (
                          <p style={{ margin: '0 0 10px', fontSize: '13px', color: '#64748b' }}>Нет условий — манипуляция выполняется для каждой строки.</p>
                        ) : (
                          <ul style={{ margin: '0 0 10px', paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {(manualFileConfigs[idx].params.manipulationConditions || []).map((cond) => {
                              const opMeta = MANUAL_MANIPULATION_CONDITION_OPS.find((o) => o.value === cond.op) || MANUAL_MANIPULATION_CONDITION_OPS[1];
                              return (
                                <li key={cond.id} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', padding: '8px 10px', borderRadius: '8px', background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                  <select
                                    value={Number(cond.colIndex) || 0}
                                    onChange={(e) => {
                                      const v = parseInt(e.target.value, 10);
                                      setManualFileConfigs((prev) => {
                                        const n = prev.slice();
                                        const p = { ...(n[idx]?.params || {}) };
                                        const list = (p.manipulationConditions || []).map((c) => (c.id === cond.id ? { ...c, colIndex: v } : c));
                                        n[idx] = { ...n[idx], params: { ...p, manipulationConditions: list } };
                                        return n;
                                      });
                                    }}
                                    style={{ padding: '6px 8px', borderRadius: '6px', border: '2px solid #cbd5e1', fontSize: '13px', minWidth: 140, flex: '1 1 160px' }}
                                  >
                                    {(manualFiles[idx].headers || []).map((h, ci) => (
                                      <option key={ci} value={ci}>{colIndexToLetter(ci)} — {String((manualRenames[idx] || {})[ci] ?? h ?? `Кол.${ci + 1}`).slice(0, 28)}</option>
                                    ))}
                                  </select>
                                  <select
                                    value={cond.op || 'not_empty'}
                                    onChange={(e) => {
                                      const op = e.target.value;
                                      setManualFileConfigs((prev) => {
                                        const n = prev.slice();
                                        const p = { ...(n[idx]?.params || {}) };
                                        const list = (p.manipulationConditions || []).map((c) => (c.id === cond.id ? { ...c, op } : c));
                                        n[idx] = { ...n[idx], params: { ...p, manipulationConditions: list } };
                                        return n;
                                      });
                                    }}
                                    style={{ padding: '6px 8px', borderRadius: '6px', border: '2px solid #cbd5e1', fontSize: '13px', flex: '1 1 140px' }}
                                  >
                                    {MANUAL_MANIPULATION_CONDITION_OPS.map((o) => (
                                      <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                  </select>
                                  {opMeta.needsValue ? (
                                    <input
                                      type="text"
                                      placeholder="Значение"
                                      value={cond.value ?? ''}
                                      onChange={(e) => {
                                        setManualFileConfigs((prev) => {
                                          const n = prev.slice();
                                          const p = { ...(n[idx]?.params || {}) };
                                          const list = (p.manipulationConditions || []).map((c) => (c.id === cond.id ? { ...c, value: e.target.value } : c));
                                          n[idx] = { ...n[idx], params: { ...p, manipulationConditions: list } };
                                          return n;
                                        });
                                      }}
                                      style={{ padding: '6px 10px', borderRadius: '6px', border: '2px solid #cbd5e1', fontSize: '13px', flex: '1 1 120px', minWidth: 100 }}
                                    />
                                  ) : (
                                    <span style={{ fontSize: '12px', color: '#94a3b8', flex: '1 1 80px' }}>—</span>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setManualFileConfigs((prev) => {
                                        const n = prev.slice();
                                        const p = { ...(n[idx]?.params || {}) };
                                        const list = (p.manipulationConditions || []).filter((c) => c.id !== cond.id);
                                        n[idx] = { ...n[idx], params: { ...p, manipulationConditions: list } };
                                        return n;
                                      });
                                    }}
                                    style={{ padding: '6px 10px', fontSize: '12px', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: '6px', background: '#fef2f2', cursor: 'pointer', fontWeight: 600 }}
                                  >
                                    Удалить
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            setManualFileConfigs((prev) => {
                              const n = prev.slice();
                              const p = { ...(n[idx]?.params || createDefaultManualFileParams()) };
                              const list = [...(p.manipulationConditions || [])];
                              list.push({ id: vprManualGenId(), colIndex: 0, op: 'not_empty', value: '' });
                              n[idx] = { ...n[idx], params: { ...p, manipulationConditions: list } };
                              return n;
                            });
                          }}
                          style={{ padding: '8px 14px', borderRadius: '8px', border: '2px solid rgba(33, 147, 176, 0.45)', background: '#fff', color: '#0f172a', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}
                        >
                          + Добавить условие
                        </button>
                      </div>
                    )}
                    <select value={manualFileConfigs[idx]?.action || 'discrepancies'} onChange={(e) => {
                      const v = e.target.value;
                      setManualFileConfigs((prev) => {
                        const n = prev.slice();
                        const cur = { ...(n[idx] || {}), action: v };
                        if (v === 'phone_format' && manualFiles[idx]?.headers) {
                          const p = { ...(cur.params || createDefaultManualFileParams()) };
                          const detected = detectPhoneColumnIndices(manualFiles[idx].headers);
                          if (!p.phoneFormatColIndices?.length && detected.length > 0) {
                            p.phoneFormatColIndices = detected;
                          }
                          cur.params = p;
                        }
                        if (v === 'replace_text' && manualFiles[idx]?.headers) {
                          const p = { ...(cur.params || createDefaultManualFileParams()) };
                          const hdrLen = manualFiles[idx].headers.length;
                          if (!p.replaceTextColIndices?.length) {
                            p.replaceTextColIndices = Array.from({ length: hdrLen }, (_, i) => i);
                          }
                          cur.params = p;
                        }
                        n[idx] = cur;
                        return n;
                      });
                      if (v === 'cross_file_phone') setManualCrossFileMatch((prev) => ({ ...prev, enabled: true }));
                    }} style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '2px solid rgba(33, 147, 176, 0.5)', background: 'rgba(255,255,255,0.9)', fontSize: '15px', marginBottom: '8px', color: '#0f172a' }}>
                      <option value="discrepancies">Расхождения (построчно с другим файлом)</option>
                      <option value="cross_file_phone">Совпадения по телефону (все файлы)</option>
                      <option value="merge_sheets">Объединить все вкладки в один лист</option>
                      <option value="replace_text">Замена текста</option>
                      <option value="phone_format">Преобразование телефонов</option>
                      <option value="split_column">Разделение столбца по разделителю</option>
                      <option value="extract_phone">Извлечь телефон из столбца (ФИО + номер)</option>
                      <option value="fallback_if_empty">Заполнить из столбца, если пусто (ТТН / запасной номер)</option>
                    </select>
                    {(manualFileConfigs[idx]?.actionPipeline || []).length > 0 && (
                      <div style={{ marginBottom: '10px', padding: '10px 12px', borderRadius: '8px', background: '#f0fdf4', border: '1px solid #86efac' }}>
                        <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '6px', color: '#166534' }}>Очередь для «Запустить»:</div>
                        <ol style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#14532d' }}>
                          {(manualFileConfigs[idx].actionPipeline || []).map((step, si) => (
                            <li key={si} style={{ marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              <span>{si + 1}. {getManualActionStepLabel(step)}</span>
                              <button
                                type="button"
                                title="Убрать из очереди"
                                onClick={() => setManualFileConfigs((prev) => {
                                  const n = prev.slice();
                                  const cur = n[idx] || {};
                                  const pipeline = (cur.actionPipeline || []).filter((_, j) => j !== si);
                                  n[idx] = { ...cur, actionPipeline: pipeline };
                                  return n;
                                })}
                                style={{ border: 'none', background: 'transparent', color: '#b91c1c', cursor: 'pointer', padding: 0, fontSize: '12px', fontWeight: 700 }}
                              >
                                удалить
                              </button>
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}
                    {MANUAL_PIPELINE_ACTIONS.has(manualFileConfigs[idx]?.action || '') && (
                      <div style={{ marginBottom: '10px' }}>
                        <button
                          type="button"
                          onClick={() => applyManualActionForSlot(idx)}
                          style={{
                            width: '100%',
                            padding: '10px 14px',
                            borderRadius: '8px',
                            border: '2px solid #16a34a',
                            background: 'linear-gradient(180deg, #dcfce7 0%, #bbf7d0 100%)',
                            color: '#14532d',
                            fontWeight: 700,
                            fontSize: '14px',
                            cursor: 'pointer',
                          }}
                        >
                          Применить к превью и добавить в очередь
                        </button>
                        <p style={{ margin: '6px 0 0', fontSize: '12px', lineHeight: 1.45, color: '#64748b' }}>
                          Настройте действие → «Применить» → выберите следующее в списке и снова «Применить». По «Запустить» выполнятся все шаги очереди по порядку.
                        </p>
                      </div>
                    )}
                    {(manualFileConfigs[idx]?.action || '') === 'merge_sheets' && (() => {
                      const sheetOpts = listMergeSheetOptions(manualFiles[idx]);
                      const include = manualFileConfigs[idx]?.params?.mergeSheetsInclude;
                      const includeSet = manualSheetKeysSelectionSet(
                        sheetOpts.map((o) => o.key),
                        include,
                      );
                      return (
                      <div style={{ marginBottom: '10px', padding: '10px 12px', borderRadius: '8px', background: '#f8fafc', border: '1px solid #cbd5e1' }}>
                        <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '8px', color: '#0f172a' }}>Вкладки для объединения</div>
                        {sheetOpts.length < 2 ? (
                          <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>В файле меньше 2 вкладок. Загрузите книгу с Sheet1 и Лист1 (или нажмите ✕ и загрузите снова).</p>
                        ) : (
                          <>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                              <button type="button" onClick={() => setManualFileConfigs((prev) => {
                                const n = prev.slice();
                                const p = { ...(n[idx]?.params || createDefaultManualFileParams()) };
                                p.mergeSheetsInclude = sheetOpts.map((o) => o.key);
                                n[idx] = { ...(n[idx] || {}), params: p };
                                return n;
                              })} style={{ padding: '4px 10px', fontSize: '12px', borderRadius: '6px', border: '1px solid #94a3b8', background: '#fff', cursor: 'pointer' }}>Все</button>
                              <button type="button" onClick={() => setManualFileConfigs((prev) => {
                                const n = prev.slice();
                                const p = { ...(n[idx]?.params || createDefaultManualFileParams()) };
                                p.mergeSheetsInclude = [];
                                n[idx] = { ...(n[idx] || {}), params: p };
                                return n;
                              })} style={{ padding: '4px 10px', fontSize: '12px', borderRadius: '6px', border: '1px solid #94a3b8', background: '#fff', cursor: 'pointer' }}>Снять все</button>
                            </div>
                            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              {sheetOpts.map((opt) => (
                                <li key={opt.key}>
                                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '13px', cursor: 'pointer', color: '#0f172a' }}>
                                    <input
                                      type="checkbox"
                                      checked={includeSet.has(opt.key)}
                                      onChange={(e) => setManualFileConfigs((prev) => {
                                        const n = prev.slice();
                                        const p = { ...(n[idx]?.params || createDefaultManualFileParams()) };
                                        let list = Array.isArray(p.mergeSheetsInclude)
                                          ? [...p.mergeSheetsInclude]
                                          : sheetOpts.map((o) => o.key);
                                        if (e.target.checked) {
                                          if (!list.includes(opt.key)) list.push(opt.key);
                                        } else {
                                          list = list.filter((k) => k !== opt.key);
                                        }
                                        p.mergeSheetsInclude = list;
                                        n[idx] = { ...(n[idx] || {}), params: p };
                                        return n;
                                      })}
                                      style={{ marginTop: '3px' }}
                                    />
                                    <span>
                                      <strong>{opt.label}</strong>
                                      {' — '}{opt.rowCount} строк
                                      {opt.dateLabel ? `, дата в таблице: ${opt.dateLabel}` : ''}
                                    </span>
                                  </label>
                                </li>
                              ))}
                            </ul>
                            <p style={{ margin: '8px 0 0', fontSize: '12px', lineHeight: 1.45, color: '#64748b' }}>
                              Отметьте минимум 2 вкладки → «Применить» → «Запустить». Даты 14.05 и 15.05 — в столбце «Плановий час доставки»; в превью результата показываются строки с разных дат.
                            </p>
                          </>
                        )}
                      </div>
                      );
                    })()}
                    {(manualFileConfigs[idx]?.action || 'discrepancies') === 'discrepancies' && (
                      <>
                        <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '4px', color: vprFileLabelColor }}>Файл(ы) для взаимодействия:</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                          {manualSlotList.filter((i) => i !== idx && manualFiles[i]).map((i) => (
                            <label key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '15px' }}>
                              <input type="checkbox" checked={(manualFileConfigs[idx]?.partnerFiles || []).indexOf(i) >= 0} onChange={(e) => setManualFileConfigs((prev) => { const n = prev.slice(); const pf = n[idx]?.partnerFiles || []; n[idx] = { ...(n[idx] || {}), partnerFiles: e.target.checked ? [...pf, i].sort((a,b)=>a-b) : pf.filter((x) => x !== i) }; return n; })} />
                              Файл {i + 1}
                            </label>
                          ))}
                        </div>
                      </>
                    )}
                    {(manualFileConfigs[idx]?.action || 'discrepancies') === 'discrepancies' && (
                      <>
                        <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '4px', color: vprFileLabelColor }}>Метод манипуляции:</div>
                        <select value={manualFileConfigs[idx]?.method || 'compare_eq'} onChange={(e) => setManualFileConfigs((prev) => { const n = prev.slice(); n[idx] = { ...(n[idx] || {}), method: e.target.value }; return n; })} style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '2px solid rgba(33, 147, 176, 0.5)', background: 'rgba(255,255,255,0.9)', fontSize: '15px' }}>
                          <option value="compare_eq">Сравнить (равно)</option>
                          <option value="compare_ne">Сравнить (не равно)</option>
                        </select>
                      </>
                    )}
                    {(manualFileConfigs[idx]?.action || '') === 'replace_text' && (() => {
                      const hdrs = manualFiles[idx]?.headers || [];
                      const replaceCols = manualFileConfigs[idx]?.params?.replaceTextColIndices;
                      const replaceSet = manualColIndicesSelectionSet(hdrs, replaceCols);
                      return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <input type="text" placeholder="Найти текст" value={manualFileConfigs[idx]?.params?.replaceFind ?? ''} onChange={(e) => setManualFileConfigs((prev) => { const n = prev.slice(); n[idx] = { ...(n[idx] || {}), params: { ...(n[idx]?.params || {}), replaceFind: e.target.value } }; return n; })} style={{ padding: '8px 10px', borderRadius: '6px', border: '2px solid rgba(33, 147, 176, 0.5)', fontSize: '15px' }} />
                        <input type="text" placeholder="Заменить на" value={manualFileConfigs[idx]?.params?.replaceWith ?? ''} onChange={(e) => setManualFileConfigs((prev) => { const n = prev.slice(); n[idx] = { ...(n[idx] || {}), params: { ...(n[idx]?.params || {}), replaceWith: e.target.value } }; return n; })} style={{ padding: '8px 10px', borderRadius: '6px', border: '2px solid rgba(33, 147, 176, 0.5)', fontSize: '15px' }} />
                        <div style={{ fontSize: '14px', fontWeight: 600, color: vprFileLabelColor }}>Столбцы для замены</div>
                        <p style={{ margin: 0, fontSize: '12px', lineHeight: 1.45, color: '#64748b' }}>
                          Замена только в отмеченных столбцах. После «Применить» нажмите «Запустить» для результата.
                        </p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          <button type="button" onClick={() => setManualFileConfigs((prev) => {
                            const n = prev.slice();
                            const p = { ...(n[idx]?.params || createDefaultManualFileParams()) };
                            p.replaceTextColIndices = hdrs.map((_, i) => i);
                            n[idx] = { ...(n[idx] || {}), params: p };
                            return n;
                          })} style={{ padding: '4px 10px', fontSize: '12px', borderRadius: '6px', border: '1px solid #94a3b8', background: '#fff', cursor: 'pointer' }}>Все столбцы</button>
                          <button type="button" onClick={() => setManualFileConfigs((prev) => {
                            const n = prev.slice();
                            const p = { ...(n[idx]?.params || createDefaultManualFileParams()) };
                            p.replaceTextColIndices = [];
                            n[idx] = { ...(n[idx] || {}), params: p };
                            return n;
                          })} style={{ padding: '4px 10px', fontSize: '12px', borderRadius: '6px', border: '1px solid #94a3b8', background: '#fff', cursor: 'pointer' }}>Снять все</button>
                        </div>
                        <ul style={{ margin: 0, padding: 0, listStyle: 'none', maxHeight: 120, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {hdrs.map((h, ci) => {
                            if ((manualDeletedCols[idx] || []).indexOf(ci) >= 0) return null;
                            const label = (manualRenames[idx] || {})[ci] ?? h ?? `Кол.${ci + 1}`;
                            return (
                              <li key={ci}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                                  <input
                                    type="checkbox"
                                    checked={replaceSet.has(ci)}
                                    onChange={(e) => setManualFileConfigs((prev) => {
                                      const n = prev.slice();
                                      const p = { ...(n[idx]?.params || createDefaultManualFileParams()) };
                                      let list = Array.isArray(p.replaceTextColIndices)
                                        ? [...p.replaceTextColIndices]
                                        : hdrs.map((_, i) => i);
                                      if (e.target.checked) {
                                        if (!list.includes(ci)) list.push(ci);
                                      } else {
                                        list = list.filter((x) => x !== ci);
                                      }
                                      p.replaceTextColIndices = [...new Set(list)].sort((a, b) => a - b);
                                      n[idx] = { ...(n[idx] || {}), params: p };
                                      return n;
                                    })}
                                  />
                                  <span><strong>{colIndexToLetter(ci)}</strong> — {String(label).slice(0, 40)}</span>
                                </label>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                      );
                    })()}
                    {(manualFileConfigs[idx]?.action || '') === 'phone_format' && (() => {
                      const hdrs = manualFiles[idx]?.headers || [];
                      const phoneCols = manualFileConfigs[idx]?.params?.phoneFormatColIndices;
                      const phoneSet = manualColIndicesSelectionSet(
                        hdrs,
                        Array.isArray(phoneCols) ? phoneCols : [],
                      );
                      return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: vprFileLabelColor }}>Столбцы с телефонами</div>
                        <p style={{ margin: 0, fontSize: '12px', lineHeight: 1.45, color: '#64748b' }}>
                          Отметьте только столбцы с мобильными номерами (например «Телефон отримувача»). Номер посылки, внутрішній номер, дата и ТТН не меняются.
                        </p>
                        {phoneSet.size === 0 && (
                          <p style={{ margin: 0, fontSize: '12px', color: '#b45309', fontWeight: 600 }}>
                            Отметьте хотя бы один столбец или нажмите «Найти по заголовку».
                          </p>
                        )}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '4px' }}>
                          <button
                            type="button"
                            onClick={() => setManualFileConfigs((prev) => {
                              const n = prev.slice();
                              const p = { ...(n[idx]?.params || createDefaultManualFileParams()) };
                              p.phoneFormatColIndices = detectPhoneColumnIndices(hdrs);
                              n[idx] = { ...(n[idx] || {}), params: p };
                              return n;
                            })}
                            style={{ padding: '4px 10px', fontSize: '12px', borderRadius: '6px', border: '1px solid #94a3b8', background: '#fff', cursor: 'pointer' }}
                          >
                            Найти по заголовку «телефон»
                          </button>
                          <button
                            type="button"
                            onClick={() => setManualFileConfigs((prev) => {
                              const n = prev.slice();
                              const p = { ...(n[idx]?.params || createDefaultManualFileParams()) };
                              p.phoneFormatColIndices = [];
                              n[idx] = { ...(n[idx] || {}), params: p };
                              return n;
                            })}
                            style={{ padding: '4px 10px', fontSize: '12px', borderRadius: '6px', border: '1px solid #94a3b8', background: '#fff', cursor: 'pointer' }}
                          >
                            Снять все
                          </button>
                        </div>
                        <ul style={{ margin: 0, padding: 0, listStyle: 'none', maxHeight: 140, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {hdrs.map((h, ci) => {
                            if ((manualDeletedCols[idx] || []).indexOf(ci) >= 0) return null;
                            const label = (manualRenames[idx] || {})[ci] ?? h ?? `Кол.${ci + 1}`;
                            return (
                              <li key={ci}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                                  <input
                                    type="checkbox"
                                    checked={phoneSet.has(ci)}
                                    onChange={(e) => setManualFileConfigs((prev) => {
                                      const n = prev.slice();
                                      const p = { ...(n[idx]?.params || createDefaultManualFileParams()) };
                                      let list = Array.isArray(p.phoneFormatColIndices) ? [...p.phoneFormatColIndices] : [];
                                      if (e.target.checked) {
                                        if (!list.includes(ci)) list.push(ci);
                                      } else {
                                        list = list.filter((x) => x !== ci);
                                      }
                                      p.phoneFormatColIndices = list.sort((a, b) => a - b);
                                      n[idx] = { ...(n[idx] || {}), params: p };
                                      return n;
                                    })}
                                  />
                                  <span><strong>{colIndexToLetter(ci)}</strong> — {String(label).slice(0, 42)}</span>
                                </label>
                              </li>
                            );
                          })}
                        </ul>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: vprFileLabelColor, marginTop: '4px' }}>Формат</div>
                      <select value={manualFileConfigs[idx]?.method || 'ua'} onChange={(e) => setManualFileConfigs((prev) => { const n = prev.slice(); n[idx] = { ...(n[idx] || {}), method: e.target.value }; return n; })} style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '2px solid rgba(33, 147, 176, 0.5)', background: 'rgba(255,255,255,0.9)', fontSize: '15px' }}>
                        <option value="ua">Украина: 0XX XXX XX XX</option>
                        <option value="intl">Международный: +38 0XX XXX XX XX</option>
                        <option value="brackets">+380 (XX) XXX-XX-XX</option>
                        <option value="digits">Только цифры (без пробелов и символов)</option>
                      </select>
                      </div>
                      );
                    })()}
                    {(manualFileConfigs[idx]?.action || '') === 'split_column' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <select value={manualFileConfigs[idx]?.params?.splitColIndex ?? 0} onChange={(e) => setManualFileConfigs((prev) => { const n = prev.slice(); n[idx] = { ...(n[idx] || {}), params: { ...(n[idx]?.params || {}), splitColIndex: parseInt(e.target.value, 10) } }; return n; })} style={{ padding: '8px 10px', borderRadius: '6px', border: '2px solid rgba(33, 147, 176, 0.5)', fontSize: '15px' }}>
                          {(manualFiles[idx].headers || []).map((h, ci) => ({ h, ci })).filter(({ ci }) => (manualDeletedCols[idx] || []).indexOf(ci) < 0).map(({ h, ci }) => (
                            <option key={ci} value={ci}>{colIndexToLetter(ci)} — {(manualRenames[idx] || {})[ci] ?? h ?? `Кол.${ci + 1}`}</option>
                          ))}
                        </select>
                        <input type="text" placeholder="Разделитель (например запятая)" value={manualFileConfigs[idx]?.params?.splitDelimiter ?? ','} onChange={(e) => setManualFileConfigs((prev) => { const n = prev.slice(); n[idx] = { ...(n[idx] || {}), params: { ...(n[idx]?.params || {}), splitDelimiter: e.target.value } }; return n; })} style={{ padding: '8px 10px', borderRadius: '6px', border: '2px solid rgba(33, 147, 176, 0.5)', fontSize: '15px' }} />
                        <span style={{ fontSize: '14px', opacity: 0.9 }}>Создаются 2 столбца: часть до разделителя и после</span>
                      </div>
                    )}
                    {(manualFileConfigs[idx]?.action || '') === 'extract_phone' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: vprFileLabelColor }}>Столбец с «ФИО + телефон»:</div>
                        <select value={manualFileConfigs[idx]?.params?.extractPhoneColIndex ?? 0} onChange={(e) => setManualFileConfigs((prev) => { const n = prev.slice(); n[idx] = { ...(n[idx] || {}), params: { ...(n[idx]?.params || {}), extractPhoneColIndex: parseInt(e.target.value, 10) } }; return n; })} style={{ padding: '8px 10px', borderRadius: '6px', border: '2px solid rgba(33, 147, 176, 0.5)', fontSize: '15px' }}>
                          {(manualFiles[idx].headers || []).map((h, ci) => ({ h, ci })).filter(({ ci }) => (manualDeletedCols[idx] || []).indexOf(ci) < 0).map(({ h, ci }) => (
                            <option key={ci} value={ci}>{colIndexToLetter(ci)} — {(manualRenames[idx] || {})[ci] ?? h ?? `Кол.${ci + 1}`}</option>
                          ))}
                        </select>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: vprFileLabelColor }}>Формат телефона после извлечения:</div>
                        <select value={manualFileConfigs[idx]?.params?.extractPhoneFormat ?? 'ua'} onChange={(e) => setManualFileConfigs((prev) => { const n = prev.slice(); n[idx] = { ...(n[idx] || {}), params: { ...(n[idx]?.params || {}), extractPhoneFormat: e.target.value } }; return n; })} style={{ padding: '8px 10px', borderRadius: '6px', border: '2px solid rgba(33, 147, 176, 0.5)', fontSize: '15px' }}>
                          <option value="ua">Украина: 0XX XXX XX XX</option>
                          <option value="intl">Международный: +38 0XX XXX XX XX</option>
                          <option value="brackets">+380 (XX) XXX-XX-XX</option>
                          <option value="digits">Только цифры (без пробелов и символов)</option>
                        </select>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: vprFileLabelColor, marginTop: '6px' }}>Имена из «+ Добавить столбец» (по желанию)</div>
                        <p style={{ margin: '0 0 6px', fontSize: '13px', lineHeight: 1.5, color: '#475569' }}>
                          Добавьте два столбца выше (например «ФИО» и «Номер телефона»), затем укажите соответствие: под эти имена подставятся заголовки у извлечённых колонок в отчёте, а в хвост листа в те же строки попадут копии значений — удобно для связок и превью.
                        </p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                          <span style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>ФИО →</span>
                          <select
                            value={manualFileConfigs[idx]?.params?.extractFioAddedColumnIndex === '' || manualFileConfigs[idx]?.params?.extractFioAddedColumnIndex == null ? '' : String(manualFileConfigs[idx].params.extractFioAddedColumnIndex)}
                            onChange={(e) => setManualFileConfigs((prev) => {
                              const n = prev.slice();
                              const v = e.target.value;
                              n[idx] = { ...(n[idx] || {}), params: { ...(n[idx]?.params || {}), extractFioAddedColumnIndex: v === '' ? '' : parseInt(v, 10) } };
                              return n;
                            })}
                            style={{ padding: '8px 10px', borderRadius: '6px', border: '2px solid rgba(33, 147, 176, 0.5)', fontSize: '14px', minWidth: 160 }}
                          >
                            <option value="">— не задавать (авто)</option>
                            {(manualAddedColumns[idx] || []).map((ac, ai) => (
                              <option key={ai} value={ai}>{ac.name || ac.letter || `Добавленный ${ai + 1}`}</option>
                            ))}
                          </select>
                          <span style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>Телефон →</span>
                          <select
                            value={manualFileConfigs[idx]?.params?.extractPhoneAddedColumnIndex === '' || manualFileConfigs[idx]?.params?.extractPhoneAddedColumnIndex == null ? '' : String(manualFileConfigs[idx].params.extractPhoneAddedColumnIndex)}
                            onChange={(e) => setManualFileConfigs((prev) => {
                              const n = prev.slice();
                              const v = e.target.value;
                              n[idx] = { ...(n[idx] || {}), params: { ...(n[idx]?.params || {}), extractPhoneAddedColumnIndex: v === '' ? '' : parseInt(v, 10) } };
                              return n;
                            })}
                            style={{ padding: '8px 10px', borderRadius: '6px', border: '2px solid rgba(33, 147, 176, 0.5)', fontSize: '14px', minWidth: 160 }}
                          >
                            <option value="">— не задавать (авто)</option>
                            {(manualAddedColumns[idx] || []).map((ac, ai) => (
                              <option key={ai} value={ai}>{ac.name || ac.letter || `Добавленный ${ai + 1}`}</option>
                            ))}
                          </select>
                        </div>
                        <span style={{ fontSize: '14px', opacity: 0.9 }}>Создаются 2 столбца в середине выбранных колонок: ФИО и телефон (в выбранном формате); при привязке к добавленным — те же подписи и копии в конце листа.</span>
                      </div>
                    )}
                    {(manualFileConfigs[idx]?.action || '') === 'fallback_if_empty' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <p style={{ margin: 0, fontSize: '13px', lineHeight: 1.5, color: '#475569' }}>
                          Для каждой строки: если в <strong>основном столбце</strong> пусто (или только пробелы), в него копируется значение из <strong>запасного столбца</strong>. Если в основном уже есть текст — ячейка не меняется.
                        </p>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: vprFileLabelColor }}>Основной столбец (куда должна быть ТТН / номер):</div>
                        <select
                          value={manualFileConfigs[idx]?.params?.fallbackTargetColIndex ?? 0}
                          onChange={(e) => setManualFileConfigs((prev) => {
                            const n = prev.slice();
                            n[idx] = { ...(n[idx] || {}), params: { ...(n[idx]?.params || {}), fallbackTargetColIndex: parseInt(e.target.value, 10) } };
                            return n;
                          })}
                          style={{ padding: '8px 10px', borderRadius: '6px', border: '2px solid rgba(33, 147, 176, 0.5)', fontSize: '15px', color: '#0f172a' }}
                        >
                          {(manualFiles[idx].headers || []).map((h, ci) => (
                            <option key={ci} value={ci}>{colIndexToLetter(ci)} — {(manualRenames[idx] || {})[ci] ?? h ?? `Кол.${ci + 1}`}</option>
                          ))}
                        </select>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: vprFileLabelColor }}>Запасной столбец (откуда брать, если основной пуст):</div>
                        <select
                          value={manualFileConfigs[idx]?.params?.fallbackSourceColIndex ?? 0}
                          onChange={(e) => setManualFileConfigs((prev) => {
                            const n = prev.slice();
                            n[idx] = { ...(n[idx] || {}), params: { ...(n[idx]?.params || {}), fallbackSourceColIndex: parseInt(e.target.value, 10) } };
                            return n;
                          })}
                          style={{ padding: '8px 10px', borderRadius: '6px', border: '2px solid rgba(33, 147, 176, 0.5)', fontSize: '15px', color: '#0f172a' }}
                        >
                          {(manualFiles[idx].headers || []).map((h, ci) => (
                            <option key={ci} value={ci}>{colIndexToLetter(ci)} — {(manualRenames[idx] || {})[ci] ?? h ?? `Кол.${ci + 1}`}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                  </>
                )}
              </div>
              );
            })}
          </div>
          <div style={{ marginTop: '8px' }}>
            <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '8px', color: vprFileLabelColor }}>Действия между столбцами</div>
            <p style={{ margin: '0 0 8px', fontSize: '15px', opacity: 0.9 }}>Добавьте правило: столбец из одного файла + действие + столбец из другого файла. Если для файла задано «Извлечь телефон» или «Разбить столбец», в списке исходного столбца появятся отдельные пункты (ФИО и телефон, либо часть 1 и 2) — укажите, какую часть сравнивать или считать.</p>
            <select id="manual-action-type" style={{ marginRight: '8px', padding: '8px 12px', borderRadius: '8px', border: '2px solid rgba(33, 147, 176, 0.5)', background: 'rgba(255,255,255,0.9)', fontSize: '15px' }}>
              <option value="compare_eq">Сравнить (равно)</option>
              <option value="compare_ne">Сравнить (не равно)</option>
              <option value="sum">Сумма</option>
              <option value="subtract">Разность</option>
              <option value="multiply">Умножение</option>
              <option value="divide">Деление</option>
              <option value="concat">Объединить текст</option>
            </select>
            <button type="button" onClick={() => {
              const loaded = manualDataSlotIndices;
              const sel = document.getElementById('manual-action-type');
              const action = (sel && sel.value) ? sel.value : 'compare_eq';
              setManualRules((prev) => [...prev, { fileA: loaded[0] ?? 0, colA: 0, colApart: undefined, action, fileB: loaded[1] ?? 1, colB: 0, colBpart: undefined }]);
            }} style={{ padding: '8px 14px', borderRadius: '8px', border: '2px solid var(--modal-vpr-button-border-color, rgba(33, 147, 176, 0.5))', background: 'var(--modal-vpr-button-background, rgba(255,255,255,0.8))', color: 'var(--modal-vpr-button-text-color, #2193b0)', fontWeight: 600, cursor: 'pointer', fontSize: '15px' }}>
              Добавить правило
            </button>
            {manualRules.length > 0 && (
              <ul style={{ margin: '12px 0 0', paddingLeft: 0, listStyle: 'none', fontSize: '15px' }}>
                {manualRules.map((r, ri) => (
                  <li key={ri} style={{ marginBottom: '10px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
                    <select value={r.fileA} onChange={(e) => setManualRules((prev) => { const n = prev.slice(); n[ri] = { ...n[ri], fileA: parseInt(e.target.value, 10), colA: 0, colApart: undefined }; return n; })} style={{ padding: '8px 10px', borderRadius: '6px', border: '2px solid rgba(33, 147, 176, 0.5)', background: 'rgba(255,255,255,0.9)', fontSize: '15px' }}>
                      {manualDataSlotIndices.map((i) => (
                        <option key={i} value={i}>Файл {i + 1}</option>
                      ))}
                    </select>
                    <select value={manualRuleColSelectValue(r.colA, r.colApart)} onChange={(e) => setManualRules((prev) => { const n = prev.slice(); const { col, part } = parseManualRuleColSelectValue(e.target.value); const next = { ...n[ri], colA: col }; if (part) next.colApart = part; else delete next.colApart; n[ri] = next; return n; })} style={{ padding: '8px 10px', borderRadius: '6px', border: '2px solid rgba(33, 147, 176, 0.5)', background: 'rgba(255,255,255,0.9)', fontSize: '15px', minWidth: 120 }}>
                      {(manualFiles[r.fileA]?.headers || []).map((h, ci) => ({ h, ci })).filter(({ ci }) => (manualDeletedCols[r.fileA] || []).indexOf(ci) < 0).flatMap(({ h, ci }) => {
                        const base = `${colIndexToLetter(ci)} — ${(manualRenames[r.fileA] || {})[ci] ?? h ?? `Кол.${ci + 1}`}`;
                        const choices = manualColPartChoices(ci, manualFileConfigs[r.fileA], manualAddedColumns[r.fileA]);
                        if (choices) {
                          return choices.map((c) => {
                            const lab = c.ruleOptionLabel != null && c.ruleOptionLabel !== ''
                              ? c.ruleOptionLabel
                              : (c.part === 'fio' ? `${base} (ФИО)` : c.part === 'phone' ? `${base} (телефон)` : c.part === 'split0' ? `${base} (часть 1)` : `${base} (часть 2)`);
                            return <option key={`${ci}-${c.part}`} value={manualRuleColSelectValue(ci, c.part)}>{lab}</option>;
                          });
                        }
                        return [<option key={ci} value={String(ci)}>{base}</option>];
                      })}
                    </select>
                    <span style={{ fontWeight: 600, color: 'var(--modal-vpr-header-text-color, #2193b0)' }}>→</span>
                    <select value={r.action} onChange={(e) => setManualRules((prev) => { const n = prev.slice(); n[ri] = { ...n[ri], action: e.target.value }; return n; })} style={{ padding: '8px 10px', borderRadius: '6px', border: '2px solid rgba(33, 147, 176, 0.5)', background: 'rgba(255,255,255,0.9)', fontSize: '15px' }}>
                      <option value="compare_eq">Сравнить (равно)</option>
                      <option value="compare_ne">Сравнить (не равно)</option>
                      <option value="sum">Сумма</option>
                      <option value="subtract">Разность</option>
                      <option value="multiply">Умножение</option>
                      <option value="divide">Деление</option>
                      <option value="concat">Объединить текст</option>
                    </select>
                    <span style={{ fontWeight: 600, color: 'var(--modal-vpr-header-text-color, #2193b0)' }}>→</span>
                    <select value={r.fileB} onChange={(e) => setManualRules((prev) => { const n = prev.slice(); n[ri] = { ...n[ri], fileB: parseInt(e.target.value, 10), colB: 0, colBpart: undefined }; return n; })} style={{ padding: '8px 10px', borderRadius: '6px', border: '2px solid rgba(33, 147, 176, 0.5)', background: 'rgba(255,255,255,0.9)', fontSize: '15px' }}>
                      {manualDataSlotIndices.map((i) => (
                        <option key={i} value={i}>Файл {i + 1}</option>
                      ))}
                    </select>
                    <select value={manualRuleColSelectValue(r.colB, r.colBpart)} onChange={(e) => setManualRules((prev) => { const n = prev.slice(); const { col, part } = parseManualRuleColSelectValue(e.target.value); const next = { ...n[ri], colB: col }; if (part) next.colBpart = part; else delete next.colBpart; n[ri] = next; return n; })} style={{ padding: '8px 10px', borderRadius: '6px', border: '2px solid rgba(33, 147, 176, 0.5)', background: 'rgba(255,255,255,0.9)', fontSize: '15px', minWidth: 120 }}>
                      {(manualFiles[r.fileB]?.headers || []).map((h, ci) => ({ h, ci })).filter(({ ci }) => (manualDeletedCols[r.fileB] || []).indexOf(ci) < 0).flatMap(({ h, ci }) => {
                        const base = `${colIndexToLetter(ci)} — ${(manualRenames[r.fileB] || {})[ci] ?? h ?? `Кол.${ci + 1}`}`;
                        const choices = manualColPartChoices(ci, manualFileConfigs[r.fileB], manualAddedColumns[r.fileB]);
                        if (choices) {
                          return choices.map((c) => {
                            const lab = c.ruleOptionLabel != null && c.ruleOptionLabel !== ''
                              ? c.ruleOptionLabel
                              : (c.part === 'fio' ? `${base} (ФИО)` : c.part === 'phone' ? `${base} (телефон)` : c.part === 'split0' ? `${base} (часть 1)` : `${base} (часть 2)`);
                            return <option key={`${ci}-${c.part}`} value={manualRuleColSelectValue(ci, c.part)}>{lab}</option>;
                          });
                        }
                        return [<option key={ci} value={String(ci)}>{base}</option>];
                      })}
                    </select>
                    <button type="button" onClick={() => setManualRules((prev) => prev.filter((_, i) => i !== ri))} style={{ padding: '4px 10px', fontSize: '14px', border: '1px solid #c53030', background: 'transparent', color: '#c53030', cursor: 'pointer', borderRadius: '4px' }}>Удалить</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: 600, color: vprDateLabelColor }}>
            <span>Дата:</span>
            <input type="date" value={manualFilterDate} onChange={(e) => setManualFilterDate(e.target.value || '')} style={{ padding: '8px 12px', borderRadius: '8px', border: '2px solid rgba(33, 147, 176, 0.5)', background: 'rgba(255,255,255,0.9)', fontSize: '14px' }} />
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <button type="button" disabled={manualLoading || manualDataSlotIndices.length < 1} onClick={async () => {
              setManualError('');
              const crossMode = normalizeVprCrossMatchMode(manualCrossFileMatch.matchMode);
              if (manualCrossFileMatch.enabled && (crossMode === 'phone_nd' || crossMode === 'leading_phone_fio')) {
                if (manualDataSlotIndices.length < 2) {
                  setManualError(crossMode === 'leading_phone_fio'
                    ? 'Режим «Ведущий файл»: загрузите минимум 2 выгрузки и укажите главный файл выше.'
                    : 'Режим «Телефон + ФИО + НД»: загрузите и разнесите минимум 2 выгрузки (утро / обед).');
                  return;
                }
                const fioFill = fillManualCrossFioFromDetect(
                  manualCrossFileMatch.fioBySlot,
                  manualDataSlotIndices,
                  manualFiles,
                  manualRenames,
                );
                if (fioFill.changed) {
                  setManualCrossFileMatch((prev) => ({ ...prev, fioBySlot: fioFill.fioBySlot }));
                }
                const fioSlotsForCheck = fioFill.fioBySlot;
                const missingFioLabels = listManualCrossFioMissingSlotLabels(
                  manualDataSlotIndices,
                  fioSlotsForCheck,
                  manualFiles,
                  manualRenames,
                );
                if (missingFioLabels.length) {
                  setManualError(
                    `Укажите столбец ФИО / контакта в зелёном блоке для: ${missingFioLabels.join(', ')}. `
                    + 'Подойдёт «Контакт отримувача» — не обязательно слово «ФИО» в заголовке. '
                    + 'Если столбец разбит на телефон и ФИО, выберите вариант с пометкой «(ФИО)».',
                  );
                  return;
                }
                if (crossMode === 'phone_nd') {
                  const missingNd = manualDataSlotIndices.some((i) => {
                    const nd = manualCrossFileMatch.ndBySlot?.[i] || {};
                    return nd.col == null || nd.col === '';
                  });
                  if (missingNd) {
                    setManualError('Укажите столбец «НД / статус» для каждого слота в зелёном блоке.');
                    return;
                  }
                }
                if (crossMode === 'leading_phone_fio' && manualCrossFileMatch.leadingOnlyNd) {
                  const leadIdx = manualMainFileIndex;
                  const nd = manualCrossFileMatch.ndBySlot?.[leadIdx] || {};
                  if (nd.col == null || nd.col === '') {
                    setManualError('Для «только НД в ведущем» укажите столбец НД у главного (ведущего) файла.');
                    return;
                  }
                }
              }
              setManualLoading(true);
              try {
                const form = new FormData();
                const loadedIndices = manualDataSlotIndices;
                loadedIndices.forEach((slotIdx, uploadIdx) => {
                  const blob = getManualFileBlobForSlot(slotIdx, manualFiles);
                  if (blob) form.append(`file${uploadIdx}`, blob);
                });
                const mapStepForApi = (step) => {
                  const chainRulesSlot = expandManualColumnChainsToRules(step.columnChains || []);
                  const mappedFromChains = chainRulesSlot.map((r) => mapVprManualRuleToApiPayload(r, loadedIndices)).filter(Boolean);
                  const mappedRulesManual = (step.rules || []).map((r) => mapVprManualRuleToApiPayload(r, loadedIndices)).filter(Boolean);
                  const mappedRules = [...mappedFromChains, ...mappedRulesManual];
                  const fileConfigs = loadedIndices.map((slotIdx) => {
                    const raw = (step.fileConfigs && step.fileConfigs[slotIdx]) || {};
                    const c = syncManualActionPipelineForRun(raw, (step.selectedCols && step.selectedCols[slotIdx]) || manualSelectedCols[slotIdx] || []);
                    const liveSheet = manualFiles[slotIdx]?.excelSheetName;
                    const excelSheetName = (c.excelSheetName != null && String(c.excelSheetName).trim() !== '')
                      ? String(c.excelSheetName).trim()
                      : (liveSheet != null && String(liveSheet).trim() !== '' ? String(liveSheet).trim() : null);
                    const pipeline = Array.isArray(c.actionPipeline) ? c.actionPipeline : [];
                    return {
                      action: c.action || 'discrepancies',
                      partnerFiles: (c.partnerFiles || []).map((p) => loadedIndices.indexOf(p)).filter((i) => i >= 0),
                      method: c.method || 'compare_eq',
                      params: c.params || {},
                      actionPipeline: pipeline.map((step) => ({
                        action: step.action,
                        method: step.method,
                        params: step.params || {},
                        selectedCols: Array.isArray(step.selectedCols) ? step.selectedCols.slice() : [],
                      })),
                      excelSheetName,
                    };
                  });
                  let mainMapped = null;
                  if (step.mainFileIndex != null && step.mainFileIndex !== '') {
                    const mi = loadedIndices.indexOf(Number(step.mainFileIndex));
                    if (mi >= 0) mainMapped = mi;
                  }
                  return {
                    title: step.title || 'Задача',
                    selectedCols: loadedIndices.map((i) => (step.selectedCols && step.selectedCols[i]) || []),
                    rules: mappedRules,
                    fileConfigs,
                    deletedCols: loadedIndices.map((i) => (step.deletedCols && step.deletedCols[i]) || []),
                    renames: loadedIndices.map((i) => (step.renames && step.renames[i]) || {}),
                    addedColumns: loadedIndices.map((i) => (step.addedColumns && step.addedColumns[i]) || []),
                    mainFileIndex: mainMapped,
                  };
                };
                let configPayload;
                const crossFilePayload = buildManualCrossFileMatchPayload(
                  loadedIndices,
                  manualCrossFileMatch,
                  manualFileConfigs,
                  manualFiles,
                  manualMainFileIndex,
                  manualRenames,
                );
                if (manualTaskSteps.length > 0) {
                  configPayload = {
                    taskSteps: manualTaskSteps.map(mapStepForApi),
                    filterDate: manualFilterDate || null,
                    slotCount: manualSlotCount,
                    crossFilePhoneMatch: crossFilePayload,
                  };
                } else {
                  const chainRulesSlotSingle = expandManualColumnChainsToRules(manualColumnChains);
                  const mappedFromChainsSingle = chainRulesSlotSingle.map((r) => mapVprManualRuleToApiPayload(r, loadedIndices)).filter(Boolean);
                  const mappedRulesManualSingle = manualRules.map((r) => mapVprManualRuleToApiPayload(r, loadedIndices)).filter(Boolean);
                  const mappedRules = [...mappedFromChainsSingle, ...mappedRulesManualSingle];
                  const mainMappedSingle = loadedIndices.indexOf(manualMainFileIndex);
                  configPayload = {
                    selectedCols: loadedIndices.map((i) => manualSelectedCols[i] || []),
                    rules: mappedRules,
                    fileConfigs: loadedIndices.map((slotIdx) => {
                      const base = manualFileConfigForApi(slotIdx, manualFileConfigs, manualFiles, manualSelectedCols);
                      return {
                        ...base,
                        partnerFiles: (manualFileConfigs[slotIdx]?.partnerFiles || []).map((p) => loadedIndices.indexOf(p)).filter((i) => i >= 0),
                      };
                    }),
                    deletedCols: loadedIndices.map((i) => manualDeletedCols[i] || []),
                    renames: loadedIndices.map((i) => manualRenames[i] || {}),
                    addedColumns: loadedIndices.map((i) => manualAddedColumns[i] || []),
                    filterDate: manualFilterDate || null,
                    slotCount: manualSlotCount,
                    mainFileIndex: mainMappedSingle >= 0 ? mainMappedSingle : null,
                    crossFilePhoneMatch: crossFilePayload,
                  };
                }
                form.append('config', JSON.stringify(configPayload));
                const { data, status, headers } = await api.post('/api/vpr-manual', form, { responseType: 'blob', headers: { 'Content-Type': undefined } });
                const contentType = headers?.['content-type'] || '';
                if (status !== 200 || contentType.includes('application/json')) {
                  const text = typeof data === 'string' ? data : await (data instanceof Blob ? new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsText(data); }) : '');
                  let msg = 'Ошибка выполнения.';
                  try { const j = JSON.parse(text); if (j?.error) msg = j.error; } catch (_) {}
                  setManualError(msg);
                  return;
                }
                const blob = data instanceof Blob ? data : new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                const fileName = `vpr_manual_${Date.now()}.xlsx`;
                let sheets = [];
                try {
                  const ab = await blob.arrayBuffer();
                  const wb = XLSX.read(ab, { type: 'arraybuffer', cellDates: true });
                  sheets = (wb.SheetNames || []).map((name) => {
                    const parsed = parseManualWorksheetToSheetData(wb.Sheets[name]);
                    return { name, headers: parsed.headers, rows: parsed.rows };
                  });
                } catch (_) {}
                setManualPreviewDisplayRules([]);
                setManualPreviewRulesOpen(false);
                setManualResultTextFilters({ name: '', phone: '', waybill: '' });
                setManualResult({ blob, fileName, sheets });
                setManualResultTab(0);
              } catch (err) {
                setManualError(err?.response?.data?.error || err?.message || 'Ошибка запроса.');
              } finally {
                setManualLoading(false);
              }
            }} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px', borderRadius: '10px', border: '2px solid var(--modal-vpr-find-discrepancies-button-border-color, #2193b0)', background: (manualLoading || manualDataSlotIndices.length < 1) ? 'rgba(33, 147, 176, 0.2)' : 'rgba(33, 147, 176, 0.25)', color: 'var(--modal-vpr-find-discrepancies-button-text-color, #2193b0)', fontWeight: 600, cursor: manualLoading ? 'wait' : 'pointer', fontSize: '16px' }}>
              {manualLoading ? 'Выполняется…' : 'Запустить'}
            </button>
            {manualError && (
              <span style={{ color: String(manualError).startsWith('Объединено') ? '#166534' : '#c53030', fontSize: '14px', lineHeight: 1.45 }}>
                {manualError}
              </span>
            )}
          </div>
        </>
          )}
        </div>
        )}
        </div>
        {isAdmin && discTelegramSettingsOpen && (
          <Modal
            isOpen={discTelegramSettingsOpen}
            onRequestClose={() => { setDiscTelegramSettingsOpen(false); setDiscTelegramSettingsError(''); }}
            ariaHideApp={false}
            style={{
              overlay: { backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 100002, display: 'flex', alignItems: 'center', justifyContent: 'center' },
              content: { position: 'relative', inset: 'auto', width: mobile ? '95%' : '90%', maxWidth: 440, borderRadius: 16, padding: mobile ? 20 : 24, background: 'var(--modal-calendar-background, linear-gradient(135deg, #e8eef2 0%, #b0d4dc 100%))', border: '2px solid rgba(33, 147, 176, 0.4)', color: 'var(--modal-calendar-text-color, #1a5f6e)', boxShadow: '0 12px 40px rgba(0,0,0,0.2)' },
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: 'var(--modal-calendar-text-color, #2193b0)' }}>
                <FiSettings style={{ verticalAlign: 'middle', marginRight: 8 }} /> Настройка Telegram
              </h3>
              <button type="button" onClick={() => { setDiscTelegramSettingsOpen(false); setDiscTelegramSettingsError(''); }} aria-label="Закрыть" style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', opacity: 0.8, color: 'inherit', lineHeight: 1 }}>&times;</button>
            </div>
            <p style={{ margin: '0 0 16px', fontSize: 14, opacity: 0.9 }}>
              Токен бота выдаёт @BotFather в Telegram. ID группы — числовой идентификатор чата (например <code style={{ background: 'rgba(0,0,0,0.08)', padding: '2px 6px', borderRadius: 4 }}>-1001234567890</code>). Бот должен быть добавлен в группу.
            </p>
            {discTelegramSettingsLoading && <p style={{ margin: '0 0 12px', fontSize: 14 }}>Загрузка…</p>}
            {discTelegramSettingsError && <p style={{ margin: '0 0 12px', color: '#c53030', fontSize: 14 }}>{discTelegramSettingsError}</p>}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Токен бота</label>
              <input
                type="password"
                value={discTelegramBotToken}
                onChange={(e) => setDiscTelegramBotToken(e.target.value)}
                placeholder="123456789:AAH..."
                style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 10, border: '2px solid rgba(33, 147, 176, 0.5)', background: 'rgba(255,255,255,0.9)', fontSize: 14 }}
              />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 6 }}>ID группы (chat_id)</label>
              <input
                type="text"
                value={discTelegramChatId}
                onChange={(e) => setDiscTelegramChatId(e.target.value)}
                placeholder="-1001234567890"
                style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 10, border: '2px solid rgba(33, 147, 176, 0.5)', background: 'rgba(255,255,255,0.9)', fontSize: 14 }}
              />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => { setDiscTelegramSettingsOpen(false); setDiscTelegramSettingsError(''); }} style={{ padding: '10px 18px', borderRadius: 10, border: '2px solid rgba(33, 147, 176, 0.5)', background: 'rgba(255,255,255,0.9)', color: 'var(--modal-calendar-text-color, #2193b0)', fontWeight: 600, cursor: 'pointer' }}>
                Отмена
              </button>
              <button type="button" onClick={saveDiscTelegramSettings} disabled={discTelegramSettingsSaving} style={{ padding: '10px 18px', borderRadius: 10, border: '2px solid #2193b0', background: 'rgba(33, 147, 176, 0.3)', color: 'var(--modal-calendar-text-color, #2193b0)', fontWeight: 600, cursor: discTelegramSettingsSaving ? 'wait' : 'pointer' }}>
                {discTelegramSettingsSaving ? 'Сохранение…' : 'Сохранить'}
              </button>
            </div>
          </Modal>
        )}

        </div>
      </div>
    </Modal>
  );
}
